// DispatchDeck - Projects Module
// Unified project management — groups jobs, tracks lifecycle, optional customer portal

import {
  db,
  auth, // TASK-1333 — job creates must stamp the caller's uid (rules-enforced)
  collection,
  doc,
  getDocs,
  onSnapshot,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  arrayUnion,
  arrayRemove,
  functions, // TASK-4842 — geocode a project's address via the existing geocodeAddress callable
  httpsCallable,
  storage, ref, getDownloadURL, uploadBytes, deleteObject, // TASK-9650 library files; TASK-9647 — Docs tab opens files whose stored link is missing
} from '../firebase-config.js?v=1868';
// TASK-4828 — a project's standing PO propagates to every job on the corridor
// and from there onto the invoice, so it is a money field and takes the same
// single normalizer the job form and the ticket review modal use. Found by COD
// Blast-Radius on TASK-4782 and left open: every writer below used a bare
// `.trim()`, which does NOT strip NBSP or the zero-width characters — so an
// invisible character on a standing PO silently forks it into a second value
// and stamps that fork onto every job linked afterwards.
// Keep this `?v=` in sync with billing.js, which imports the same module.
import { normalizePoForSave as poNormalizeForSave } from '../utils/poField.js?v=1';
// TASK-9289 COD r1 — runTransaction is not re-exported from firebase-config.js;
// same gstatic URL (and version) as firebase-config, so it is the same SDK
// instance (dispatch.js imports it the same way).
import { runTransaction, serverTimestamp } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
// TASK-4705 — the ONE people model (`window.ProjectPeople`), the same renderer
// the CRM opportunity form uses. Side-effect module, read through window only
// (Pitfall #1); crm.js imports the same ?v=.
import '../projectPeople.js?v=4705001';
// TASK-9694 — a driver's job-screen Doc rides in job.permits[] but is not a load permit.
import { isJobDocEntry } from '../shared/job-permit-entry.js?v=10282127';

// ============================================
// PROJECT STATE
// ============================================
const ProjectState = {
  tenantId: null,
  projects: [],
  templates: [],
  customers: [],
  currentProject: null, // detail view
  searchQuery: '',
  statusFilter: 'active', // active, completed, archived, all
  customerFilter: '',
  tagFilter: '',
  savedFilters: [],
  viewMode: 'list', // list, detail
  sortBy: 'name', // name, startDate, endDate, status, customer
  sortDir: 'asc',
  listMode: 'cards', // cards, calendar
  // Cards mode orders by customer by default (Lacey @ TITS via Chris
  // 2026-09-29; one grid, no collapsible headers since TASK-9552). Per-browser
  // pref (localStorage), loaded in initProjects via loadGroupPrefs() — never
  // written to Firestore.
  groupByCustomer: true,
  calendarStart: null, // Monday of current week
  calendarMode: 'week', // week, month
  loading: false,
  // For project detail sub-tabs
  detailTab: 'overview', // overview, jobs, inventory, timeline, documents, notes
  // TASK-9649 — Docs tab filter, per project: { type, jobId, q }
  docsFilter: null,
  docsFilterFor: null,
  // Notes
  pendingNote: '',
  // Change orders
  changeOrders: [],
};

// ============================================
// HELPERS
// ============================================
function isDemoMode() {
  return window.AppState?.isDemo === true;
}

function escHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function escAttr(str) {
  return (str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateShort(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// >>> TASK-9506 projectDayStatus — BYTE-IDENTICAL copy in public/project-portal.html (standalone page); tests/task9506-project-overdue-day.unit.cjs enforces it >>>
// "Overdue" = after the END of the local calendar day of endDate, in the
// tenant's time zone. End dates are noon-anchored, so a raw `endDate < now`
// turned a project red at noon on its last day. Calendar-day serials from
// Intl (not getDate(), which is the viewer's zone, and not ms/86400000, which
// is off by an hour across DST). daysLeft: 0 = last day, <0 = overdue.
function projectDayStatus(endDate, now, timeZone) {
  const asDate = (v) => {
    if (v === null || v === undefined || v === '') return null;
    let d = null;
    if (typeof v.toDate === 'function') d = v.toDate();
    else if (v instanceof Date) d = v;
    else if (typeof v === 'object' && typeof v.seconds === 'number') d = new Date(v.seconds * 1000);
    else d = new Date(v);
    return d && !isNaN(d.getTime()) ? d : null;
  };
  const end = asDate(endDate);
  if (!end) return null;
  const at = asDate(now) || new Date();
  let fmt;
  try {
    fmt = new Intl.DateTimeFormat('en-US', { timeZone: String(timeZone || '').trim() || 'America/Chicago', year: 'numeric', month: 'numeric', day: 'numeric' });
  } catch (e) {
    fmt = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: 'numeric', day: 'numeric' });
  }
  const serial = (d) => {
    const p = {};
    fmt.formatToParts(d).forEach(x => { p[x.type] = Number(x.value); });
    return Math.round(Date.UTC(p.year, p.month - 1, p.day) / 86400000);
  };
  const daysLeft = serial(end) - serial(at);
  return { daysLeft, overdue: daysLeft < 0 };
}
// <<< TASK-9506 projectDayStatus <<<

// The tenant's wall clock for project day math (settings.timezone; the app-wide
// default is America/Chicago, same as reminders + the clock day rule).
function tenantTimeZone() {
  const s = window.AppState?.settings || window.AppState?.tenantData?.settings || {};
  return s.timezone || 'America/Chicago';
}

// Calendar days until ts in the tenant's zone (0 = today, <0 = past). Was
// Math.ceil(ms / 86400000), which flipped to 0 at noon on a noon-anchored date.
function daysFromNow(ts) {
  const st = projectDayStatus(ts, new Date(), tenantTimeZone());
  return st ? st.daysLeft : null;
}

function getCustomerName(customerId) {
  if (!customerId) return 'No customer';
  const customers = window.AppState?.customers || [];
  const c = customers.find(c => c.id === customerId);
  return c?.name || c?.company || customerId;
}

function getPeople() {
  return window.AppState?.people || [];
}

// ============================================
// TASK-4705 — PEOPLE ON THIS JOB (one model, shared with the CRM opportunity)
// ============================================
// Same shape as an opportunity: people: [{personId, personName, role,
// canCompleteSteps}], rendered by window.ProjectPeople. The RENDERER renders;
// this HOST authorizes. Editing = the tenant-wide project manager (owner, or
// dispatch + projects, or demo) — the arm whose rules take any key. A
// project-scoped manager's allow-list does not include `people`, so the card is
// read-only for them rather than offering a save the rules refuse.
// TASK-4706 — the capability is LIVE here now: a ticked row is the grant the
// crew loop reads (functions/src/services/crewPortalProjects.js
// crewProjectGrantIds → getCrewPortalProjects / portalCompleteStep /
// portalSubmitNote; the onCrewProjectPeopleWrite trigger indexes it). The same
// edit gate decides who may tick it — nobody below the tenant-wide project
// manager can grant a crew member step completion.
function projectPeopleCanEdit() {
  const S = window.AppState || {};
  if (S.isDemo || S.tenant === 'demo') return true;
  return fieldTotalsCanEdit();
}

// Mirrors crewProjectGrantIds' closed-status rule so the card says plainly
// when a tick grants nothing right now (the CRM card does the same for a
// portal that is off).
const PROJECT_CREW_CLOSED_STATUSES = ['completed', 'archived'];
function projectCrewCapabilityHint(project) {
  const p = project || {};
  if (PROJECT_CREW_CLOSED_STATUSES.includes(p.status)) {
    return 'This project is closed, so nobody can tick steps right now. Ticks are kept and work again if it is reopened.';
  }
  const steps = Array.isArray(p.portalSettings?.trackerSteps) ? p.portalSettings.trackerSteps.filter(s => s && s.name) : [];
  if (!steps.length) {
    return 'Ticked people see this project on their Driver PIN. Add progress steps in Edit (Customer portal) for them to check off.';
  }
  return 'Ticked people see this project on their Driver PIN and can check off its progress steps, in order.';
}

function renderProjectPeopleCardBody(project) {
  const PP = window.ProjectPeople;
  const people = PP ? PP.normalizeProjectPeople(project) : [];
  return `
    <h4><span class="material-icons" style="font-size:18px;vertical-align:middle;">group</span> People on this job</h4>
    ${PP ? PP.renderHTML({
      people,
      roster: getPeople().filter(p => p && p.id && p.active !== false), // same filter as the CRM picker
      idPrefix: 'project-people',
      canEdit: projectPeopleCanEdit(),
      showCapability: true,
      capabilityLabel: 'Can tick tracker steps',
      capabilityHint: projectCrewCapabilityHint(project),
      onAdd: 'window.ProjectsModule.addProjectPerson',
      onRemove: 'window.ProjectsModule.removeProjectPerson',
      onToggle: 'window.ProjectsModule.toggleProjectPersonSteps',
      emptyText: 'Nobody on this job yet.',
    }) : ''}`;
}

function _repaintProjectPeopleCard(projectId) {
  const card = document.getElementById('project-people-card');
  const project = ProjectState.currentProject;
  if (card && project && project.id === projectId) card.innerHTML = renderProjectPeopleCardBody(project);
}

// Read-modify-write in ONE transaction so two people editing the list at once
// never drop each other's change. Only `people` + updatedAt move.
async function _mutateProjectPeople(projectId, mutate) {
  if (!ProjectState.tenantId || !projectId || !window.ProjectPeople) return false;
  if (!projectPeopleCanEdit()) { window.showToast?.('Changing who is on a project needs Dispatch and Projects access.', 'error'); return false; }
  const PP = window.ProjectPeople;
  const ref = doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId);
  try {
    const result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return { missing: true };
      const people = PP.normalizeProjectPeople(snap.data());
      const verdict = mutate(people);
      if (verdict !== true) return { verdict };
      const next = PP.peopleForSave(people);
      tx.update(ref, { people: next, updatedAt: Timestamp.now() });
      return { people: next };
    });
    if (result.missing) { window.showToast?.('This project no longer exists.', 'error'); return false; }
    if (!result.people) return result.verdict || false;
    const idx = ProjectState.projects.findIndex(p => p.id === projectId);
    if (idx >= 0) ProjectState.projects[idx].people = result.people;
    if (ProjectState.currentProject?.id === projectId) ProjectState.currentProject = { ...ProjectState.currentProject, people: result.people };
    window.logAudit?.('projects', 'PROJECT_UPDATE', { projectId, fields: ['people'], count: result.people.length });
    _repaintProjectPeopleCard(projectId);
    return true;
  } catch (err) {
    console.error('[Projects] people update failed:', err);
    window.logError?.(err, { module: 'projects', operation: 'UPDATE_PROJECT_PEOPLE', projectId });
    window.showToast?.(err?.code === 'permission-denied'
      ? 'Changing who is on a project needs Dispatch and Projects access.'
      : 'Could not update the people on this project.', 'error');
    return false;
  }
}

async function addProjectPerson() {
  const projectId = ProjectState.currentProject?.id;
  const row = window.ProjectPeople?.readPicker('project-people');
  if (!projectId || !row) return;
  const r = await _mutateProjectPeople(projectId, (people) => (window.ProjectPeople.addPerson(people, row) ? true : 'dup'));
  if (r === 'dup') window.showToast?.('Already on this job', 'error');
}

async function removeProjectPerson(index) {
  const project = ProjectState.currentProject;
  if (!project || !window.ProjectPeople) return;
  // Resolve the row the user clicked to a stable key BEFORE the transaction:
  // the list may have changed under the index since it was drawn.
  const shown = window.ProjectPeople.normalizeProjectPeople(project)[index];
  if (!shown) return;
  const same = (r) => (shown.personId ? r.personId === shown.personId : (!r.personId && r.personName === shown.personName));
  await _mutateProjectPeople(project.id, (people) => {
    const i = people.findIndex(same);
    if (i < 0) return false;
    people.splice(i, 1);
    return true;
  });
}

// TASK-4706 — tick / untick "Can tick tracker steps". Same transaction + gate
// as add/remove; resolved by personId (only rows with a personId render the
// checkbox), so a list that changed under the click never flips someone else.
async function toggleProjectPersonSteps(index, checked) {
  const project = ProjectState.currentProject;
  if (!project || !window.ProjectPeople) return;
  const shown = window.ProjectPeople.normalizeProjectPeople(project)[index];
  if (!shown || !shown.personId) return;
  const want = checked === true;
  const ok = await _mutateProjectPeople(project.id, (people) => {
    const row = people.find(r => r.personId === shown.personId);
    if (!row) return false;
    if (row.canCompleteSteps === want) return 'same';
    row.canCompleteSteps = want;
    return true;
  });
  // A refused / failed save must not leave the checkbox showing a grant that
  // was not stored: repaint from the stored list.
  if (ok !== true) _repaintProjectPeopleCard(project.id);
}

// ============================================
// HEALTH STATUS CALCULATION
// ============================================
// TASK-9605 — how an ACTIVE project sits against its end date, using the last
// scheduled jobDay (project._lastDayMs, filled in by loadProjectJobCounts).
//   none     not active / no end date / end date not yet over
//   unknown  end date over but no schedule data (not loaded yet, load failed, or
//            the project's jobs have NO jobDays at all) -> render NEUTRAL, never
//            red, never offered "Mark complete"
//   ranlong  a jobDay falls AFTER the end date's local day -> the only red
//   wrapped  end date over and no jobDay after it -> grey, finished
// Day compare reuses projectDayStatus (TASK-9506 noon-anchor rule): asking for the
// END date's distance from the LAST day, daysLeft < 0 means the last day is later.
function projectRunState(project, now, timeZone) {
  if (!project || project.status !== 'active') return { state: 'none' };
  const endSt = projectDayStatus(project.endDate, now, timeZone);
  if (!endSt || !endSt.overdue) return { state: 'none' };
  const last = project._lastDayMs;
  if (typeof last !== 'number' || !isFinite(last)) return { state: 'unknown' };
  const lateSt = projectDayStatus(project.endDate, new Date(last), timeZone);
  if (!lateSt) return { state: 'unknown' };
  return { state: lateSt.daysLeft < 0 ? 'ranlong' : 'wrapped', lastDayMs: last };
}

// Within a customer group: running/upcoming -> ran long -> wrapped/unknown past
// end -> completed/archived. Pure; `now`/`tz` passed in.
function projectRunRank(project, now, timeZone) {
  if (project.status !== 'active') return 3;
  const st = projectRunState(project, now, timeZone).state;
  if (st === 'ranlong') return 1;
  if (st === 'wrapped' || st === 'unknown') return 2;
  return 0;
}

function calculateHealth(project, linkedJobs) {
  // Red: overdue jobs, past end date with incomplete work
  // Yellow: jobs ending soon, approaching end date
  // Green: everything on track
  // TASK-9506 — day math, not instant math: overdue only once the LAST local
  // day of the end date is over (tenant zone). See projectDayStatus.
  const now = new Date();
  const tz = tenantTimeZone();
  const endSt = projectDayStatus(project.endDate, now, tz);

  // TASK-9605 — red only when work actually ran past the end date. Past the end
  // date with nothing scheduled after it is finished (grey), and past it with no
  // schedule data is unknown (grey) — never red on the date alone.
  const run = projectRunState(project, now, tz);
  if (run.state === 'ranlong') return { status: 'red', reason: 'Ran past end date' };
  if (run.state === 'wrapped') return { status: 'grey', reason: 'Wrapped — no work scheduled after the end date' };
  if (run.state === 'unknown') return { status: 'grey', reason: 'Past end date — no schedule data' };

  // Check linked jobs for issues (same rule for a job's end date)
  const jobEndSt = (j) => (j.endDate ? projectDayStatus(j.endDate, now, tz) : null);
  const overdueJobs = linkedJobs.filter(j => {
    const st = jobEndSt(j);
    return st && st.overdue && j.status === 'active';
  });
  if (overdueJobs.length > 0) {
    return { status: 'red', reason: `${overdueJobs.length} overdue job(s)` };
  }

  // Yellow: end date within 7 days (today = the last day, still yellow)
  if (endSt) {
    const daysLeft = endSt.daysLeft;
    if (daysLeft === 0) {
      return { status: 'yellow', reason: 'Ends today' };
    }
    if (daysLeft <= 7 && daysLeft > 0) {
      return { status: 'yellow', reason: `${daysLeft} day(s) until end date` };
    }
  }

  // Yellow: jobs ending in the next 3 days (or today)
  const soonJobs = linkedJobs.filter(j => {
    const st = jobEndSt(j);
    if (!st || j.status !== 'active') return false;
    return st.daysLeft <= 3 && st.daysLeft >= 0;
  });
  if (soonJobs.length > 0) {
    return { status: 'yellow', reason: `${soonJobs.length} job(s) ending soon` };
  }

  return { status: 'green', reason: 'On track' };
}

function healthDot(health) {
  const colors = { green: '#10B981', yellow: '#F59E0B', red: '#EF4444', grey: '#9CA3AF' };
  return `<span title="${escAttr(health.reason)}" style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${colors[health.status] || colors.green};flex-shrink:0;"></span>`;
}

// ============================================
// DATA LOADING
// ============================================
// ============================================
// PROJECTS — LIVE, not a one-shot
// ============================================
// This was `await getDocs(...)` fired once per initProjects (i.e. once per
// Dispatch view entry) and never again. Everything downstream inherited that
// staleness: the Add Job / Edit Job project <select> (dispatch.js), the job
// board's project label, and BI. A project created anywhere else — the AR/AP
// "Group into project" callable, the Projects tab, another user on the tenant —
// stayed invisible until the dispatcher reloaded. Reported from the field
// 2026-09-01.
//
// onSnapshot instead. The collection is small (a tenant has projects, not
// tickets — TITS has one), it is already read on every Dispatch entry, and a
// listener costs one read per change rather than a full re-fetch per open.
//
// IDEMPOTENT: initProjects runs on EVERY Dispatch navigation, so re-entering
// must reuse the existing subscription rather than stacking a second one — the
// tenant guard below is what makes that safe, and a tenant SWITCH tears the old
// one down first.
//
// Resolves on the FIRST snapshot so `await loadProjects()` keeps meaning "the
// list is populated" for initProjects' callers. Later snapshots just update
// state and notify.
let _projectsUnsub = null;
let _projectsSubTenant = null;
// TASK-10003 — the live listener's first-snapshot promise (what an adopting mount
// awaits) and, while it is still pending, the function that settles it.
let _projectsFirstSnapshot = null;
let _projectsSettlePending = null;
// TASK-10003 — mount generation. initProjects runs on EVERY Dispatch mount (and
// on ensureProjectsModule), and its callers run the PREVIOUS mount's teardown
// LATE: app.js's dispatch case defers it behind `.then`, and it can only run
// once that mount's first snapshot landed. Re-mounting Dispatch while already on
// Dispatch therefore ran the old teardown AFTER the new mount had adopted the
// live listener — unsubscribing it and emptying ProjectState.projects ("0 of 0",
// an empty Add Job project picker, or "Loading projects..." forever). Only the
// NEWEST mount's teardown may touch the shared listener and state.
let _projectsMountGen = 0;

function _unsubscribeProjects() {
  if (_projectsUnsub) { try { _projectsUnsub(); } catch (_) { /* already gone */ } }
  _projectsUnsub = null;
  _projectsSubTenant = null;
  // TASK-10003 — a first snapshot that can no longer arrive: settle its waiter so
  // ProjectState.loading never stays true with no listener running, and no
  // `await loadProjects()` hangs forever.
  const settle = _projectsSettlePending;
  _projectsSettlePending = null;
  _projectsFirstSnapshot = null;
  if (settle) settle();
}

// TASK-10003 — the teardown initProjects hands back. A no-op (returns false) when
// a newer mount has started since: that mount owns the listener and the data now.
function _teardownProjectsMount(mountGen) {
  if (mountGen !== _projectsMountGen) return false;
  _unsubscribeProjects();
  _projectJobCounts = new Map(); // TASK-9558 — and void any in-flight count run
  _projectJobCountsGen++;
  ProjectState.projects = [];
  ProjectState.templates = [];
  ProjectState.currentProject = null;
  return true;
}

// TASK-9558 — is the Projects LIST the thing on screen, with nothing layered
// over it? The one check both re-render paths use. It reads the DOM, not
// `window.DispatchState`: dispatch.js keeps DispatchState module-private, and the
// only `window.DispatchState` in the app is app.js's clock-entries stub, which
// never has a viewMode — so the old count-arrival gate was always false and the
// cards sat on "0 jobs" whenever they rendered before the count query returned.
function _projectsListOnScreen() {
  return !!document.querySelector('.view-btn[data-view="projects"]')?.classList.contains('active')
    && ProjectState.viewMode === 'list'
    && !document.querySelector('.modal:not(.hidden)');
}

// TASK-9558 — last known per-project job counts, keyed by project id. A projects
// snapshot rebuilds every project object from Firestore (no `_jobCount`), so the
// counts are re-applied from here before the snapshot re-renders, instead of
// every card flashing — or sticking at — "0 jobs".
let _projectJobCounts = new Map();
let _projectJobCountsGen = 0;

function _applyProjectJobCounts(projects) {
  for (const p of projects) {
    const c = _projectJobCounts.get(p.id);
    p._jobCount = c ? c.total : 0;
    p._activeJobCount = c ? c.active : 0;
  }
}

// TASK-9605 — last scheduled jobDay per project (ms), keyed by project id.
// ABSENT = not loaded yet (renders neutral); null = loaded, project has no
// jobDays at all (also neutral). Per-job cache with a short TTL so a projects
// snapshot does not re-read every jobDay, and a day added in dispatch shows up.
let _projectLastDays = new Map();
let _projectLastDaysTenant = null; // the map above belongs to ONE tenant; never reuse it across a switch
let _projectLinkedJobs = new Map(); // projectId -> its jobs (id/status/projectId), for the re-check on Mark complete
const _jobLastDayCache = new Map(); // `${tenant}/${jobId}` -> { ms: number|null, at }
const JOB_LAST_DAY_TTL_MS = 5 * 60 * 1000;

function _applyProjectLastDays(projects) {
  const mine = _projectLastDaysTenant === ProjectState.tenantId;
  for (const p of projects) {
    if (mine && _projectLastDays.has(p.id)) p._lastDayMs = _projectLastDays.get(p.id);
    else delete p._lastDayMs;
  }
}

function _dayMs(v) {
  if (v === null || v === undefined || v === '') return null;
  let d = null;
  if (typeof v.toDate === 'function') d = v.toDate();
  else if (v instanceof Date) d = v;
  else if (typeof v === 'object' && typeof v.seconds === 'number') d = new Date(v.seconds * 1000);
  else d = new Date(v);
  const ms = d ? d.getTime() : NaN;
  return isFinite(ms) ? ms : null;
}

// Pure: per listed project, the latest jobDay across its non-cancelled jobs.
// null when no job of the project has any day (unknown, not "wrapped").
function tallyProjectLastDays(projectIds, jobs, lastDayByJob) {
  const out = new Map();
  for (const id of projectIds) out.set(id, null);
  for (const job of jobs) {
    if (!job || !job.id || !out.has(job.projectId) || job.status === 'cancelled') continue;
    const ms = lastDayByJob.get(job.id);
    if (typeof ms !== 'number') continue;
    const cur = out.get(job.projectId);
    if (cur === null || ms > cur) out.set(job.projectId, ms);
  }
  return out;
}

// jobDays `where jobId in [...]`, batches of 10. Returns Map jobId -> ms|null, or
// null when superseded (newer run / tenant switch). Throws on a read failure so
// the caller leaves every project neutral.
async function loadJobLastDays(tenantId, jobs, gen) {
  const ids = [...new Set(jobs.filter(j => j && j.id && j.status !== 'cancelled').map(j => j.id))];
  const now = Date.now();
  const stale = ids.filter(id => {
    const e = _jobLastDayCache.get(`${tenantId}/${id}`);
    return !e || now - e.at > JOB_LAST_DAY_TTL_MS;
  });
  for (let i = 0; i < stale.length; i += 10) {
    const batch = stale.slice(i, i + 10);
    const snap = await getDocs(
      query(collection(db, 'tenants', tenantId, 'jobDays'), where('jobId', 'in', batch))
    );
    if (gen !== _projectJobCountsGen || ProjectState.tenantId !== tenantId) return null;
    const maxByJob = new Map();
    for (const d of snap.docs) {
      const row = d.data();
      if (row && row.status === 'cancelled') continue; // a cancelled day is not scheduled work (dispatch skips them too)
      const ms = _dayMs(row && row.date);
      if (ms === null || !row.jobId) continue;
      if (!maxByJob.has(row.jobId) || ms > maxByJob.get(row.jobId)) maxByJob.set(row.jobId, ms);
    }
    for (const id of batch) {
      _jobLastDayCache.set(`${tenantId}/${id}`, { ms: maxByJob.has(id) ? maxByJob.get(id) : null, at: Date.now() });
    }
  }
  const byJob = new Map();
  for (const id of ids) {
    const e = _jobLastDayCache.get(`${tenantId}/${id}`);
    byJob.set(id, e ? e.ms : null);
  }
  return byJob;
}

// Pure: tally linked jobs per project. Only ids in `projectIds` are counted (a
// job pointing at a project outside the list is ignored), and every listed
// project gets an entry, so a project whose last job was unlinked reads 0.
function tallyProjectJobCounts(projectIds, jobs) {
  const counts = new Map();
  for (const id of projectIds) counts.set(id, { total: 0, active: 0 });
  for (const job of jobs) {
    const c = job && counts.get(job.projectId);
    if (!c) continue;
    c.total += 1;
    if (job.status === 'active') c.active += 1;
  }
  return counts;
}

// TASK-9605 — plain "N jobs". The old "(N active)" was noise: jobs here never
// leave 'active'. `active` is still tallied, just not shown.
function projectJobCountLabel(total) {
  if (!total) return 'No jobs yet';
  return `${total} job${total !== 1 ? 's' : ''}`;
}

// When the list can't be re-rendered (a modal is over it — the create modal's
// Cancel only HIDES it, so nothing re-renders on close), patch the count text of
// the cards already in the DOM instead. Matches on the attribute VALUE, never by
// building a selector from a tenant-typed id.
function _patchRenderedJobCounts() {
  for (const el of document.querySelectorAll('[data-project-jobcount]')) {
    const c = _projectJobCounts.get(el.getAttribute('data-project-jobcount'));
    if (c) el.textContent = projectJobCountLabel(c.total);
  }
}

function _onProjectsChanged() {
  // Background: job counts for the card stats.
  loadProjectJobCounts();

  // TASK-9652 S2 — the Projects nav badge adds pending customer uploads
  // (customerUploadsPending on each project doc). Reached through
  // window.DispatchModule — never a cross-import (Pitfall #1).
  try { window.DispatchModule?.repaintProjectsBadge?.(); } catch (err) {
    console.warn('[Projects] repaintProjectsBadge failed:', err?.message || err);
  }

  // The dropdown that actually went stale in the field. Reached through
  // window.DispatchModule — never a cross-import (Pitfall #1). It no-ops unless
  // the job modal is open, so this is free on every other snapshot.
  try { window.DispatchModule?.refreshProjectSelects?.(); } catch (err) {
    console.warn('[Projects] refreshProjectSelects failed:', err?.message || err);
  }

  // TASK-9767 — keep an open project's 811 list in step with the snapshot.
  try {
    const cur = ProjectState.currentProject;
    const live = cur ? (ProjectState.projects || []).find(p => p && p.id === cur.id) : null;
    if (live && live !== cur && JSON.stringify(projectPermitIdsOf(live)) !== JSON.stringify(projectPermitIdsOf(cur))) {
      cur.permitIds = projectPermitIdsOf(live);
      _rerenderProjectDocsIfOpen(cur.id);
    }
  } catch (err) {
    console.warn('[Projects] 811 sync failed:', err?.message || err);
  }

  // Re-render the Projects list ONLY when it is the thing on screen, in list
  // mode, with nothing layered over it. A snapshot must never blow away a detail
  // view someone is reading or a modal they are half-way through filling in.
  try {
    if (_projectsListOnScreen()) renderProjectsView();
  } catch (err) {
    console.warn('[Projects] live re-render failed:', err?.message || err);
  }
}

// createdAt desc (the order the old orderBy gave), missing createdAt last.
function sortProjectsNewestFirst(list) {
  const ms = v => (v && typeof v.toMillis === 'function') ? v.toMillis()
    : (v && typeof v.seconds === 'number') ? v.seconds * 1000 : 0;
  return list.sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
}

function loadProjects() {
  if (!ProjectState.tenantId) return Promise.resolve();

  // TASK-9651 — no 'projects' module = no listener (firestore.rules projRead
  // would refuse it). An empty list is the honest state: the job form, board
  // chips, map and 811 pickers all read "no projects" and hide their project
  // bits rather than surfacing a permission error. Checked BEFORE the reuse
  // below (COD): a listener left from before a revoke must not keep serving.
  if (window.canAccessProjects?.() === false) {
    _unsubscribeProjects();
    ProjectState.projects = [];
    ProjectState.loading = false;
    return Promise.resolve();
  }

  // Already live on this tenant — reuse it. TASK-10003: hand back ITS first-snapshot
  // promise, so an adopting mount (a re-mount, ensureProjectsModule) still means
  // "the list is populated" when it resolves, even if that snapshot hasn't landed.
  if (_projectsUnsub && _projectsSubTenant === ProjectState.tenantId) return _projectsFirstSnapshot || Promise.resolve();
  _unsubscribeProjects();

  const tenantId = ProjectState.tenantId;
  ProjectState.loading = true;

  return (_projectsFirstSnapshot = new Promise(resolve => {
    let settled = false;
    const done = () => {
      if (_projectsSettlePending === done) _projectsSettlePending = null;
      if (!settled) { settled = true; ProjectState.loading = false; resolve(); }
    };
    _projectsSettlePending = done;
    _projectsSubTenant = tenantId;
    _projectsUnsub = onSnapshot(
      // TASK-9700 — NO orderBy: Firestore drops every doc that lacks the
      // ordered field, so a project with no createdAt was invisible here but
      // listed in AR/AP (plain collection read). Sort client-side instead.
      collection(db, 'tenants', tenantId, 'projects'),
      snap => {
        // A snapshot that arrives after a tenant switch belongs to the old
        // tenant — drop it rather than cross-contaminating state.
        if (ProjectState.tenantId !== tenantId) return;
        ProjectState.projects = sortProjectsNewestFirst(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        _applyProjectJobCounts(ProjectState.projects); // TASK-9558 — keep last known counts
        _applyProjectLastDays(ProjectState.projects); // TASK-9605 — and last known last-day
        done();
        _onProjectsChanged();
      },
      err => {
        console.error('[Projects] projects listener failed:', err);
        // Fail to an EMPTY list exactly as the getDocs catch did — but only on
        // the first failure, so a transient mid-session drop doesn't wipe a
        // list that is already on screen.
        if (!settled) ProjectState.projects = [];
        done();
      }
    );
  }));
}

// TASK-9558 — counts are a direct `jobs where projectId in [...]` query, NOT the
// dispatch board's date-windowed job list, so a project's old/future jobs count.
// Tallied into a FRESH map (the old code did `_jobCount += 1` onto the live
// objects, so two overlapping runs double-counted) and dropped if a newer run or
// a tenant switch started meanwhile.
async function loadProjectJobCounts() {
  if (!ProjectState.tenantId || ProjectState.projects.length === 0) return;
  const gen = ++_projectJobCountsGen;
  const tenantId = ProjectState.tenantId;
  try {
    const projectIds = ProjectState.projects.map(p => p.id);
    const jobs = [];
    // batch by 10 for the Firestore 'in' limit
    for (let i = 0; i < projectIds.length; i += 10) {
      const batch = projectIds.slice(i, i + 10);
      const snap = await getDocs(
        query(
          collection(db, 'tenants', tenantId, 'jobs'),
          where('projectId', 'in', batch)
        )
      );
      for (const d of snap.docs) jobs.push({ id: d.id, ...d.data() }); // TASK-9605 — id needed for the jobDays lookup
    }
    if (gen !== _projectJobCountsGen || ProjectState.tenantId !== tenantId) return; // superseded
    _projectJobCounts = tallyProjectJobCounts(projectIds, jobs);
    _applyProjectJobCounts(ProjectState.projects);
    // The counts land AFTER the list has usually rendered — repaint it, or patch
    // the cards in place when a modal is over the list.
    if (_projectsListOnScreen()) renderProjectsView();
    else _patchRenderedJobCounts();
    // TASK-9605 — last scheduled day per project. Until this lands (or if it
    // fails) every past-end project renders neutral, then repaints.
    _projectLinkedJobs = new Map(projectIds.map(id => [id, []]));
    for (const j of jobs) { const l = _projectLinkedJobs.get(j.projectId); if (l) l.push({ id: j.id, status: j.status, projectId: j.projectId }); }
    try {
      const byJob = await loadJobLastDays(tenantId, jobs, gen);
      if (!byJob || gen !== _projectJobCountsGen || ProjectState.tenantId !== tenantId) return;
      _projectLastDays = tallyProjectLastDays(projectIds, jobs, byJob);
    } catch (err) {
      if (gen !== _projectJobCountsGen || ProjectState.tenantId !== tenantId) return;
      console.warn('[Projects] Last-day load failed — cards stay neutral:', err);
      _projectLastDays = new Map(); // a failed refresh must not leave a stale "Wrapped" + Mark complete
    }
    _projectLastDaysTenant = tenantId;
    _applyProjectLastDays(ProjectState.projects);
    if (_projectsListOnScreen()) renderProjectsView();
  } catch (err) {
    console.warn('[Projects] Job count load failed:', err);
  }
}

async function loadTemplates() {
  if (!ProjectState.tenantId) return;
  // Driver-PIN sessions can't read projectTemplates per Firestore rules — skip silently.
  if (window.AppState?.driverModePin) {
    ProjectState.templates = [];
    return;
  }
  try {
    const snap = await getDocs(
      collection(db, 'tenants', ProjectState.tenantId, 'projectTemplates')
    );
    ProjectState.templates = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // Permission denials are expected for non-admin contexts; downgrade to warn.
    if (err?.code === 'permission-denied') {
      ProjectState.templates = [];
    } else {
      console.error('[Projects] Error loading templates:', err);
      ProjectState.templates = [];
    }
  }
}

async function loadCustomers() {
  if (!ProjectState.tenantId) return;
  try {
    const snap = await getDocs(
      query(collection(db, 'tenants', ProjectState.tenantId, 'customers'), orderBy('name'))
    );
    ProjectState.customers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[Projects] Error loading customers:', err);
    ProjectState.customers = [];
  }
}

async function loadProjectJobs(projectId) {
  if (!ProjectState.tenantId || !projectId) return [];
  try {
    const snap = await getDocs(
      query(
        collection(db, 'tenants', ProjectState.tenantId, 'jobs'),
        where('projectId', '==', projectId)
      )
    );
    if (ProjectState.jobsLoadFailedFor === projectId) ProjectState.jobsLoadFailedFor = null;
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[Projects] Error loading project jobs:', err);
    // TASK-9647 — remembered so the Docs tab says "couldn't load" instead of
    // "no documents" (an empty list here is not proof there are none).
    ProjectState.jobsLoadFailedFor = projectId;
    return [];
  }
}

async function loadProjectNotes(projectId) {
  if (!ProjectState.tenantId || !projectId) return [];
  try {
    const snap = await getDocs(
      query(
        collection(db, 'tenants', ProjectState.tenantId, 'projects', projectId, 'notes'),
        orderBy('createdAt', 'desc')
      )
    );
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[Projects] Error loading notes:', err);
    return [];
  }
}

async function loadChangeOrders(projectId) {
  if (!ProjectState.tenantId || !projectId) return [];
  try {
    const snap = await getDocs(
      query(
        collection(db, 'tenants', ProjectState.tenantId, 'projects', projectId, 'changeOrders'),
        orderBy('createdAt', 'desc')
      )
    );
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[Projects] Error loading change orders:', err);
    return [];
  }
}

async function loadProjectClockHours(projectId) {
  // Get total hours from clock entries on linked jobs
  if (!ProjectState.tenantId || !projectId) return { totalHours: 0, entries: [] };
  try {
    const jobs = await loadProjectJobs(projectId);
    const jobIds = jobs.map(j => j.id);
    if (jobIds.length === 0) return { totalHours: 0, entries: [] };

    // Query clock entries for these jobs (batch by 10 for Firestore 'in' limit)
    let allEntries = [];
    for (let i = 0; i < jobIds.length; i += 10) {
      const batch = jobIds.slice(i, i + 10);
      const snap = await getDocs(
        query(
          collection(db, 'tenants', ProjectState.tenantId, 'clockEntries'),
          where('jobId', 'in', batch)
        )
      );
      allEntries = allEntries.concat(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }

    let totalHours = 0;
    for (const entry of allEntries) {
      if (entry.clockIn && entry.clockOut) {
        const inTime = entry.clockIn.toDate ? entry.clockIn.toDate() : new Date(entry.clockIn);
        const outTime = entry.clockOut.toDate ? entry.clockOut.toDate() : new Date(entry.clockOut);
        totalHours += (outTime - inTime) / 3600000;
      }
    }

    return { totalHours: Math.round(totalHours * 100) / 100, entries: allEntries };
  } catch (err) {
    // TASK-4710 — the `projects` module no longer carries tenant-wide clock
    // read (firestore.rules clockEntries). A caller without clock read gets
    // permission-denied here; say "--", never a confident "0.0h".
    if (err?.code === 'permission-denied') return { totalHours: null, entries: [], restricted: true };
    console.error('[Projects] Error loading clock hours:', err);
    return { totalHours: 0, entries: [] };
  }
}

// TASK-9675 — every ticket on the project's jobs, for the Docs tab's ticket
// photos. Same 'in'-chunks as countBilledTicketsForJobs. Never throws: a read
// failure comes back as { failed: true } so the tab says "couldn't load".
// TASK-9674 — also THE reader for the Overview "Field totals" card: one load,
// cached for PROJECT_TICKET_CACHE_MS per (tenant, job set), so moving between
// Overview and Docs doesn't read the tickets twice. Opening a project (or the
// card's refresh, opts.fresh) always reads fresh. Callers get their own
// shallow result object (the Docs tab adds clockEntries to it).
const PROJECT_TICKET_CACHE_MS = 60 * 1000;
let _projectTicketCache = null; // { key, at, tickets }
// COD — a read that started BEFORE a newer read (refresh) or a clear must not
// write its older result into the cache when it lands late.
let _projectTicketCacheSeq = 0;
let _ftLineCacheGen = 0;
function clearProjectTicketCache() {
  _projectTicketCache = null;
  _projectTicketCacheSeq++;
  _ftLineCache.clear();
  _ftLineCacheGen++;
}
async function loadProjectTicketsForDocs(jobIds, opts = {}) {
  const tid = ProjectState.tenantId; // COD — captured once, not re-read between chunks
  if (!tid) return { tickets: [], failed: true };
  const key = `${tid}|${[...(jobIds || [])].sort().join(',')}`;
  const c = _projectTicketCache;
  if (!opts.fresh && c && c.key === key && Date.now() - c.at < PROJECT_TICKET_CACHE_MS) {
    return { tickets: c.tickets.slice(), failed: false };
  }
  const seq = ++_projectTicketCacheSeq;
  try {
    const tickets = [];
    for (const ids of chunkForBatch(jobIds || [], TICKET_IN_QUERY_MAX)) {
      const snap = await getDocs(query(
        collection(db, 'tenants', tid, 'tickets'),
        where('jobId', 'in', ids)
      ));
      snap.docs.forEach(d => tickets.push({ id: d.id, ...d.data() }));
    }
    if (ProjectState.tenantId === tid && seq === _projectTicketCacheSeq) _projectTicketCache = { key, at: Date.now(), tickets };
    return { tickets: tickets.slice(), failed: false };
  } catch (err) {
    console.error('[Projects] Error loading project tickets for Docs:', err && err.code);
    return { tickets: [], failed: true };
  }
}

async function loadProjectInspections(projectId) {
  // Get DVIRs and JSAs from linked jobs
  if (!ProjectState.tenantId || !projectId) return { dvirs: [], jsas: [] };
  try {
    const jobs = await loadProjectJobs(projectId);
    const jobIds = jobs.map(j => j.id);
    if (jobIds.length === 0) return { dvirs: [], jsas: [] };

    // Get vehicle/trailer IDs assigned to these jobs' jobDays
    const assignedVehicleIds = new Set();
    for (let i = 0; i < jobIds.length; i += 10) {
      const batch = jobIds.slice(i, i + 10);
      const jdSnap = await getDocs(
        query(
          collection(db, 'tenants', ProjectState.tenantId, 'jobDays'),
          where('jobId', 'in', batch)
        )
      );
      for (const jd of jdSnap.docs) {
        const data = jd.data();
        (data.trucks || []).forEach(id => assignedVehicleIds.add(id));
        (data.trailers || []).forEach(id => assignedVehicleIds.add(id));
      }
    }

    // Get pretrips (DVIRs) for these vehicles
    let dvirs = [];
    const vIds = [...assignedVehicleIds];
    for (let i = 0; i < vIds.length; i += 10) {
      const batch = vIds.slice(i, i + 10);
      const snap = await getDocs(
        query(
          collection(db, 'tenants', ProjectState.tenantId, 'pretrips'),
          where('truckId', 'in', batch),
          orderBy('createdAt', 'desc'),
          limit(50)
        )
      );
      dvirs = dvirs.concat(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }

    return { dvirs, jsas: [] }; // JSAs would need similar query if stored per-job
  } catch (err) {
    console.error('[Projects] Error loading inspections:', err);
    return { dvirs: [], jsas: [] };
  }
}

// ============================================
// PROJECT CRUD
// ============================================
// ============================================
// TASK-9391 Slice 1 — "New job defaults": what New Job in this project inherits
// ============================================
// A Project is the dispatcher's folder (customer, PO, location). New Job inside it
// starts from the project's MOST RECENT job (last jobDay by date), carrying only
// the groups ticked here. Set ONCE per project — this is deliberately NOT a
// per-copy "choose what to copy" modal (Chris rejected that in TASK-9363).
//
// Stored as `project.newJobDefaults = { carry: {<key>: boolean}, jobTemplateId }`
// — FLAGS ONLY, never copied values. The values are read from the latest job at
// the moment New Job is pressed, so an edit to that job is what the next one gets.
//
// Customer + PO are not in this list because they ALWAYS carry (the project owns
// them). Dates never carry. Notes default OFF: stale instructions reaching drivers
// on the next run is the Loenbro incident (TASK-9363) — when notes are off, the
// job form shows the previous notes read-only with a "Use these" button instead.
const NEW_JOB_CARRY_FIELDS = [
  { key: 'location', label: 'Location & destinations', def: true },
  { key: 'jobType', label: 'Job type, ticket split & load flags', def: true },
  { key: 'contact', label: 'Contact (POC)', def: true },
  { key: 'rates', label: 'Price book & rates', def: true },
  { key: 'times', label: 'Start / end times', def: true },
  { key: 'crew', label: 'Crew (people)', def: false },
  { key: 'equipment', label: 'Vehicles, trailers & equipment', def: false },
  { key: 'notes', label: 'Notes', def: false },
];

// Total over any input: absent / malformed → the defaults above; unknown keys are
// dropped; only a literal boolean overrides a default. The dispatch side reads the
// result with `=== true`, so a shape this function did not produce carries nothing.
function normalizeNewJobDefaults(raw) {
  const src = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
  const carryIn = (src.carry && typeof src.carry === 'object' && !Array.isArray(src.carry)) ? src.carry : {};
  const carry = {};
  for (const f of NEW_JOB_CARRY_FIELDS) {
    carry[f.key] = typeof carryIn[f.key] === 'boolean' ? carryIn[f.key] : f.def;
  }
  const tpl = typeof src.jobTemplateId === 'string' ? src.jobTemplateId.trim() : '';
  return { carry, jobTemplateId: (tpl && tpl.length <= 128) ? tpl : null };
}

// The project's latest job = the job owning the chronologically LAST live jobDay.
// Cancelled days and cancelled jobs never count (a cancelled run is not the
// template for the next one). Jobs with no jobDays at all (legacy GAS imports)
// fall back to the latest job startDate, so a project of legacy jobs still has a
// source. Pure — callers pass the rows in. Returns { job, day } or null.
function pickProjectSourceJob(jobs, jobDays) {
  const live = (jobs || []).filter(j => j && j.id && j.status !== 'cancelled');
  if (live.length === 0) return null;
  const byId = new Map(live.map(j => [j.id, j]));
  const ms = (v) => {
    const d = v?.toDate ? v.toDate() : (v instanceof Date ? v : (v ? new Date(v) : null));
    const t = d ? d.getTime() : NaN;
    return Number.isFinite(t) ? t : null;
  };
  let best = null, bestMs = null;
  for (const d of (jobDays || [])) {
    if (!d || !byId.has(d.jobId) || d.status === 'cancelled') continue;
    const t = ms(d.date);
    if (t === null) continue;
    if (bestMs === null || t > bestMs) { bestMs = t; best = d; }
  }
  if (best) return { job: byId.get(best.jobId), day: best };
  // COD r1 — the fallback is for LEGACY jobs only: jobs with NO jobDays at all.
  // A job whose days are all cancelled is a cancelled run, not a legacy one, and
  // must never supply the next job's crew or instructions.
  const hasAnyDay = new Set((jobDays || []).filter(d => d && d.jobId).map(d => d.jobId));
  const legacy = live.filter(j => !hasAnyDay.has(j.id));
  if (legacy.length === 0) return null;
  let bestJob = null, bestJobMs = null;
  for (const j of legacy) {
    const t = ms(j.startDate) ?? ms(j.createdAt);
    if (t === null) continue;
    if (bestJobMs === null || t > bestJobMs) { bestJobMs = t; bestJob = j; }
  }
  return { job: bestJob || legacy[0], day: null };
}

// jobTemplates (TASK-9258 equipment kits) for the "use a kit" picker. Read here
// rather than borrowed from DispatchState: Pitfall #1 forbids the cross-import,
// and the rules stanza admits any tenant member to read.
async function loadJobTemplatesForProjects() {
  if (!ProjectState.tenantId) return [];
  if (ProjectState.jobTemplates && ProjectState._jobTemplatesTenant === ProjectState.tenantId) {
    return ProjectState.jobTemplates;
  }
  try {
    const snap = await getDocs(collection(db, 'tenants', ProjectState.tenantId, 'jobTemplates'));
    ProjectState.jobTemplates = snap.docs
      .map(d => ({ id: d.id, name: d.data().name || 'Untitled' }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    ProjectState._jobTemplatesTenant = ProjectState.tenantId;
  } catch (err) {
    console.warn('[Projects] jobTemplates unavailable for New job defaults:', err?.code || err);
    ProjectState.jobTemplates = [];
    ProjectState._jobTemplatesTenant = ProjectState.tenantId;
  }
  return ProjectState.jobTemplates;
}

// The checkbox section, shared by the Create and Edit project modals. `prefix` is
// 'new-project' or 'edit-project'; ids are `${prefix}-njd-<key>`.
function renderNewJobDefaultsSection(prefix, rawDefaults) {
  const d = normalizeNewJobDefaults(rawDefaults);
  const boxes = NEW_JOB_CARRY_FIELDS.map(f => `
    <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:0.85rem;min-height:32px;">
      <input type="checkbox" id="${prefix}-njd-${f.key}" ${d.carry[f.key] ? 'checked' : ''}> ${escHtml(f.label)}
    </label>`).join('');
  return `
    <div class="form-section" id="${prefix}-njd-section" data-tour="project-new-job-defaults" style="border-top:1px solid var(--border-light);padding-top:12px;margin-top:12px;">
      <h4 style="font-size:0.9rem;margin:0 0 4px;display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">content_copy</span>
        New job defaults
      </h4>
      <small style="display:block;margin-bottom:8px;color:var(--text-secondary);font-size:0.75rem;">
        What <strong>New Job</strong> in this project copies from the project's most recent job. Customer and PO always carry; dates never do.
      </small>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(210px,1fr));gap:2px 12px;">${boxes}</div>
      <div class="form-group" style="margin-top:8px;">
        <label for="${prefix}-njd-template" style="font-size:0.8rem;">Equipment kit for new jobs (optional)</label>
        <select id="${prefix}-njd-template" data-selected="${escAttr(d.jobTemplateId || '')}">
          <option value="">None</option>
        </select>
      </div>
    </div>`;
}

// Fill the kit picker once the templates are read. Keeps the saved choice even
// before the list lands (data-selected), so a slow read cannot blank it on Save.
async function hydrateNewJobDefaultsTemplatePicker(prefix) {
  const list = await loadJobTemplatesForProjects();
  const sel = document.getElementById(`${prefix}-njd-template`);
  if (!sel) return;
  const want = sel.dataset.selected || '';
  const known = list.some(t => t.id === want);
  sel.innerHTML = '<option value="">None</option>'
    + list.map(t => `<option value="${escAttr(t.id)}">${escHtml(t.name)}</option>`).join('')
    + (want && !known ? `<option value="${escAttr(want)}">(kit no longer exists)</option>` : '');
  sel.value = want;
}

function readNewJobDefaultsFromForm(prefix) {
  const carry = {};
  for (const f of NEW_JOB_CARRY_FIELDS) {
    const el = document.getElementById(`${prefix}-njd-${f.key}`);
    carry[f.key] = el ? !!el.checked : f.def;
  }
  const sel = document.getElementById(`${prefix}-njd-template`);
  // A picker that never hydrated still carries the saved id in data-selected.
  const tpl = sel ? (sel.value || (sel.options.length <= 1 ? (sel.dataset.selected || '') : '')) : '';
  return normalizeNewJobDefaults({ carry, jobTemplateId: tpl || null });
}

// ============================================
// TASK-4696 — Project defaults: values every NEW job on this project starts with
// ============================================
// Chris 2026-09-01 / 09-08: "pick things that will be consistent across every job
// that user adds to this project ... with the ability to overwrite." Stored on the
// project as `project.jobDefaults = { wellName, afeNumber, vendorNumber, type }` —
// VALUES (unlike newJobDefaults, which is flags). The standing PO keeps its own
// `poNumber` key and its own resolver; this sits beside it, same precedence rule:
// the job's own value wins, the project fills a blank.
//
// Pre-fill only (dispatch.js applyProjectJobDefaults). Nothing here rewrites a
// job or a ticket that already exists: changing a default affects the NEXT job,
// and tickets copy the job's fields at clock-out, so an invoiced ticket can never
// move. Deliberately NOT in the set (COD + domain lane, TASKBOARD TASK-4696):
//   • hazmat / interstate / liquidTank — compliance flags need per-job confirmation;
//   • title — inheriting it trips the exact-title duplicate modal on every job;
//   • poNumber — already the project's standing PO.
// wellName holds name + number together, exactly like the job form's one box.
const PROJECT_JOB_DEFAULT_FIELDS = [
  { key: 'wellName', label: 'Well name / number', max: 100, placeholder: 'e.g. Johnson 14-23H' },
  { key: 'afeNumber', label: 'AFE #', max: 50, placeholder: 'AFE number' },
  { key: 'vendorNumber', label: 'Vendor #', max: 50, placeholder: 'Your vendor number with this customer', po: true },
  { key: 'type', label: 'Job type', max: 64 },
];

// Total over any input. Returns only non-empty string fields (an all-blank
// project → {}). Unknown keys are dropped, so a compliance flag smuggled into the
// map can never reach a job form.
function normalizeProjectJobDefaults(raw) {
  const src = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
  const out = {};
  for (const f of PROJECT_JOB_DEFAULT_FIELDS) {
    const v = src[f.key];
    if (typeof v !== 'string') continue;
    let s = f.po ? poNormalizeForSave(v) : v.replace(/\s+/g, ' ').trim();
    if (!s || s.length > f.max) continue;
    if (f.key === 'type' && s === 'job') continue; // 'job' is the form's own default — not a choice worth inheriting
    out[f.key] = s;
  }
  return out;
}

function projectJobDefaultsEqual(a, b) {
  const x = normalizeProjectJobDefaults(a), y = normalizeProjectJobDefaults(b);
  return PROJECT_JOB_DEFAULT_FIELDS.every(f => (x[f.key] || '') === (y[f.key] || ''));
}

// Mirrors firestore.rules projMgr (the only arm that may write a key outside the
// project-manager allow-list, and jobDefaults is NOT on that list): demo, the
// tenant OWNER via ownerId, or a member holding BOTH 'dispatch' and 'projects'
// who is not view/upload-only. MODULE access, never a role. A project manager
// still saves the rest of Edit Project: submitEditProjectBody writes jobDefaults
// only when it changed.
function projectJobDefaultsCanEdit() {
  const S = window.AppState || {};
  if (S.isDemo || S.tenant === 'demo') return true;
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const m = S.memberAccess;
  if (!m || m.status === 'suspended' || m.status === 'removed' || m.viewUploadOnly === true) return false;
  return Array.isArray(m.modules) && m.modules.includes('dispatch') && m.modules.includes('projects');
}

function _projectJobTypeOptionsHtml(selected) {
  const JT = window.JobTypes;
  const want = String(selected || '');
  let opts = [];
  try {
    const list = JT ? JT.list(window.AppState?.settings || {}) : [{ value: 'job', label: 'Job' }];
    opts = JT ? JT.options(list, want || 'job') : list;
  } catch (_) { opts = [{ value: 'job', label: 'Job' }]; }
  if (want && !opts.some(o => o.value === want)) opts.push({ value: want, label: want });
  return '<option value="">No default (each job picks)</option>'
    + opts.filter(o => o.value !== 'job' || want === 'job')
      .map(o => `<option value="${escAttr(o.value)}" ${o.value === want ? 'selected' : ''}>${escHtml(o.label)}${o.archived ? ' (archived)' : ''}</option>`).join('');
}

// Shared by the Create and Edit project modals. ids: `${prefix}-jd-<key>`.
function renderProjectJobDefaultsSection(prefix, rawDefaults) {
  const d = normalizeProjectJobDefaults(rawDefaults);
  const canEdit = projectJobDefaultsCanEdit();
  const dis = canEdit ? '' : 'disabled';
  const field = (f) => f.key === 'type'
    ? `<div class="form-group" style="margin:0;">
        <label for="${prefix}-jd-type" style="font-size:0.8rem;">${escHtml(f.label)}</label>
        <select id="${prefix}-jd-type" ${dis}>${_projectJobTypeOptionsHtml(d.type || '')}</select>
      </div>`
    : `<div class="form-group" style="margin:0;">
        <label for="${prefix}-jd-${f.key}" style="font-size:0.8rem;">${escHtml(f.label)}</label>
        <input type="text" id="${prefix}-jd-${f.key}" maxlength="${f.max}" value="${escAttr(d[f.key] || '')}" placeholder="${escAttr(f.placeholder || '')}" ${dis}>
      </div>`;
  return `
    <div class="form-section" id="${prefix}-jd-section" data-tour="project-job-defaults" style="border-top:1px solid var(--border-light);padding-top:12px;margin-top:12px;">
      <h4 style="font-size:0.9rem;margin:0 0 4px;display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">playlist_add_check</span>
        Same on every job
      </h4>
      <small style="display:block;margin-bottom:8px;color:var(--text-secondary);font-size:0.75rem;">
        Filled in on each new job added to this project. Change it on the job if one is different. Jobs and tickets that already exist are not changed.
      </small>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:8px 12px;">${PROJECT_JOB_DEFAULT_FIELDS.map(field).join('')}</div>
      ${canEdit ? '' : '<small style="display:block;margin-top:6px;color:var(--text-secondary);font-size:0.75rem;">Only someone with Dispatch and Projects access (or the owner) can change these.</small>'}
    </div>`;
}

function readProjectJobDefaultsFromForm(prefix) {
  const raw = {};
  for (const f of PROJECT_JOB_DEFAULT_FIELDS) {
    const el = document.getElementById(`${prefix}-jd-${f.key}`);
    if (el) raw[f.key] = String(el.value || '');
  }
  return normalizeProjectJobDefaults(raw);
}

// ============================================
// TASK-4842 — GEOCODE A PROJECT'S ADDRESS ON SAVE
// ============================================
// Measured 2026-09-09 across all 93 tenants: 12 projects, 12 with
// locationLat: null, 11 with a non-empty location string. Nothing geocoded a
// project, so the customer portal map (TASK-4843) had nothing to pin.
//
// 🔴 RULES THIS FOLLOWS, each from a bug we already shipped:
//  • ONE egress path: the EXISTING `geocodeAddress` callable (one key, one rate
//    limit, one place to rotate). Never a second inline fetch to Google.
//  • AWAITED, not fire-and-forget (TASK-4805: an unregistered background geocode
//    let a job save {lat:null} behind a green check). The save waits for the
//    answer, bounded by a timeout, and then SAVES EITHER WAY.
//  • A failure still saves the project, stores NULL, and says so (amber
//    "Address unmapped" on the card) — never a green check over a null.
//  • An edited address with a failed re-geocode CLEARS the old pin: a confidently
//    wrong pin is worse than a blank one.
//  • A real zero is a number, not a null (`x || null` was the old coercion); the
//    pair (0,0) is refused as null island, same as dispatch plottableCoord.
const PROJECT_GEOCODE_TIMEOUT_MS = 6000;
const PROJECT_NULL_ISLAND_EPS = 1e-7;

// Pure: a stored (lat, lng) that the portal can actually pin, or null.
function projectCoordPair(lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat > 90 || lat < -90 || lng > 180 || lng < -180) return null;
  if (Math.abs(lat) < PROJECT_NULL_ISLAND_EPS && Math.abs(lng) < PROJECT_NULL_ISLAND_EPS) return null;
  return { lat, lng };
}

// TASK-9552 — a location typed/pasted as a coordinate pair ("48.235412, -102.59679",
// copied straight out of Google Maps) is the pin itself: store it, never send it
// to the geocoder (which can't resolve it and left the card "Address unmapped").
// Deliberately strict — anything that isn't EXACTLY two decimal numbers falls
// back to the normal geocode:
//   accepted: "48.23, -102.59" · "48.23,-102.59" · "48.23 -102.59" · "(48.23, -102.59)" · "+48.23, -102.59"
//   refused:  DMS (48°14'N), integers ("48, -102"), leading/trailing text, 3 numbers,
//             out of range / null island (projectCoordPair), and NO lat/lng swap
//             is ever inferred ("-102.59, 48.23" -> lat -102.59 -> refused -> geocode).
const PROJECT_COORD_STRING_RE = /^\s*([+-]?\d{1,3}\.\d+)\s*(?:,\s*|\s+)([+-]?\d{1,3}\.\d+)\s*$/;
function parseProjectCoordString(text) {
  if (typeof text !== 'string' || text.length > 64) return null;
  // Parentheses are optional but must be balanced — "(48.23, -102.59" is refused.
  const t = text.trim();
  const inner = t.startsWith('(') && t.endsWith(')') ? t.slice(1, -1) : t;
  const m = PROJECT_COORD_STRING_RE.exec(inner);
  if (!m) return null;
  return projectCoordPair(Number(m[1]), Number(m[2]));
}

// TASK-9552 — map link for a stored pin. Built ONLY from the validated numeric
// pair, never from the typed text, so nothing a user typed reaches the href.
function projectPinMapUrl(project) {
  const c = projectCoordPair(project?.locationLat, project?.locationLng);
  return c ? `https://www.google.com/maps?q=${c.lat},${c.lng}` : '';
}

// Pure: does this project need the amber "Address unmapped" marker?
function projectAddressUnmapped(project) {
  const loc = typeof project?.location === 'string' ? project.location.trim() : '';
  return !!loc && !projectCoordPair(project.locationLat, project.locationLng);
}

// The amber marker on the dispatcher's card/detail (UX lane's ask): the person
// who can fix a blank customer map is told why it is blank. Amber = DESIGN.md
// warning surface (#FFFBEB / #92400E / #FDE68A) — dark text on a light fill.
function renderAddressUnmappedChip() {
  return `<span class="project-address-unmapped" title="This address could not be found on the map, so the customer portal map has no project pin. Edit the location — try adding a city, state or ZIP."
    style="display:inline-flex;align-items:center;gap:4px;background:#FFFBEB;color:#92400E;border:1px solid #FDE68A;border-radius:10px;padding:2px 8px;font-size:0.7rem;font-weight:600;margin:2px 0 4px;">
    <span class="material-icons" style="font-size:12px;">wrong_location</span> Address unmapped
  </span>`;
}

// Resolve one address through the existing callable. Returns {lat, lng} or null
// on ANY failure (timeout, rate limit, no result, garbage) — never throws.
// TASK-10013 — a TIMEOUT is not a "no": the callable is still running. With
// opts.onTimeout, the caller is handed a promise of the late answer
// ({lat, lng} | null, never rejects) so it can write the pin when it lands
// instead of telling the dispatcher the address "could not be mapped".
async function geocodeProjectLocation(location, opts) {
  const address = typeof location === 'string' ? location.trim() : '';
  if (address.length < 3) return null;
  const toPair = (res) => {
    const lat = typeof res?.data?.lat === 'string' ? Number(res.data.lat) : res?.data?.lat;
    const lng = typeof res?.data?.lng === 'string' ? Number(res.data.lng) : res?.data?.lng;
    return projectCoordPair(lat, lng);
  };
  let timer;
  try {
    const call = httpsCallable(functions, 'geocodeAddress')({ address });
    const TIMED_OUT = {};
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(TIMED_OUT), PROJECT_GEOCODE_TIMEOUT_MS); });
    const res = await Promise.race([call, timeout]);
    if (res === TIMED_OUT || !res) {
      console.warn('[Projects] geocode timed out for project address');
      if (res === TIMED_OUT && typeof opts?.onTimeout === 'function') {
        opts.onTimeout(Promise.resolve(call).then(toPair, () => null));
      }
      return null;
    }
    return toPair(res);
  } catch (err) {
    console.warn('[Projects] geocode failed for project address:', err?.message || err);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// Decide the coordinate to STORE for a save. `prev` is the project as it was
// (null on create). Returns { locationLat, locationLng } — always both keys, so
// a caller can never write one without the other.
//   blank address                        -> null/null (nothing to pin)
//   same address + already-valid pin     -> keep it (no API call)
//   caller supplied a valid pin (copy)   -> keep it (no API call)
//   address IS a "lat, lng" pair         -> store it (no API call, TASK-9552)
//   otherwise                            -> geocode; failure -> null/null
// TASK-10013 — when the geocode TIMES OUT, the result also carries `late`: a
// promise of the answer still on its way ({lat, lng} | null). Callers save null
// now and hand `late` to finishProjectGeocodeLate, which writes the pin later.
async function resolveProjectCoords(location, prev, supplied) {
  const loc = typeof location === 'string' ? location.trim() : '';
  if (!loc) return { locationLat: null, locationLng: null };
  const given = supplied ? projectCoordPair(supplied.locationLat, supplied.locationLng) : null;
  if (given) return { locationLat: given.lat, locationLng: given.lng };
  // TASK-9552 — a typed coordinate pair IS the pin: stored as-is, no API call.
  const typed = parseProjectCoordString(loc);
  if (typed) return { locationLat: typed.lat, locationLng: typed.lng };
  if (prev) {
    const prevLoc = typeof prev.location === 'string' ? prev.location.trim() : '';
    const kept = projectCoordPair(prev.locationLat, prev.locationLng);
    if (prevLoc === loc && kept) return { locationLat: kept.lat, locationLng: kept.lng };
  }
  let late = null;
  const c = await geocodeProjectLocation(loc, { onTimeout: (p) => { late = p; } });
  if (c) return { locationLat: c.lat, locationLng: c.lng };
  return late ? { locationLat: null, locationLng: null, late } : { locationLat: null, locationLng: null };
}

// TASK-10013 — the warning, said once, with the fix in it. Only shown when
// nothing could place the address (not on a timeout that is still running).
function projectUnmappedMessage(prefix, location) {
  const loc = typeof location === 'string' ? location.trim() : '';
  const shown = loc.length > 60 ? loc.slice(0, 57) + '…' : loc;
  return `${prefix} — but “${shown}” couldn't be found on the map. To fix it, edit the project's location: type it as lat, lng (like 47.8672, -103.7135), or a full address with city and state.`;
}

// TASK-10013 — finish a geocode that outlived PROJECT_GEOCODE_TIMEOUT_MS.
// The project was saved with a null pin; when the answer lands, write it — but
// only if the project still has the SAME address and still has no pin (an edit
// or a hand-typed pin in between wins). In a transaction, so that check and the
// write are one step. A late "no" gets the warning the save held back.
// Never throws. Resolves to 'written' | 'skipped' | 'unmapped' | 'error' | 'tenant-changed'.
async function finishProjectGeocodeLate(projectId, location, late) {
  const tenantId = ProjectState.tenantId;
  const loc = typeof location === 'string' ? location.trim() : '';
  let c = null;
  try { c = await late; } catch (_) { c = null; }
  if (!tenantId || !projectId || ProjectState.tenantId !== tenantId) return 'tenant-changed';
  if (!c) {
    // COD r1 (Code) — warn only if the project is STILL that address with no
    // pin. A newer edit may have fixed it; then a warning would be false.
    try {
      const snap = await getDoc(doc(db, 'tenants', tenantId, 'projects', projectId));
      const d = snap.exists() ? (snap.data() || {}) : null;
      if (!d || (typeof d.location === 'string' ? d.location.trim() : '') !== loc || projectCoordPair(d.locationLat, d.locationLng)) return 'skipped';
    } catch (_) {
      // COD r2 — can't tell whether it was fixed in between, so say nothing
      // rather than risk a false warning; the amber chip on the card stays.
      return 'error';
    }
    if (ProjectState.tenantId !== tenantId) return 'tenant-changed';
    window.showToast?.(projectUnmappedMessage('Project saved', loc), 'warning', null, 10000);
    return 'unmapped';
  }
  try {
    const wrote = await runTransaction(db, async (tx) => {
      const pref = doc(db, 'tenants', tenantId, 'projects', projectId);
      const snap = await tx.get(pref);
      if (!snap.exists()) return false;
      const d = snap.data() || {};
      if ((typeof d.location === 'string' ? d.location.trim() : '') !== loc) return false;
      if (projectCoordPair(d.locationLat, d.locationLng)) return false;
      tx.update(pref, { locationLat: c.lat, locationLng: c.lng, updatedAt: Timestamp.now() });
      return true;
    });
    // The projects listener (loadProjects) picks the new pin up and re-renders.
    return wrote ? 'written' : 'skipped';
  } catch (err) {
    console.warn('[Projects] late project pin write failed:', err?.message || err);
    window.logError?.(err, { module: 'projects', operation: 'PROJECT_LATE_GEOCODE', projectId });
    if (ProjectState.tenantId !== tenantId) return 'tenant-changed';
    // COD r2 — the address WAS found; it's the save of the pin that failed.
    window.showToast?.('Project saved — we found it on the map but couldn’t save the pin. Open the project and save it again to retry.', 'warning', null, 10000);
    return 'error';
  }
}

async function createProject(data) {
  // TASK-731: this guard used to return null silently — the New Job "create a
  // new project" flow then skipped the link with zero feedback. Make it loud so
  // a missing tenant context is visible instead of a mystery no-op. This is the
  // early-return path only; the thrown-error path below already toasts (~:451),
  // so this won't double-toast the Projects-UI native flow.
  if (!ProjectState.tenantId) {
    console.warn('[Projects] createProject called with no tenantId — cannot create project.');
    window.logError?.(new Error('createProject: missing tenantId'), { module: 'projects', operation: 'CREATE_PROJECT_NO_TENANT' });
    window.showToast?.('Could not create project — no active company context. Reload and try again.', 'error');
    return null;
  }
  try {
    // TASK-4842 — every create path (form, template, duplicate, and dispatch's
    // "new project" from the job form) funnels through here, so this is the one
    // place a new project's address gets its pin. Awaited, bounded, never fatal.
    const coords = await resolveProjectCoords(data.location, null, data);
    const projectData = {
      name: data.name || 'Untitled Project',
      description: data.description || '',
      customerIds: data.customerIds || [],
      primaryCustomerId: data.primaryCustomerId || data.customerIds?.[0] || null,
      primaryCustomerName: data.primaryCustomerName || '',
      startDate: data.startDate || Timestamp.now(),
      endDate: data.endDate || null,
      status: 'active',
      // Standing PO for the whole project. Jobs linked to this project inherit it
      // ONLY when the job has no PO of its own (see linkJobToProject). A PO is
      // per-corridor/per-project in the field — never per-customer — so this is the
      // right home for "every Lostwood PL job from here on out".
      poNumber: poNormalizeForSave(data.poNumber),
      location: data.location || '',
      // TASK-4842 — was an `||`-to-null coercion, which turned a valid 0 into
      // null. Now the validated pair from resolveProjectCoords (null on failure).
      locationLat: coords.locationLat,
      locationLng: coords.locationLng,
      tags: data.tags || [],
      onSiteContacts: data.onSiteContacts || [],
      portalEnabled: false,
      portalSettings: {},
      portalPin: '',
      templateId: data.templateId || null,
      estimatedRevenue: data.estimatedRevenue || null,
      opportunityId: data.opportunityId || null,
      // TASK-9391 — flags only, normalized; absent when the caller (e.g. the job
      // form's "create a new project") offered no choice, which reads as defaults.
      ...(data.newJobDefaults ? { newJobDefaults: normalizeNewJobDefaults(data.newJobDefaults) } : {}),
      // TASK-4696 — values every new job on the project starts with. Absent when
      // nothing was set (an empty map is not written).
      ...(Object.keys(normalizeProjectJobDefaults(data.jobDefaults)).length
        ? { jobDefaults: normalizeProjectJobDefaults(data.jobDefaults) } : {}),
      createdAt: Timestamp.now(),
      createdBy: window.AppState?.user?.uid || 'unknown',
      createdByName: window.AppState?.user?.displayName || window.AppState?.user?.email || 'Unknown',
      // TASK-9549 — the Project lead starts as whoever made the project.
      ...(window.AppState?.user?.uid ? {
        leadUid: window.AppState.user.uid,
        leadName: window.AppState?.user?.displayName || window.AppState?.user?.email || ''
      } : {}),
      updatedAt: Timestamp.now()
    };

    const ref = await addDoc(
      collection(db, 'tenants', ProjectState.tenantId, 'projects'),
      projectData
    );

    window.logAudit?.('projects', 'PROJECT_CREATE', {
      projectId: ref.id,
      projectName: projectData.name,
      customerId: projectData.primaryCustomerId
    });

    // TASK-4842 — never a green check over a null pin.
    // TASK-10013 — but a geocode that only TIMED OUT is still running: say
    // nothing false, and write the pin when it lands (finishProjectGeocodeLate
    // warns then if the answer is a real "no").
    if (projectAddressUnmapped(projectData) && coords.late) {
      window.showToast?.('Project created — still placing it on the map.', 'success');
      finishProjectGeocodeLate(ref.id, projectData.location, coords.late);
    } else if (projectAddressUnmapped(projectData)) {
      window.showToast?.(projectUnmappedMessage('Project created', projectData.location), 'warning', null, 10000);
    } else {
      window.showToast?.('Project created', 'success');
    }
    await loadProjects();
    return ref.id;
  } catch (err) {
    console.error('[Projects] Error creating project:', err);
    window.logError?.(err, { module: 'projects', operation: 'CREATE_PROJECT' });
    window.showToast?.('Error creating project: ' + (err.message || 'Unknown'), 'error');
    return null;
  }
}

async function updateProject(projectId, updates) {
  if (!ProjectState.tenantId || !projectId) return false;
  try {
    updates.updatedAt = Timestamp.now();
    await updateDoc(
      doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId),
      updates
    );

    window.logAudit?.('projects', 'PROJECT_UPDATE', {
      projectId,
      fields: Object.keys(updates).filter(k => k !== 'updatedAt')
    });

    // Update local cache
    const idx = ProjectState.projects.findIndex(p => p.id === projectId);
    if (idx >= 0) Object.assign(ProjectState.projects[idx], updates);

    return true;
  } catch (err) {
    console.error('[Projects] Error updating project:', err);
    window.logError?.(err, { module: 'projects', operation: 'UPDATE_PROJECT', projectId });
    window.showToast?.('Error updating project', 'error');
    return false;
  }
}

// TASK-9289 B3 — under Firestore's 500-writes-per-transaction cap with headroom
// (r3: each chunk is a transaction that also READS every job, so kept smaller).
const PROJECT_DELETE_BATCH_SIZE = 100;
// TASK-9289 r4 — collect/unlink passes after the deleting flag; the last pass must find none.
const PROJECT_DELETE_MAX_PASSES = 3;

// Pure: the one confirm deleteProject shows.
function deleteProjectMessage(jobCount, billedCount) {
  const jobs = `${jobCount} job${jobCount === 1 ? '' : 's'}`;
  const billed = billedCount > 0
    ? ` Those jobs have ${billedCount} invoiced ticket${billedCount === 1 ? '' : 's'}, which will leave this project’s billed totals.`
    : '';
  return `Delete this project? Its ${jobs} will NOT be deleted — they will be unlinked.${billed}`;
}
function chunkForBatch(items, size) {
  const n = Math.max(1, Math.floor(Number(size) || 1));
  const out = [];
  for (let i = 0; i < (items || []).length; i += n) out.push(items.slice(i, i + n));
  return out;
}

// TASK-9289 r3 — Firestore 'in' takes at most 30 values.
const TICKET_IN_QUERY_MAX = 30;

// Billed tickets across a set of jobs, in 'in'-query chunks. Throws on a read
// failure (callers fail CLOSED).
async function countBilledTicketsForJobs(jobIds) {
  let billed = 0;
  for (const ids of chunkForBatch(jobIds, TICKET_IN_QUERY_MAX)) {
    const snap = await getDocs(query(
      collection(db, 'tenants', ProjectState.tenantId, 'tickets'),
      where('jobId', 'in', ids)
    ));
    billed += countBilledTickets(snap.docs.map(d => d.data()));
  }
  return billed;
}

// TASK-9289 r5 (COD r4) — THE DELETION LOCK IS OWNED. r4's bare `deleting: true`
// could be cleared by anyone: deleteProject's own failure path cleared it
// unconditionally (so an overlapping delete that failed wiped the protection of
// the one still running), and firestore.rules let any dispatcher write
// `deleting: false` between the empty verify and the deleteDoc — then link a job
// into a project about to vanish. Now the lock carries WHO holds it
// (deletingBy = auth uid), WHEN (deletingAt = server time, rules-checked) and
// WHICH ATTEMPT (deletingToken). firestore.rules projectDeleteLockOk: only the
// holder may release it, nobody may take a FRESH lock, and a lock older than
// PROJECT_DELETE_LOCK_MINUTES is stale (a crashed tab) and may be taken over or
// cleared. jobProjectAdmits refuses every link while `deleting` is set at all.
// Keep the minutes in step with the rules' duration.value(15, 'm').
const PROJECT_DELETE_LOCK_MINUTES = 15;

class ProjectDeleteLockBusy extends Error {
  constructor() { super('project-delete-lock-busy'); }
}
class ProjectDeleteLockLost extends Error {
  constructor() { super('project-delete-lock-lost'); }
}

// Pure: is this project doc's lock held and still fresh at `nowMs`? A lock with
// no deletingAt (an r4 flag) is never fresh — nothing can prove it is alive.
function projectDeleteLockFresh(p, nowMs) {
  if (!p || p.deleting !== true) return false;
  const at = p.deletingAt && typeof p.deletingAt.toMillis === 'function' ? p.deletingAt.toMillis() : null;
  if (at == null) return false;
  return nowMs < at + PROJECT_DELETE_LOCK_MINUTES * 60000;
}

function newProjectDeleteLockToken() {
  try { if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID(); } catch (_) { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// Take the lock in a transaction: refuses (ProjectDeleteLockBusy) while someone
// holds a FRESH one. The rules re-judge the same thing server-side.
async function acquireProjectDeleteLock(projectRef, token) {
  const uid = auth.currentUser?.uid || null;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(projectRef);
    if (!snap.exists()) throw new ProjectDeleteLockLost();
    if (projectDeleteLockFresh(snap.data(), Date.now())) throw new ProjectDeleteLockBusy();
    tx.update(projectRef, {
      deleting: true, deletingBy: uid, deletingAt: serverTimestamp(), deletingToken: token,
      updatedAt: Timestamp.now()
    });
  });
}

// Release ONLY our own attempt's lock: a lock that went stale and was taken
// over (different token) is someone else's now, and is left alone.
async function releaseProjectDeleteLock(projectRef, token) {
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(projectRef);
    if (!snap.exists() || snap.data()?.deletingToken !== token) return false;
    tx.update(projectRef, {
      deleting: false, deletingBy: null, deletingAt: null, deletingToken: null,
      updatedAt: Timestamp.now()
    });
    return true;
  });
}

// Delete the project only if OUR lock is still on it — checked and deleted in
// one transaction, so a lock that lapsed and was cleared or taken over (during
// which a link could have landed) never ends in a delete.
async function deleteProjectUnderLock(projectRef, token) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(projectRef);
    const d = snap.exists() ? snap.data() : null;
    if (!d || d.deleting !== true || d.deletingToken !== token) throw new ProjectDeleteLockLost();
    tx.delete(projectRef);
  });
}

// Collect → unlink (transactional chunks) → re-collect until a pass finds none.
// Only ever called while this attempt holds the lock, so nothing new can link.
// r5.1 (COD r5) — EVERY chunk transaction re-reads the project and refuses
// (ProjectDeleteLockLost) unless THIS attempt (`token`) still holds the lock. A
// tab that stalled past the lease — while someone took the lock over (or cleared
// it) and jobs were linked again — must not resume and pull those jobs out.
async function unlinkAllProjectJobs(projectId, token) {
  const projectRef = doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId);
  let unlinked = 0;
  let skipped = 0;
  for (let pass = 0; pass < PROJECT_DELETE_MAX_PASSES; pass++) {
    const fresh = await getDocs(query(
      collection(db, 'tenants', ProjectState.tenantId, 'jobs'),
      where('projectId', '==', projectId)
    ));
    const ids = fresh.docs.map(d => ({ id: d.id }));
    if (!ids.length) return { unlinked, skipped };
    if (pass === PROJECT_DELETE_MAX_PASSES - 1) throw new Error(`jobs still linked after ${pass} passes`);
    for (const chunk of chunkForBatch(ids, PROJECT_DELETE_BATCH_SIZE)) {
      const r = await runTransaction(db, async (tx) => {
        const lockSnap = await tx.get(projectRef);
        const lk = lockSnap.exists() ? lockSnap.data() : null;
        if (!lk || lk.deleting !== true || lk.deletingToken !== token) throw new ProjectDeleteLockLost();
        const refs = chunk.map(j => doc(db, 'tenants', ProjectState.tenantId, 'jobs', j.id));
        const snaps = [];
        for (const ref of refs) snaps.push(await tx.get(ref));
        let u = 0, s = 0;
        snaps.forEach((snap, i) => {
          if (snap.exists() && (snap.data().projectId || null) === projectId) {
            tx.update(refs[i], { projectId: null, updatedAt: Timestamp.now() });
            u++;
          } else { s++; }
        });
        return { u, s };
      });
      unlinked += r.u; skipped += r.s;
    }
  }
  return { unlinked, skipped };
}

async function deleteProject(projectId) {
  if (!ProjectState.tenantId || !projectId) return;

  try {
    // TASK-9289 B3 — read the linked jobs with a query that THROWS on failure.
    // loadProjectJobs swallows errors into [], which here would delete the
    // project and leave every job pointing at a project that no longer exists.
    const jobsSnap = await getDocs(query(
      collection(db, 'tenants', ProjectState.tenantId, 'jobs'),
      where('projectId', '==', projectId)
    ));
    const jobs = jobsSnap.docs.map(d => ({ id: d.id }));
    // TASK-9289 r3 — the same accounting as changeJobProject: count what leaves
    // this project's billed totals and SAY it in the one confirm. A failed count
    // refuses the delete (fail closed), it does not guess zero.
    const billedCount = await countBilledTicketsForJobs(jobs.map(j => j.id));
    if (!confirm(deleteProjectMessage(jobs.length, billedCount))) return;

    // TASK-9289 r4 (COD r3) — LOCK FIRST, then collect the jobs fresh. The job
    // list above is only for the confirm; a link landing between it and the
    // delete would leave a job pointing at a deleted project.
    // r5 (COD r4) — the lock is OWNED (see PROJECT_DELETE_LOCK_MINUTES).
    const projectRef = doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId);
    const token = newProjectDeleteLockToken();
    try {
      await acquireProjectDeleteLock(projectRef, token);
    } catch (lockErr) {
      if (lockErr instanceof ProjectDeleteLockBusy) {
        window.showToast?.('Someone else is deleting this project right now — nothing was changed.', 'info', null, 8000);
        return;
      }
      throw lockErr;
    }
    let unlinked = 0;
    let skipped = 0;
    try {
      ({ unlinked, skipped } = await unlinkAllProjectJobs(projectId, token));
      await deleteProjectUnderLock(projectRef, token);
    } catch (inner) {
      // Release OUR lock (only ours — never another attempt's) so the project
      // can be linked again. If this fails too the lock stays until it goes
      // stale, and deleting again finishes the job.
      try { await releaseProjectDeleteLock(projectRef, token); } catch (_) { /* reported below */ }
      throw inner;
    }

    window.logAudit?.('projects', 'PROJECT_DELETE', { projectId, unlinkedJobs: unlinked, skippedJobs: skipped, billedTicketCount: billedCount });
    window.showToast?.('Project deleted', 'success');

    ProjectState.currentProject = null;
    ProjectState.viewMode = 'list';
    await loadProjects();
    renderProjectsView();
  } catch (err) {
    console.error('[Projects] Error deleting project:', err);
    window.logError?.(err, { module: 'projects', operation: 'DELETE_PROJECT', projectId });
    window.showToast?.(err instanceof ProjectDeleteLockLost
      ? 'Project NOT deleted — the delete was interrupted. Try again.'
      : 'Error deleting project', 'error');
  }
}

// TASK-9289 r5 — take back a project saveJob created a moment ago for a job
// whose move into it then did not go through (declined, refused, conflict), so
// "nothing was saved" stays true. Same owned lock as deleteProject; deletes only
// if NO job is linked to it. No confirm, no success toast: the caller says what
// happened. Returns true when the project is gone.
async function discardUnusedProject(projectId) {
  if (!ProjectState.tenantId || !projectId) return false;
  const projectRef = doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId);
  const token = newProjectDeleteLockToken();
  try {
    await acquireProjectDeleteLock(projectRef, token);
  } catch (err) {
    console.warn('[Projects] discardUnusedProject: could not lock', err);
    return false;
  }
  try {
    const linked = await getDocs(query(
      collection(db, 'tenants', ProjectState.tenantId, 'jobs'),
      where('projectId', '==', projectId)
    ));
    if (linked.docs.length) throw new Error('project has linked jobs');
    await deleteProjectUnderLock(projectRef, token);
    window.logAudit?.('projects', 'PROJECT_DELETE', { projectId, unlinkedJobs: 0, skippedJobs: 0, billedTicketCount: 0, source: 'job-save-rollback' });
    try { await loadProjects(); } catch (_) { /* list refresh only */ }
    return true;
  } catch (err) {
    console.warn('[Projects] discardUnusedProject: kept', projectId, err);
    try { await releaseProjectDeleteLock(projectRef, token); } catch (_) { /* stale lock expires */ }
    return false;
  }
}

// TASK-9289 B2 — a project names its customers in primaryCustomerId +
// customerIds[]. A job for a customer the project does not name must not be
// linked: its tickets would sum into a rollup that still reads single-customer.
// A project that names NO customer yet has nothing to conflict with.
//
// TASK-9289 r3 — now the EXACT mirror of firestore.rules jobProjectAdmits, which
// enforces it server-side: the project's customerIds must contain the job's
// customerId; a customer-less job fits only a customer-less project. (It used to
// let any job into a project that named nobody, and read primaryCustomerId — the
// rule reads customerIds only, and createProject always puts the primary there.)
function linkCustomerConflict(project, customerId) {
  if (!project) return false; // unknown here — the transaction re-reads it
  const ids = Array.isArray(project.customerIds) ? project.customerIds : [];
  const cust = typeof customerId === 'string' && customerId ? customerId : null;
  return cust ? !ids.includes(cust) : ids.length > 0;
}

// TASK-9599 — the ONE conflict a dispatcher may resolve in the same save: the
// project names NO customer yet (customerIds empty — a project made from a
// customer-less job) and this job has one. The project ADOPTS the customer, in
// the same transaction as the link, so firestore.rules jobProjectAdmits (which
// reads the project with getAfter) sees it naming the customer. A project that
// already names anyone never adopts — that block stays exactly as it was.
// COD r1 (Edge): a project whose primaryCustomerId already names SOMEONE ELSE
// (duplicateProject copies it separately from customerIds) is not "no customer"
// — adopting would overwrite that name. Blank or the same customer only.
function projectAdoptsCustomer(project, customerId) {
  if (!project || project.deleting === true) return false;
  const ids = Array.isArray(project.customerIds) ? project.customerIds : [];
  if (!(ids.length === 0 && typeof customerId === 'string' && customerId !== '')) return false;
  return !project.primaryCustomerId || project.primaryCustomerId === customerId;
}

// TASK-9599 — the refusal text, honest about WHICH conflict it is. It used to say
// a project "doesn't include" / "is for another customer" even when the project
// named no customer at all.
function linkCustomerConflictMessage(project, customerId, jobTitle) {
  const pn = project?.name ? `“${project.name}”` : 'That project';
  const jt = `“${jobTitle || 'this job'}”`;
  const ids = Array.isArray(project?.customerIds) ? project.customerIds : [];
  if (!ids.length && !project?.primaryCustomerId) {
    return `Not linked — ${pn} has no customer yet and ${jt} is for ${getCustomerName(customerId)}. Add the customer to the project (Edit Project), or link it from the job form, which offers to add it.`;
  }
  if (!customerId) {
    return `Not linked — ${pn} is for ${project?.primaryCustomerName || 'a customer'} and ${jt} has no customer. Set the job’s customer first.`;
  }
  return `Not linked — ${jt} is for a customer that ${pn} doesn’t include. Add the customer in Edit Project first.`;
}

// TASK-9599 — asks before a project adopts a customer. Centered confirm when the
// app's showConfirm is up (Pitfall #6), native confirm otherwise.
async function confirmAdoptCustomer(project, customerName) {
  const pn = project?.name ? `“${project.name}”` : 'This project';
  const msg = `${pn} has no customer yet. Add ${customerName || 'this job’s customer'} to the project and link this job to it? Jobs already in the project stay in it and bill under this customer.`;
  if (typeof window.showConfirm === 'function') {
    return !!(await window.showConfirm('Add customer to project?', msg, { confirmText: 'Add customer & link', cancelText: 'Cancel' }));
  }
  return confirm(msg);
}

// TASK-9289 S2 trap — linking a job moves EVERY ticket on it into the
// project's rollup (billing.js _billingProjectRollup keys on job.projectId),
// invoiced ones included. Mirror of dispatch.js _ticketIsBilled (itself a
// mirror of billing.js isTicketInvoiced + the archived/locked/sent lock) plus
// paid. NOT a cross-import (Pitfall #1). Deleted tickets never count.
const LINK_BILLED_STATUSES = ['archived', 'locked', 'sent', 'invoiced'];
function countBilledTickets(tickets) {
  let n = 0;
  for (const t of (tickets || [])) {
    if (!t || t.status === 'deleted') continue;
    if (t.invoiced === true || t.paid === true || !!t.invoiceNumber || !!t.invoicedAt ||
        LINK_BILLED_STATUSES.includes(t.status)) n++;
  }
  return n;
}

// ============================================
// TASK-9289 COD r1 — THE ONE GATED PATH FOR jobs.projectId
// ============================================
// Every client writer that changes a job's projectId comes through here: the
// job form's create and edit saves (dispatch.js), the project page's Link
// Existing and Unlink, and the dupe-gate add-day carry. billing.js
// _billingProjectRollup keys on job.projectId, so moving a job moves EVERY one of
// its tickets — invoiced included — into, out of, or between project rollups.
//
// opts:
//   expectedPreviousProjectId — the projectId the caller believes the job has
//                  (the form's value at open). undefined = whatever is read now.
//                  Enforced again INSIDE the transaction: a link someone else
//                  made in the meantime is refused, never overwritten.
//   newJob       — created in this same save: no tickets can exist, no ticket read.
//   billedPolicy — 'confirm' (default: a person asked for this move) asks before
//                  moving billed tickets; 'refuse' (a move the app makes on the
//                  side of another action) never moves them.
//   source       — audit tag.
//   quiet        — no success toast (the caller says what happened).
// Returns { ok, reason?, billedCount?, projectId?, currentProjectId? } where
// projectId is the job's projectId AFTER the call.
function _projName(id) {
  if (!id) return 'no project';
  const p = ProjectState.projects.find(x => x.id === id);
  return p?.name ? `“${p.name}”` : 'another project';
}

// Pure: the confirm text for moving billed tickets between rollups.
function billedMoveMessage(title, billedCount, fromName, toName, fromId, toId) {
  const n = `${billedCount} invoiced ticket${billedCount === 1 ? '' : 's'}`;
  const it = billedCount === 1 ? 'it' : 'them';
  let move;
  if (fromId && toId) move = `Moving it takes ${it} out of ${fromName}’s billed totals and into ${toName}’s.`;
  else if (fromId) move = `Taking it out of ${fromName} removes ${it} from that project’s billed totals.`;
  else move = `Linking it moves ${it} into ${toName}’s billed totals.`;
  return `“${title || 'This job'}” has ${n}. ${move} Continue?`;
}

// TASK-9289 r4 — the ONLY extra fields changeJobProject may write alongside
// projectId: the job's customer, which firestore.rules now pairs with the
// project. Anything else is dropped, so this can never become a generic write.
function pickAlsoSet(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const out = {};
  if ('customerId' in raw) out.customerId = (typeof raw.customerId === 'string' && raw.customerId) ? raw.customerId : null;
  if ('customer' in raw) out.customer = typeof raw.customer === 'string' ? raw.customer : '';
  return Object.keys(out).length ? out : null;
}

class ProjectLinkConflict extends Error {
  constructor(current) { super('project-link-conflict'); this.current = current; }
}
// TASK-9289 r3 — the destination project, re-read INSIDE the transaction, is
// gone or no longer names the job's CURRENT customer.
class ProjectLinkTargetRefused extends Error {
  constructor(kind, current, pData, cust) { super('project-link-target-refused'); this.kind = kind; this.current = current; this.pData = pData || null; this.cust = cust || null; }
}

async function changeJobProject(jobId, toProjectId, opts = {}) {
  if (!ProjectState.tenantId || !jobId) return { ok: false, reason: 'no-tenant' };
  const to = toProjectId || null;
  const jobRef = doc(db, 'tenants', ProjectState.tenantId, 'jobs', jobId);
  try {
    const jobSnap = await getDoc(jobRef);
    if (!jobSnap.exists()) {
      window.showToast?.('That job no longer exists.', 'error');
      return { ok: false, reason: 'missing' };
    }
    const jobData = jobSnap.data() || {};
    const from = jobData.projectId || null;
    const expected = opts.expectedPreviousProjectId === undefined ? from : (opts.expectedPreviousProjectId || null);
    // TASK-9289 r4 — the job's customer can only change TOGETHER with its
    // project decision now (firestore.rules checks the pair whenever either
    // moves). saveJob hands the new customer here so both land in ONE write.
    const alsoSet = pickAlsoSet(opts.alsoSet);
    const hasPendingPo = Object.prototype.hasOwnProperty.call(opts, 'pendingPo');
    const custAfter = alsoSet && ('customerId' in alsoSet) ? alsoSet.customerId : jobData.customerId;

    if (from !== expected) {
      window.showToast?.(
        `Project not changed — someone else moved “${jobData.title || 'this job'}” to ${_projName(from)} since you opened it. Reopen the job to see its current project.`,
        'error', null, 10000);
      return { ok: false, reason: 'changed', currentProjectId: from, projectId: from };
    }
    if (to === from && !alsoSet) return { ok: true, unchanged: true, projectId: from, billedCount: 0 };

    const toProj = to ? ProjectState.projects.find(p => p.id === to) : null;
    // TASK-9599 — opts.adoptCustomer: true = the caller already asked the
    // dispatcher (saveJob); 'ask' = ask here (project page Link Existing);
    // anything else = never adopt (side-effect links: add-day carry, AR/AP).
    // `mayAdopt` is what the transaction honours; it only ever lets a project
    // with NO customers adopt this job's one.
    // COD r1 (Code Reviewer): consent is for ONE customer. `adoptFor` is the
    // customer the dispatcher said yes to; the transaction adopts only if the
    // job's customer it reads is still that one (a customer edit landing during
    // the dialog must not be adopted unasked).
    let adoptFor = opts.adoptCustomer === true
      ? ((typeof opts.adoptFor === 'string' && opts.adoptFor) ? opts.adoptFor : custAfter)
      : null;
    if (to && linkCustomerConflict(toProj, custAfter)) {
      if (projectAdoptsCustomer(toProj, custAfter) && opts.adoptCustomer === 'ask') {
        const custName = (alsoSet && 'customer' in alsoSet && alsoSet.customer) || jobData.customer || getCustomerName(custAfter);
        if (!(await confirmAdoptCustomer(toProj, custName))) {
          return { ok: false, reason: 'declined-adopt', projectId: from };
        }
        adoptFor = custAfter;
      } else if (!(projectAdoptsCustomer(toProj, custAfter) && adoptFor && adoptFor === custAfter)) {
        window.showToast?.(linkCustomerConflictMessage(toProj, custAfter, jobData.title), 'error', null, 9000);
        return { ok: false, reason: 'customer', projectId: from };
      }
    }
    let adopted = null; // TASK-9599 — the customer the project adopted in the transaction, if any

    let billedCount = 0;
    if (!opts.newJob && to !== from) {
      try {
        const tSnap = await getDocs(query(
          collection(db, 'tenants', ProjectState.tenantId, 'tickets'),
          where('jobId', '==', jobId)
        ));
        billedCount = countBilledTickets(tSnap.docs.map(d => d.data()));
      } catch (readErr) {
        // Fail CLOSED: "could not look" is not "none".
        console.warn('[Projects] billed-ticket check failed — project not changed:', readErr);
        window.showToast?.('Project not changed — could not check this job’s tickets. Try again.', 'error', null, 7000);
        return { ok: false, reason: 'unverified', projectId: from };
      }
      if (billedCount > 0) {
        if (opts.billedPolicy === 'refuse') return { ok: false, reason: 'billed', billedCount, projectId: from };
        // r5 — the caller already asked about exactly this many (or more)
        // invoiced tickets before it wrote anything (confirmBilledJobMove):
        // ONE question per save. More than were confirmed = new invoices since
        // the question, so it is asked again.
        const preConfirmed = Number.isFinite(opts.billedConfirmed) && billedCount <= opts.billedConfirmed;
        if (!preConfirmed && !confirm(billedMoveMessage(jobData.title, billedCount, _projName(from), _projName(to), from, to))) {
          return { ok: false, reason: 'declined', billedCount, projectId: from };
        }
      }
    }

    // Inherit the project's standing PO — but ONLY into a job that has none.
    // Fill-if-blank is the whole safety property here: a job carrying its own PO
    // is carrying it for a reason (one-off change order, a different corridor,
    // a number the customer issued for that load), and silently restamping it
    // would push a wrong PO onto an invoice. Never overwrite. Decided INSIDE the
    // transaction from the doc it is about to write — and (r4, COD r3) from the
    // project doc the transaction READ, never the ProjectState cache.
    let projectPo = '';
    let inherited = false;
    let poSource = null;
    let poApplied = null; // r5 — the PO fields the transaction wrote, for a caller that writes the job next
    const projectRef = to ? doc(db, 'tenants', ProjectState.tenantId, 'projects', to) : null;
    await runTransaction(db, async (tx) => {
      const cur = await tx.get(jobRef);
      // TASK-9289 r3 — the destination project and the job's CURRENT customer
      // are re-read here, not trusted from the pre-read / ProjectState cache: a
      // customer edit or an Edit Project in the meantime is what the rule will
      // judge, so it is what this judges too.
      const projSnap = projectRef ? await tx.get(projectRef) : null;
      if (!cur.exists()) throw new ProjectLinkConflict(null);
      const curProject = cur.data().projectId || null;
      if (curProject !== from) throw new ProjectLinkConflict(curProject);
      const txCust = alsoSet && ('customerId' in alsoSet) ? alsoSet.customerId : cur.data().customerId;
      if (projectRef) {
        if (!projSnap.exists()) throw new ProjectLinkTargetRefused('missing', curProject);
        const pData = projSnap.data() || {};
        // r4 — a project being deleted admits nothing (the rule refuses it too).
        if (pData.deleting === true) throw new ProjectLinkTargetRefused('deleting', curProject);
        adopted = null;
        if (linkCustomerConflict(pData, txCust)) {
          // TASK-9599 — judged on the project doc THIS transaction read: a
          // project that picked up any customer since the dispatcher said yes
          // is a real conflict again, never overwritten.
          if (!(adoptFor && txCust === adoptFor && projectAdoptsCustomer(pData, txCust))) throw new ProjectLinkTargetRefused('customer', curProject, pData, txCust);
          const txCustName = (alsoSet && 'customer' in alsoSet ? alsoSet.customer : cur.data().customer) || getCustomerName(txCust);
          adopted = { customerId: txCust, customerName: txCustName };
        }
        projectPo = to !== from ? poNormalizeForSave(pData.poNumber) : '';
      }
      const updates = { ...(alsoSet || {}), projectId: to, updatedAt: Timestamp.now() };
      inherited = false; poSource = null;
      if (projectPo) {
        // r5 (COD r4) — a caller that writes the job's PO right after this
        // (saveJob's update, when the project step has to go first) passes the
        // PO it is ABOUT to write as `pendingPo`; fill-if-blank judges THAT,
        // and the caller carries `poApplied` into its own write. Judging the
        // stored PO here and letting the form's blank land on top of it is how
        // an inherited PO used to vanish on save.
        const existingPo = poNormalizeForSave(hasPendingPo ? opts.pendingPo : cur.data().poNumber);
        if (!existingPo) {
          updates.poNumber = projectPo;
          updates.poVisible = true;
          inherited = true;
          poSource = 'link';
        } else if (existingPo === projectPo) {
          // The New Job modal prefills #job-po from the project, so saveJob() has
          // ALREADY written this PO — without this branch the audit trail would
          // miss the most common way a PO is inherited.
          updates.poVisible = true;
          poSource = 'prefill';
        }
      }
      poApplied = null;
      if ('poNumber' in updates || 'poVisible' in updates) {
        poApplied = {};
        if ('poNumber' in updates) poApplied.poNumber = updates.poNumber;
        if ('poVisible' in updates) poApplied.poVisible = updates.poVisible;
      }
      // TASK-9599 — the project names the customer in the SAME commit, so
      // firestore.rules jobProjectAdmits (getAfter on the project) admits the
      // job. Same three fields Edit Project writes for a single customer.
      if (adopted) {
        tx.update(projectRef, {
          customerIds: [adopted.customerId],
          primaryCustomerId: adopted.customerId,
          primaryCustomerName: adopted.customerName,
          updatedAt: Timestamp.now()
        });
      }
      tx.update(jobRef, updates);
    });

    if (adopted) {
      // Keep the cached project in step until the next load, so the next check
      // in this session judges the project as it now is.
      const cached = ProjectState.projects.find(p => p.id === to);
      if (cached) {
        cached.customerIds = [adopted.customerId];
        cached.primaryCustomerId = adopted.customerId;
        cached.primaryCustomerName = adopted.customerName;
      }
      const cur = ProjectState.currentProject;
      if (cur && cur.id === to && cur !== cached) {
        cur.customerIds = [adopted.customerId];
        cur.primaryCustomerId = adopted.customerId;
        cur.primaryCustomerName = adopted.customerName;
      }
      window.logAudit?.('projects', 'PROJECT_CUSTOMER_ADOPTED', {
        projectId: to, customerId: adopted.customerId, customerName: adopted.customerName,
        jobId, source: opts.source || 'unknown'
      });
    }

    if (to === from) return { ok: true, unchanged: true, projectId: from, billedCount: 0 }; // customer-only (r4): the server trigger writes PROJECT_JOB_CUSTOMER_CHANGE
    window.logAudit?.('projects', to ? 'PROJECT_JOB_LINK' : 'PROJECT_JOB_UNLINK', {
      jobId,
      projectId: to,
      previousProjectId: from,
      billedTicketCount: billedCount,
      source: opts.source || 'unknown'
    });
    if (poSource) {
      window.logAudit?.('projects', 'PROJECT_PO_INHERITED', { jobId, projectId: to, poNumber: projectPo, source: poSource });
    }
    if (!opts.quiet) {
      if (!to) window.showToast?.('Job unlinked from project', 'success');
      else if (inherited) window.showToast?.(`Job linked — PO ${projectPo} applied from project`, 'success');
      else window.showToast?.('Job linked to project', 'success');
    }
    return { ok: true, billedCount, projectId: to, poApplied };
  } catch (err) {
    if (err instanceof ProjectLinkConflict) {
      window.showToast?.(
        `Project not changed — this job was moved to ${_projName(err.current)} by someone else a moment ago. Reopen it to see its current project.`,
        'error', null, 10000);
      return { ok: false, reason: 'changed', currentProjectId: err.current, projectId: err.current };
    }
    if (err instanceof ProjectLinkTargetRefused) {
      window.showToast?.(err.kind === 'missing'
        ? `Project not changed — ${_projName(to)} no longer exists.`
        : err.kind === 'deleting'
        ? `Project not changed — ${_projName(to)} is being deleted.`
        : (err.pData && !(Array.isArray(err.pData.customerIds) && err.pData.customerIds.length))
        ? linkCustomerConflictMessage(err.pData, err.cust, null)
        : `Not linked — ${_projName(to)} doesn’t include this job’s customer (it changed a moment ago). Add the customer in Edit Project first.`,
        'error', null, 9000);
      return { ok: false, reason: err.kind === 'missing' ? 'missing-project' : (err.kind === 'deleting' ? 'deleting' : 'customer'), projectId: err.current };
    }
    console.error('[Projects] Error changing job project:', err);
    window.logError?.(err, { module: 'projects', operation: 'CHANGE_JOB_PROJECT', jobId, toProjectId: to });
    window.showToast?.('Could not change the job’s project — nothing was changed.', 'error');
    return { ok: false, reason: 'error' };
  }
}

// Kept names — every caller goes through changeJobProject.
async function linkJobToProject(jobId, projectId, opts = {}) {
  return changeJobProject(jobId, projectId, opts);
}

async function unlinkJobFromProject(jobId, opts = {}) {
  return changeJobProject(jobId, null, { source: 'project-unlink', ...opts });
}

// TASK-9289 r5 (COD r4) — the invoiced-ticket question, asked BEFORE a save
// writes anything. saveJob's "create a new project" edit path needs the answer
// before it creates the project: asking inside changeJobProject (after the
// project exists) meant a "no" left an orphan project, and r4's unlink-then-link
// asked TWICE. The count is handed on to changeJobProject as `billedConfirmed`,
// which then does not ask again unless more invoiced tickets appeared since.
// Fails CLOSED like the gate: "could not look" is not "none".
// Returns { ok:true, billedCount } | { ok:false, reason:'declined'|'unverified', billedCount? }.
async function confirmBilledJobMove(jobId, { title = '', fromProjectId = null, toName = 'the new project' } = {}) {
  if (!ProjectState.tenantId || !jobId) return { ok: false, reason: 'unverified' };
  let billedCount = 0;
  try {
    const tSnap = await getDocs(query(
      collection(db, 'tenants', ProjectState.tenantId, 'tickets'),
      where('jobId', '==', jobId)
    ));
    billedCount = countBilledTickets(tSnap.docs.map(d => d.data()));
  } catch (readErr) {
    console.warn('[Projects] billed-ticket pre-check failed:', readErr);
    return { ok: false, reason: 'unverified' };
  }
  if (billedCount > 0 && !confirm(billedMoveMessage(title, billedCount, _projName(fromProjectId), toName, fromProjectId, 'new'))) {
    return { ok: false, reason: 'declined', billedCount };
  }
  return { ok: true, billedCount };
}

// ============================================
// NOTES CRUD
// ============================================
// TASK-9549 — every note is 'internal' (crew only) or 'customer' (shown on the
// customer portal). Default INTERNAL; a note with no field reads as internal
// (portalNoteProjection.isCustomerVisibleNote is the server's rule — keep these
// two in step). Customer-posted notes arrive via portalAddNote, source 'portal'
// (the "From customer" badge here is staff-side display only; the portal takes
// provenance from a server-only registry).
function noteVisibility(n) {
  return n && n.visibility === 'customer' ? 'customer' : 'internal';
}
function noteFromCustomer(n) {
  return !!n && (n.source === 'portal' || n.createdBy === 'portal');
}

async function addNote(projectId, text, mentions = [], visibility = 'internal') {
  if (!ProjectState.tenantId || !projectId || !text.trim()) return;
  try {
    await addDoc(
      collection(db, 'tenants', ProjectState.tenantId, 'projects', projectId, 'notes'),
      {
        text: text.trim(),
        author: window.AppState?.user?.uid || 'unknown',
        authorName: window.AppState?.user?.displayName || window.AppState?.user?.email || 'Unknown',
        mentions,
        visibility: visibility === 'customer' ? 'customer' : 'internal',
        createdAt: Timestamp.now()
      }
    );

    // Send SMS to mentioned people
    if (mentions.length > 0) {
      const people = getPeople();
      for (const personId of mentions) {
        const person = people.find(p => p.id === personId);
        if (person?.phone) {
          try {
            const project = ProjectState.projects.find(p => p.id === projectId);
            const smsBody = `[DispatchDeck] You were mentioned in project "${project?.name || 'Unknown'}": ${text.trim().substring(0, 120)}`;
            // Use existing SMS infrastructure
            await addDoc(collection(db, 'tenants', ProjectState.tenantId, 'scheduledReminders'), {
              type: 'project-mention',
              phone: person.phone,
              body: smsBody,
              sendAt: Timestamp.now(),
              sent: false,
              projectId,
              createdAt: Timestamp.now()
            });
          } catch (smsErr) {
            console.warn('[Projects] SMS mention notification failed:', smsErr);
          }
        }
      }
    }

    window.showToast?.('Note added', 'success');
  } catch (err) {
    console.error('[Projects] Error adding note:', err);
    window.showToast?.('Error adding note', 'error');
  }
}

// TASK-9549 — flip one note between Internal and Customer-visible. Staff-only
// (rules: dispatch module or the project's manager). Customer notes stay shared.
async function setNoteVisibility(projectId, noteId, visibility) {
  if (!ProjectState.tenantId || !projectId || !noteId) return;
  const v = visibility === 'customer' ? 'customer' : 'internal';
  if (v === 'customer') {
    const project = ProjectState.projects.find(p => p.id === projectId);
    const msg = project?.portalEnabled
      ? 'Show this note to the customer on their project portal?'
      : 'Mark this note customer-visible? The portal is off now — the customer sees it if you turn the portal on.';
    if (!confirm(msg)) return;
  }
  try {
    await updateDoc(doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId, 'notes', noteId), { visibility: v });
    window.showToast?.(v === 'customer' ? 'Customer can see this note' : 'Note is internal', 'success');
    if (ProjectState.currentProject?.id === projectId) renderProjectsView();
  } catch (err) {
    console.error('[Projects] Error changing note visibility:', err);
    window.showToast?.('Could not change who sees this note', 'error');
  }
}

async function deleteNote(projectId, noteId) {
  if (!ProjectState.tenantId) return;
  try {
    await deleteDoc(doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId, 'notes', noteId));
    window.showToast?.('Note deleted', 'success');
  } catch (err) {
    console.error('[Projects] Error deleting note:', err);
  }
}

// ============================================
// CHANGE ORDERS CRUD
// ============================================
async function addChangeOrder(projectId, data) {
  if (!ProjectState.tenantId || !projectId) return;
  try {
    await addDoc(
      collection(db, 'tenants', ProjectState.tenantId, 'projects', projectId, 'changeOrders'),
      {
        description: data.description || '',
        amount: parseFloat(data.amount) || 0,
        status: 'pending',
        // Chris 2026-10-17: office change orders can show on the customer portal
        // (description + status only — never the amount or who typed it; see
        // functions/src/services/portalChangeOrderProjection.js). fix/2096-preship:
        // ONLY when the "Show this on the customer portal" box was ticked —
        // customerVisible is decided here, once; firestore.rules never lets an
        // update turn it on, so an internal change order stays internal.
        source: 'office',
        customerVisible: data.customerVisible === true,
        createdBy: window.AppState?.user?.uid || 'unknown',
        createdByName: window.AppState?.user?.displayName || window.AppState?.user?.email || 'Unknown',
        createdAt: Timestamp.now()
      }
    );
    window.showToast?.('Change order added', 'success');
  } catch (err) {
    console.error('[Projects] Error adding change order:', err);
    window.showToast?.('Error adding change order', 'error');
  }
}

async function updateChangeOrderStatus(projectId, coId, status) {
  if (!ProjectState.tenantId) return;
  try {
    const updates = { status, updatedAt: Timestamp.now() };
    if (status === 'approved') updates.approvedAt = Timestamp.now();
    await updateDoc(
      doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId, 'changeOrders', coId),
      updates
    );
    window.showToast?.(`Change order ${status}`, 'success');
    // Decrement local badge count if this was a customer portal request moving off pending
    const currentProject = ProjectState.projects.find(p => p.id === projectId);
    if (currentProject && status !== 'pending') {
      const prev = currentProject.pendingCustomerRequestCount || 0;
      currentProject.pendingCustomerRequestCount = Math.max(0, prev - 1);
    }
  } catch (err) {
    console.error('[Projects] Error updating change order:', err);
  }
}

// ============================================
// TEMPLATE CRUD
// ============================================
async function createTemplate(data) {
  if (!ProjectState.tenantId) return null;
  try {
    const templateData = {
      name: data.name || 'Untitled Template',
      description: data.description || '',
      defaultJobs: data.defaultJobs || [],
      defaultTags: data.defaultTags || [],
      createdAt: Timestamp.now(),
      createdBy: window.AppState?.user?.uid || 'unknown'
    };
    const ref = await addDoc(
      collection(db, 'tenants', ProjectState.tenantId, 'projectTemplates'),
      templateData
    );
    window.showToast?.('Template created', 'success');
    await loadTemplates();
    return ref.id;
  } catch (err) {
    console.error('[Projects] Error creating template:', err);
    window.showToast?.('Error creating template', 'error');
    return null;
  }
}

async function deleteTemplate(templateId) {
  if (!ProjectState.tenantId) return;
  if (!confirm('Delete this template?')) return;
  try {
    await deleteDoc(doc(db, 'tenants', ProjectState.tenantId, 'projectTemplates', templateId));
    window.showToast?.('Template deleted', 'success');
    await loadTemplates();
    renderProjectsView();
  } catch (err) {
    console.error('[Projects] Error deleting template:', err);
  }
}

// TASK-9391 Slice 0 — the calendar days one template job occupies, as NOON-LOCAL
// Dates (memory/lesson_jobday_date_anchors_to_noon_local: a midnight-local jobDay
// renders on no board at all in western timezones).
//
// `durationDays` is a COUNT of days, not an offset: the template editor labels it
// "— Nd" and defaults it to 1, and a 1-day job is one date. The old writer did
// `end = start + durationDays`, which made every "1-day" template job span two
// dates. Missing / 0 / garbage reads as 1 (a job is at least one day); capped so a
// typo like 3000 cannot try to write a 3000-row batch.
const TEMPLATE_JOB_MAX_DAYS = 366;
function templateJobDayDates(projectStart, offsetDays, durationDays) {
  const base = (projectStart instanceof Date && !isNaN(projectStart.getTime())) ? projectStart : new Date();
  const off = Math.trunc(Number(offsetDays) || 0);
  let n = Math.trunc(Number(durationDays) || 0);
  if (!(n >= 1)) n = 1;
  if (n > TEMPLATE_JOB_MAX_DAYS) n = TEMPLATE_JOB_MAX_DAYS;
  const out = [];
  // Built from Y/M/D components (never by adding 24h of milliseconds), so a DST
  // switch inside the range cannot walk the anchor off noon.
  for (let i = 0; i < n; i++) {
    out.push(new Date(base.getFullYear(), base.getMonth(), base.getDate() + off + i, 12, 0, 0, 0));
  }
  return out;
}

// TASK-9391 Slice 0 — the jobDay rows for one template job. Same shape saveJob's
// create branch writes for a job with no crew yet (status 'scheduled', empty crew
// arrays and side maps), so the driver board, Assign Crew, drag/drop and Remove
// Day treat these like any other job. Pure: no Firestore reads or writes here.
function buildTemplateJobDayRows(jobId, dates) {
  return (dates || []).map(d => ({
    jobId,
    date: Timestamp.fromDate(d),
    status: 'scheduled',
    drivers: [],
    helpers: [],
    trucks: [],
    trailers: [],
    assets: [],
    assetQtys: {},
    assetDirections: {},
    createdAt: Timestamp.now()
  }));
}

async function createProjectFromTemplate(templateId, overrides = {}) {
  const template = ProjectState.templates.find(t => t.id === templateId);
  if (!template) { window.showToast?.('Template not found', 'error'); return null; }

  // TASK-9391 — the create form's PO, revenue, description, tags and New-job
  // defaults used to be DROPPED on this path (only name/customer/dates/location
  // were forwarded), so a project made from a template silently lost its
  // standing PO — and so did every job the template then created.
  const projectId = await createProject({
    name: overrides.name || template.name,
    description: overrides.description || template.description,
    tags: (overrides.tags && overrides.tags.length) ? overrides.tags : (template.defaultTags || []),
    templateId: templateId,
    customerIds: overrides.customerIds || [],
    primaryCustomerId: overrides.primaryCustomerId || null,
    primaryCustomerName: overrides.primaryCustomerName || '',
    startDate: overrides.startDate || Timestamp.now(),
    endDate: overrides.endDate || null,
    location: overrides.location || '',
    poNumber: overrides.poNumber || '',
    estimatedRevenue: overrides.estimatedRevenue || null,
    onSiteContacts: overrides.onSiteContacts || [],
    newJobDefaults: overrides.newJobDefaults
  });

  if (!projectId) return null;

  const projectPo = poNormalizeForSave(overrides.poNumber);
  let created = 0;
  let failed = 0;

  // Create default jobs from template
  if (template.defaultJobs?.length > 0) {
    const startDate = overrides.startDate?.toDate ? overrides.startDate.toDate() : new Date();
    for (const jobDef of template.defaultJobs) {
      const dates = templateJobDayDates(startDate, jobDef.offsetDays, jobDef.durationDays);
      const jobStart = dates[0];
      const jobEnd = dates[dates.length - 1];

      try {
        // TASK-4781 slice 2 — allocate the human job number BEFORE the write.
        // allocateJobNumberSafe never throws: on a failure it returns
        // { jobNumberPending: true } so the hole is QUERYABLE rather than invisible,
        // mirroring ticketNoPending. window.JobOps per Pitfall #1 — never a
        // cross-import with a ?v= buster.
        const _jn = await window.JobOps.allocateJobNumberSafe(ProjectState.tenantId);
        // 🔴 TASK-9391 Slice 0 — the job AND its jobDays in ONE batch. The old
        // writer created the job doc only: Dispatch showed it through the
        // `legacy-` fallback (no drag, no Remove Day) and the DRIVER board never
        // showed it at all, because jobboard.js getJobsForDate iterates jobDays
        // and has no legacy fallback. All-or-nothing, so a failure can never
        // leave a job that exists but is invisible to drivers.
        const jobRef = doc(collection(db, 'tenants', ProjectState.tenantId, 'jobs'));
        const batch = writeBatch(db);
        batch.set(jobRef, {
          ...(_jn.jobNumberPending ? { jobNumberPending: true } : { jobNumber: _jn.jobNumber }),
          title: jobDef.title || 'Untitled',
          type: jobDef.type || 'job',
          customerId: overrides.primaryCustomerId || null,
          customer: overrides.primaryCustomerName || '',
          projectId: projectId,
          // The standing PO rides onto every job the template creates, the same
          // way linkJobToProject stamps it (poVisible alongside).
          ...(projectPo ? { poNumber: projectPo, poVisible: true } : {}),
          startDate: Timestamp.fromDate(jobStart),
          endDate: Timestamp.fromDate(jobEnd),
          allDay: true,
          status: 'active',
          notes: jobDef.notes || '',
          waypoints: [],
          createdAt: Timestamp.now(),
          // TASK-1333 — REQUIRED by the jobs create rule (must equal request.auth.uid).
          // The person instantiating the template is the creator of these jobs.
          createdBy: auth.currentUser?.uid || window.AppState?.user?.uid || null,
          createdByName: window.AppState?.user?.displayName || window.AppState?.user?.email || 'Unknown',
          updatedAt: Timestamp.now()
        });
        for (const row of buildTemplateJobDayRows(jobRef.id, dates)) {
          batch.set(doc(collection(db, 'tenants', ProjectState.tenantId, 'jobDays')), row);
        }
        await batch.commit();
        created++;
        // Jobs audit to jobAuditLog, not the general auditLog (tech-support runbook).
        try {
          await addDoc(collection(db, 'tenants', ProjectState.tenantId, 'jobAuditLog'), {
            action: 'JOB_CREATE', source: 'project-template', timestamp: Timestamp.now(),
            user: window.AppState?.user?.email || 'unknown',
            jobId: jobRef.id, jobTitle: jobDef.title || 'Untitled',
            customerId: overrides.primaryCustomerId || null,
            customerName: overrides.primaryCustomerName || null,
            projectId, templateId, dayCount: dates.length
          });
        } catch (ae) { console.warn('[Projects] Template job audit log failed:', ae); }
      } catch (jobErr) {
        failed++;
        console.error('[Projects] Error creating template job:', jobErr);
        window.logError?.(jobErr, { module: 'projects', operation: 'TEMPLATE_JOB_CREATE', projectId, templateId });
      }
    }
  }

  if (failed > 0) {
    window.showToast?.(`Project created, but only ${created} of ${created + failed} template jobs were added. Add the rest with the project's New Job button.`, 'error', null, 9000);
  } else {
    window.showToast?.(`Project created from template with ${created} job${created === 1 ? '' : 's'}`, 'success');
  }
  return projectId;
}

async function duplicateProject(projectId) {
  const project = ProjectState.projects.find(p => p.id === projectId);
  if (!project) return;

  const newName = prompt('Name for the new project:', project.name + ' (Copy)');
  if (!newName) return;
  // TASK-9420 — checked after the prompt returns (soft warning, never a block).
  if (!(await confirmProjectNameNotDuplicate(newName, { customerId: project.primaryCustomerId || '' }))) return;

  const newId = await createProject({
    name: newName,
    description: project.description,
    customerIds: project.customerIds || [],
    primaryCustomerId: project.primaryCustomerId,
    primaryCustomerName: project.primaryCustomerName,
    tags: project.tags || [],
    location: project.location,
    locationLat: project.locationLat,
    locationLng: project.locationLng,
    onSiteContacts: project.onSiteContacts || [],
    estimatedRevenue: project.estimatedRevenue,
    // TASK-9391 — the copy keeps the folder's New job defaults (flags only).
    newJobDefaults: project.newJobDefaults || null
  });

  if (newId) {
    window.showToast?.('Project duplicated — no jobs linked yet', 'success');
    await openProjectDetail(newId);
  }
}

// ============================================
// SAVED FILTERS
// ============================================
function getSavedFilters() {
  try {
    const raw = localStorage.getItem(`dd_project_filters_${ProjectState.tenantId}`);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveFilter(name) {
  const filters = getSavedFilters();
  filters.push({
    name,
    statusFilter: ProjectState.statusFilter,
    customerFilter: ProjectState.customerFilter,
    tagFilter: ProjectState.tagFilter,
    searchQuery: ProjectState.searchQuery,
    savedAt: new Date().toISOString()
  });
  localStorage.setItem(`dd_project_filters_${ProjectState.tenantId}`, JSON.stringify(filters));
  ProjectState.savedFilters = filters;
  window.showToast?.(`Filter "${name}" saved`, 'success');
}

function applySavedFilter(index) {
  const filters = getSavedFilters();
  const f = filters[index];
  if (!f) return;
  ProjectState.statusFilter = window.ProjectStatusFilter.resolve(f.statusFilter);
  ProjectState.customerFilter = f.customerFilter || '';
  ProjectState.tagFilter = f.tagFilter || '';
  ProjectState.searchQuery = f.searchQuery || '';
  renderProjectsView();
}

function deleteSavedFilter(index) {
  const filters = getSavedFilters();
  filters.splice(index, 1);
  localStorage.setItem(`dd_project_filters_${ProjectState.tenantId}`, JSON.stringify(filters));
  ProjectState.savedFilters = filters;
  renderProjectsView();
}

// ============================================
// FILTERING & SORTING
// ============================================
// TASK-9700 — the status chips use the ONE shared definition AR/AP uses too
// (window.ProjectStatusFilter, utils/projectStatusFilter.js): same words, same
// order, same default, and a missing status counts as Active in both views.
function getFilteredProjects() {
  const PSF = window.ProjectStatusFilter;
  return PSF.filterBy(getNarrowedProjects(), PSF.resolve(ProjectState.statusFilter));
}

// Same chips, same order, same counts as AR/AP -> Projects (shared module).
function renderStatusChips() {
  const PSF = window.ProjectStatusFilter;
  const mode = PSF.resolve(ProjectState.statusFilter);
  const narrowed = getNarrowedProjects();
  return PSF.CHIPS.map(c => {
    const on = c.key === mode;
    return `<button type="button" class="search-filter-pill ${on ? 'active' : ''}" data-status-chip="${c.key}" aria-pressed="${on ? 'true' : 'false'}" title="${escAttr(c.tip)}" onclick="window.ProjectsModule.setStatusFilter('${c.key}')">${escHtml(c.label)} <span class="search-filter-pill-count" style="opacity:0.75;font-weight:500;">${PSF.countFor(narrowed, c.key)}</span></button>`;
  }).join('');
}

// Customer + tag + search, NOT status — the chip counts are measured on this
// so a count never promises cards the next click won't show.
function getNarrowedProjects() {
  let list = [...ProjectState.projects];

  // Customer filter
  if (ProjectState.customerFilter) {
    list = list.filter(p =>
      p.primaryCustomerId === ProjectState.customerFilter ||
      (p.customerIds || []).includes(ProjectState.customerFilter)
    );
  }

  // Tag filter
  if (ProjectState.tagFilter) {
    list = list.filter(p => (p.tags || []).includes(ProjectState.tagFilter));
  }

  // Search
  if (ProjectState.searchQuery) {
    const q = ProjectState.searchQuery.trim().toLowerCase();
    list = list.filter(p => {
      const searchable = [
        p.name,
        p.description,
        p.location,
        p.primaryCustomerName,
        ...(p.tags || []),
        ...(p.onSiteContacts || []).map(c => c.name)
      ].filter(Boolean).join(' ').toLowerCase();
      return searchable.includes(q);
    });
  }

  // Sort
  list.sort((a, b) => {
    let va, vb;
    switch (ProjectState.sortBy) {
      case 'name': va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase(); break;
      case 'startDate': va = a.startDate?.seconds || 0; vb = b.startDate?.seconds || 0; break;
      case 'endDate': va = a.endDate?.seconds || 0; vb = b.endDate?.seconds || 0; break;
      case 'customer': va = (a.primaryCustomerName || '').toLowerCase(); vb = (b.primaryCustomerName || '').toLowerCase(); break;
      default: va = a.createdAt?.seconds || 0; vb = b.createdAt?.seconds || 0;
    }
    if (va < vb) return ProjectState.sortDir === 'asc' ? -1 : 1;
    if (va > vb) return ProjectState.sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  return list;
}

function getAllTags() {
  const tags = new Set();
  ProjectState.projects.forEach(p => (p.tags || []).forEach(t => tags.add(t)));
  return [...tags].sort();
}

function getAllProjectCustomerIds() {
  const ids = new Set();
  ProjectState.projects.forEach(p => {
    if (p.primaryCustomerId) ids.add(p.primaryCustomerId);
    (p.customerIds || []).forEach(id => ids.add(id));
  });
  return [...ids];
}

// ============================================
// GROUP BY CUSTOMER (cards mode)
// ============================================
const GROUP_PREF_KEY = 'dd_projects_group_by_customer';
const NO_CUSTOMER_KEY = '__none__'; // MUST equal window.ProjectStatusFilter.NO_CUSTOMER_KEY (tests/task9700 pins it)

function loadGroupPrefs() {
  ProjectState.groupByCustomer = true;
  try {
    if (localStorage.getItem(GROUP_PREF_KEY) === '0') ProjectState.groupByCustomer = false;
  } catch { /* storage blocked — keep the default */ }
}

// Key on the customer id when there is one, so a stale primaryCustomerName on
// one project can't split a customer into two groups; fall back to the
// lower-cased name for id-less legacy projects.
// TASK-9700 — shared with AR/AP (window.ProjectStatusFilter) so both lists put a
// project under the same customer header with the same name.
function customerGroupKey(p) {
  return window.ProjectStatusFilter.customerGroupKey(p);
}

// `list` is already filtered + sorted; Map insertion order keeps the current
// sort inside each group. Groups themselves go A–Z, "No customer" last.
function groupProjectsByCustomer(list) {
  const groups = new Map();
  list.forEach(p => {
    const key = customerGroupKey(p);
    if (!groups.has(key)) groups.set(key, { key, label: '', projects: [] });
    groups.get(key).projects.push(p);
  });
  // TASK-9700 — the header is resolved from the WHOLE group (shared with AR/AP),
  // never from whichever row sorted first, so both views print the same header.
  const nameById = id => {
    const c = (window.AppState?.customers || []).find(x => x.id === id);
    return c?.name || c?.company || '';
  };
  for (const g of groups.values()) g.label = window.ProjectStatusFilter.customerGroupLabelFor(g.projects, nameById);
  // TASK-9605 — inside each group: running/upcoming -> ran long -> wrapped ->
  // completed. Stable, so the chosen sort still orders each tier.
  const nowD = new Date();
  const tz = tenantTimeZone();
  for (const g of groups.values()) {
    const rank = new Map(g.projects.map(p => [p.id, projectRunRank(p, nowD, tz)]));
    g.projects.sort((a, b) => rank.get(a.id) - rank.get(b.id));
  }
  return [...groups.values()].sort((a, b) => window.ProjectStatusFilter.compareGroups(a, b));
}

// TASK-9552 (Chris 2026-10-07: "they are all stacked on top of each other") —
// By Customer is an ORDER, not a stack of collapsible sections. Every card
// sits in the ONE #projects-grid, ordered customer by customer (same group
// order + in-group run rank as AR/AP via window.ProjectStatusFilter), and each
// card names its customer. A 1-project customer no longer owns a whole row:
// the grid packs the cards edge to edge, so the only short row is the last.
// The All Customers filter does the narrowing the old collapse did.
function renderCustomerSortedCards(list) {
  return groupProjectsByCustomer(list)
    .map(g => g.projects.map(p => renderProjectCard(p, { groupKey: g.key, groupLabel: g.label })).join(''))
    .join('');
}

function setGroupByCustomer(on) {
  ProjectState.groupByCustomer = !!on;
  try { localStorage.setItem(GROUP_PREF_KEY, on ? '1' : '0'); } catch { /* blocked */ }
  renderProjectsView();
}

// ============================================
// RENDER: PROJECT LIST VIEW
// ============================================
async function renderProjectsView() {
  const content = document.getElementById('dispatch-content');
  if (!content) return;

  // If data hasn't loaded yet, show loading and re-render when ready
  if (ProjectState.loading) {
    content.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;padding:60px;"><div class="loading-spinner"></div><span style="margin-left:12px;color:var(--text-secondary);">Loading projects...</span></div>';
    // Wait for loadProjects to finish, then re-render
    const checkLoaded = setInterval(() => {
      if (!ProjectState.loading) {
        clearInterval(checkLoaded);
        renderProjectsView();
      }
    }, 100);
    return;
  }

  if (ProjectState.viewMode === 'detail' && ProjectState.currentProject) {
    renderProjectDetail(content);
    return;
  }

  const filtered = getFilteredProjects();
  const customers = ProjectState.customers || [];
  const allTags = getAllTags();
  const savedFilters = getSavedFilters();
  ProjectState.savedFilters = savedFilters;

  // Get unique customers used in projects
  const projectCustomerIds = getAllProjectCustomerIds();
  const projectCustomers = customers.filter(c => projectCustomerIds.includes(c.id));

  content.innerHTML = `
    <div class="projects-container">
      <!-- Header -->
      <div class="projects-header">
        <div class="projects-header-left">
          <h2 style="margin:0;font-size:1.25rem;font-weight:700;color:var(--text-primary);">Projects</h2>
          <span style="color:var(--text-secondary);font-size:0.85rem;">${filtered.length} of ${ProjectState.projects.length}</span>
        </div>
        <div class="projects-header-actions">
          <div class="projects-view-toggle" style="display:flex;gap:2px;background:var(--bg-secondary);border-radius:8px;padding:2px;">
            <button class="btn btn-sm ${ProjectState.listMode === 'cards' ? 'btn-primary' : 'btn-secondary'}" onclick="window.ProjectsModule.setListMode('cards')" title="Card Grid" style="border-radius:6px;padding:4px 10px;">
              <span class="material-icons" style="font-size:18px;">grid_view</span>
            </button>
            <button class="btn btn-sm ${ProjectState.listMode === 'calendar' ? 'btn-primary' : 'btn-secondary'}" onclick="window.ProjectsModule.setListMode('calendar')" title="Calendar View" style="border-radius:6px;padding:4px 10px;">
              <span class="material-icons" style="font-size:18px;">calendar_month</span>
            </button>
          </div>
          ${ProjectState.listMode === 'cards' ? `
            <button type="button" id="projects-group-toggle" class="btn btn-sm ${ProjectState.groupByCustomer ? 'btn-primary' : 'btn-secondary'}"
                    onclick="window.ProjectsModule.setGroupByCustomer(${ProjectState.groupByCustomer ? 'false' : 'true'})"
                    aria-pressed="${ProjectState.groupByCustomer ? 'true' : 'false'}"
                    title="${ProjectState.groupByCustomer ? 'Grouped by customer. Click for a flat list.' : 'Group projects by customer'}">
              <span class="material-icons" style="font-size:18px;">workspaces</span> By Customer
            </button>
          ` : ''}
          <button class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.showCreateProject()" title="New Project">
            <span class="material-icons" style="font-size:18px;">add</span> New Project
          </button>
          ${ProjectState.templates.length > 0 ? `
            <button class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.showTemplateMenu()" title="From Template">
              <span class="material-icons" style="font-size:18px;">content_copy</span> From Template
            </button>
          ` : ''}
          <button class="btn btn-secondary btn-sm btn-neon-settings btn-module-header" onclick="window.ProjectsModule.showManageTemplates()" title="Manage Templates">
            <span class="material-icons" style="font-size:18px;">settings</span>
          </button>
        </div>
      </div>

      <!-- Filters Bar -->
      <div class="projects-filters">
        <div class="projects-search">
          <input type="search" id="project-search-input" placeholder="Search projects, locations, customers..."
                 value="${escAttr(ProjectState.searchQuery)}"
                 oninput="window.ProjectsModule.onSearch(this.value)" autocomplete="off">
        </div>

        <div class="projects-filter-group">
          ${renderStatusChips()}
        </div>

        <div class="projects-select-group">
          ${projectCustomers.length > 0 ? `
            <select id="project-customer-filter" onchange="window.ProjectsModule.setCustomerFilter(this.value)">
              <option value="">All Customers</option>
              ${projectCustomers.map(c => `<option value="${c.id}" ${ProjectState.customerFilter === c.id ? 'selected' : ''}>${escHtml(c.name || c.company)}</option>`).join('')}
            </select>
          ` : ''}
          ${allTags.length > 0 ? `
            <select id="project-tag-filter" onchange="window.ProjectsModule.setTagFilter(this.value)">
              <option value="">All Tags</option>
              ${allTags.map(t => `<option value="${t}" ${ProjectState.tagFilter === t ? 'selected' : ''}>${escHtml(t)}</option>`).join('')}
            </select>
          ` : ''}
        </div>

        <!-- Saved Filters -->
        ${savedFilters.length > 0 ? `
          <div class="projects-saved-filters">
            ${savedFilters.map((f, i) => `
              <button class="saved-filter-chip" onclick="window.ProjectsModule.applySavedFilter(${i})" title="Apply filter: ${escAttr(f.name)}">
                <span class="material-icons" style="font-size:14px;">bookmark</span> ${escHtml(f.name)}
                <span class="saved-filter-delete" onclick="event.stopPropagation();window.ProjectsModule.deleteSavedFilter(${i})">×</span>
              </button>
            `).join('')}
          </div>
        ` : ''}
        <button class="btn-text-sm" onclick="window.ProjectsModule.promptSaveFilter()" style="font-size:0.75rem;color:var(--brand-blue);cursor:pointer;background:none;border:none;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">bookmark_add</span> Save Filter
        </button>
      </div>

      <!-- Project Cards / Calendar -->
      ${ProjectState.listMode === 'calendar' ? renderProjectsCalendar(filtered) : `
      <div class="projects-grid" id="projects-grid"${ProjectState.groupByCustomer && filtered.length > 0 ? ' data-grouped="1"' : ''}>
        ${filtered.length === 0 ? `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:60px 20px;grid-column:1/-1;">
            <div style="width:80px;height:80px;border-radius:50%;background:var(--bg-secondary);display:flex;align-items:center;justify-content:center;margin-bottom:16px;">
              <span class="material-icons" style="font-size:40px;color:var(--text-secondary);">folder_open</span>
            </div>
            <div style="font-size:1.1rem;font-weight:600;color:var(--text-primary);margin-bottom:6px;">No projects found</div>
            <div style="font-size:0.9rem;color:var(--text-secondary);margin-bottom:20px;">
              ${ProjectState.searchQuery || ProjectState.customerFilter || ProjectState.tagFilter
                ? 'No projects match your current filters'
                : 'Create your first project to start grouping jobs'}
            </div>
            ${ProjectState.searchQuery || ProjectState.customerFilter || ProjectState.tagFilter ? `
              <button class="btn btn-secondary" onclick="window.ProjectsModule.clearAllFilters()" style="margin-bottom:8px;">
                <span class="material-icons" style="font-size:16px;">filter_alt_off</span> Clear Filters
              </button>
            ` : `
              <button class="btn btn-primary" onclick="window.ProjectsModule.showCreateProject()">
                <span class="material-icons">add</span> Create Project
              </button>
            `}
          </div>
        ` : ProjectState.groupByCustomer
          ? renderCustomerSortedCards(filtered)
          : filtered.map(p => renderProjectCard(p)).join('')}
      </div>`}
    </div>
  `;
}

function setListMode(mode) {
  ProjectState.listMode = mode;
  if (mode === 'calendar' && !ProjectState.calendarStart) {
    // Default to Monday of current week
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    ProjectState.calendarStart = new Date(now.getFullYear(), now.getMonth(), diff);
  }
  renderProjectsView();
}

function projectCalendarNav(dir) {
  if (!ProjectState.calendarStart) return;
  const d = new Date(ProjectState.calendarStart);
  if (ProjectState.calendarMode === 'month') {
    d.setMonth(d.getMonth() + dir);
    d.setDate(1);
  } else {
    d.setDate(d.getDate() + dir * 7);
  }
  ProjectState.calendarStart = d;
  renderProjectsView();
}

function projectCalendarToday() {
  const now = new Date();
  if (ProjectState.calendarMode === 'month') {
    ProjectState.calendarStart = new Date(now.getFullYear(), now.getMonth(), 1);
  } else {
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    ProjectState.calendarStart = new Date(now.getFullYear(), now.getMonth(), diff);
  }
  renderProjectsView();
}

function setCalendarMode(mode) {
  ProjectState.calendarMode = mode;
  const now = new Date();
  if (mode === 'month') {
    ProjectState.calendarStart = new Date(now.getFullYear(), now.getMonth(), 1);
  } else {
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    ProjectState.calendarStart = new Date(now.getFullYear(), now.getMonth(), diff);
  }
  renderProjectsView();
}

// Distinct project colors — avoids the "sea of blue" problem
const PROJECT_COLORS = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#EF4444', // red
  '#14B8A6', // teal
  '#F97316', // orange
  '#6366F1', // indigo
];

function getProjectColor(projectId, allProjects) {
  const idx = allProjects.findIndex(p => p.id === projectId);
  return PROJECT_COLORS[idx % PROJECT_COLORS.length];
}

function renderProjectsCalendar(projects) {
  const mode = ProjectState.calendarMode || 'week';

  if (!ProjectState.calendarStart) {
    const now = new Date();
    if (mode === 'month') {
      ProjectState.calendarStart = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      ProjectState.calendarStart = new Date(now.getFullYear(), now.getMonth(), diff);
    }
  }

  const todayStr = new Date().toISOString().split('T')[0];
  const statusColors = {
    active: 'var(--brand-blue)',
    completed: '#10B981',
    archived: '#6B7280'
  };

  // Calendar nav header (shared)
  const navLabel = mode === 'month'
    ? ProjectState.calendarStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : (() => {
        const s = new Date(ProjectState.calendarStart);
        const e = new Date(s); e.setDate(e.getDate() + 6);
        return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      })();

  const navHtml = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:16px;flex-wrap:wrap;">
      <div style="display:flex;gap:2px;background:var(--bg-secondary);border-radius:8px;padding:2px;">
        <button class="btn btn-sm ${mode === 'week' ? 'btn-primary' : 'btn-secondary'}" onclick="window.ProjectsModule.setCalendarMode('week')" style="border-radius:6px;padding:4px 12px;font-size:0.8rem;">Week</button>
        <button class="btn btn-sm ${mode === 'month' ? 'btn-primary' : 'btn-secondary'}" onclick="window.ProjectsModule.setCalendarMode('month')" style="border-radius:6px;padding:4px 12px;font-size:0.8rem;">Month</button>
      </div>
      <button class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.projectCalendarNav(-1)" style="padding:4px 8px;">
        <span class="material-icons" style="font-size:18px;">chevron_left</span>
      </button>
      <button class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.projectCalendarToday()" style="padding:4px 10px;font-size:0.8rem;">Today</button>
      <button class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.projectCalendarNav(1)" style="padding:4px 8px;">
        <span class="material-icons" style="font-size:18px;">chevron_right</span>
      </button>
      <span style="font-weight:600;font-size:0.95rem;color:var(--text-primary);">${navLabel}</span>
    </div>
  `;

  if (mode === 'month') {
    return renderProjectsMonthCalendar(projects, navHtml, todayStr, statusColors);
  }

  // Week view
  const startOfWeek = new Date(ProjectState.calendarStart);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startOfWeek);
    d.setDate(d.getDate() + i);
    days.push(d);
  }

  const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  const weekStart = days[0].getTime();
  const weekEnd = days[6].getTime() + 86400000;

  const weekProjects = projects.filter(p => {
    const pStart = p.startDate?.toDate ? p.startDate.toDate().getTime() : (p.startDate?.seconds ? p.startDate.seconds * 1000 : 0);
    const pEnd = p.endDate?.toDate ? p.endDate.toDate().getTime() : (p.endDate?.seconds ? p.endDate.seconds * 1000 : Date.now() + 86400000 * 365);
    return pStart < weekEnd && pEnd >= weekStart;
  });
  weekProjects.sort((a, b) => (a.startDate?.seconds || 0) - (b.startDate?.seconds || 0));

  return `
    <div class="projects-calendar">
      ${navHtml}
      <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:12px;">${weekProjects.length} project${weekProjects.length !== 1 ? 's' : ''} this week</div>

      <div class="projects-cal-grid" style="border:1px solid var(--border-light);border-radius:10px;overflow:hidden;">
        <div class="projects-cal-header" style="background:var(--bg-secondary);">
          <div class="projects-cal-label-col"></div>
          ${days.map((d, i) => {
            const dateStr = d.toISOString().split('T')[0];
            const isToday = dateStr === todayStr;
            return `<div class="projects-cal-day-header" style="text-align:center;padding:8px 4px;${isToday ? 'background:rgba(59,130,246,0.06);' : ''}">
              <div style="font-size:0.65rem;font-weight:600;color:var(--text-secondary);letter-spacing:0.5px;">${dayNames[i]}</div>
              <div style="font-size:0.95rem;font-weight:700;${isToday ? 'color:var(--brand-blue);background:var(--brand-blue);color:#fff;width:28px;height:28px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;' : 'color:var(--text-primary);'}">${d.getDate()}</div>
            </div>`;
          }).join('')}
        </div>

        <div class="projects-cal-body">
          ${weekProjects.length === 0 ? `
            <div style="text-align:center;padding:48px 20px;color:var(--text-secondary);grid-column:1/-1;">
              <span class="material-icons" style="font-size:36px;display:block;margin-bottom:8px;opacity:0.5;">event_busy</span>
              No projects this week
            </div>
          ` : weekProjects.map(p => {
            const pStartTs = p.startDate?.toDate ? p.startDate.toDate().getTime() : (p.startDate?.seconds ? p.startDate.seconds * 1000 : weekStart);
            const pEndTs = p.endDate?.toDate ? p.endDate.toDate().getTime() : (p.endDate?.seconds ? p.endDate.seconds * 1000 : weekEnd);
            const color = p.status === 'completed' ? '#6B7280' : p.status === 'archived' ? '#9CA3AF' : getProjectColor(p.id, projects);
            const customerName = p.primaryCustomerName || '';
            const barStart = Math.max(0, Math.floor((pStartTs - weekStart) / 86400000));
            const barEnd = Math.min(6, Math.floor((pEndTs - weekStart) / 86400000));
            const spanStart = Math.max(barStart, 0);
            const spanEnd = Math.min(barEnd, 6);

            return `
              <div class="projects-cal-row" style="border-bottom:1px solid var(--border-light);">
                <div class="projects-cal-label" onclick="window.ProjectsModule.openProjectDetail('${p.id}')" style="cursor:pointer;padding:8px 10px;" title="${escAttr(p.name)}">
                  <div style="font-weight:600;font-size:0.8rem;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHtml(p.name)}</div>
                  ${customerName ? `<div style="font-size:0.7rem;color:var(--text-secondary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHtml(customerName)}</div>` : ''}
                </div>
                ${days.map((d, i) => {
                  const dateStr = d.toISOString().split('T')[0];
                  const isToday = dateStr === todayStr;
                  const inRange = i >= spanStart && i <= spanEnd;
                  const isStart = i === spanStart;
                  const isEnd = i === spanEnd;
                  const barSpan = spanEnd - spanStart + 1;
                  const showNameInBar = isStart && barSpan >= 3;
                  return `<div class="projects-cal-cell ${isToday ? 'today' : ''}" data-date="${dateStr}"
                    onclick="window.ProjectsModule.openProjectDetail('${p.id}')" style="cursor:pointer;${isToday ? 'background:rgba(59,130,246,0.03);' : ''}">
                    ${inRange ? `<div class="projects-cal-bar" style="
                      background:${color};opacity:0.85;position:relative;overflow:hidden;
                      ${isStart ? 'border-radius:6px 0 0 6px;margin-left:4px;' : ''}
                      ${isEnd ? 'border-radius:0 6px 6px 0;margin-right:4px;' : ''}
                      ${isStart && isEnd ? 'border-radius:6px;margin:0 4px;' : ''}
                    ">${showNameInBar ? `<span style="position:absolute;left:6px;top:50%;transform:translateY(-50%);font-size:0.6rem;font-weight:600;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:calc(${barSpan * 100}% - 12px);pointer-events:none;">${escHtml(p.name)}</span>` : ''}</div>` : ''}
                  </div>`;
                }).join('')}
              </div>
            `;
          }).join('')}
        </div>
      </div>
    </div>
  `;
}

function renderProjectsMonthCalendar(projects, navHtml, todayStr, statusColors) {
  const year = ProjectState.calendarStart.getFullYear();
  const month = ProjectState.calendarStart.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Build 6-week grid starting from Monday
  let gridStart = new Date(firstDay);
  const dow = gridStart.getDay();
  gridStart.setDate(gridStart.getDate() - (dow === 0 ? 6 : dow - 1));

  const weeks = [];
  const current = new Date(gridStart);
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    weeks.push(week);
    // Stop if we've passed the month
    if (current.getMonth() !== month && current.getDate() > 7) break;
  }

  const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  const monthStart = firstDay.getTime();
  const monthEnd = lastDay.getTime() + 86400000;

  const monthProjects = projects.filter(p => {
    const pStart = p.startDate?.toDate ? p.startDate.toDate().getTime() : (p.startDate?.seconds ? p.startDate.seconds * 1000 : 0);
    const pEnd = p.endDate?.toDate ? p.endDate.toDate().getTime() : (p.endDate?.seconds ? p.endDate.seconds * 1000 : Date.now() + 86400000 * 365);
    return pStart < monthEnd && pEnd >= monthStart;
  });

  return `
    <div class="projects-calendar">
      ${navHtml}
      <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:12px;">${monthProjects.length} project${monthProjects.length !== 1 ? 's' : ''} this month</div>

      <div style="border:1px solid var(--border-light);border-radius:10px;overflow:hidden;min-width:600px;">
        <!-- Day headers -->
        <div style="display:grid;grid-template-columns:repeat(7,1fr);background:var(--bg-secondary);border-bottom:1px solid var(--border-light);">
          ${dayNames.map(n => `<div style="text-align:center;padding:8px 4px;font-size:0.7rem;font-weight:600;color:var(--text-secondary);letter-spacing:0.5px;">${n}</div>`).join('')}
        </div>

        <!-- Weeks -->
        ${weeks.map(week => `
          <div style="display:grid;grid-template-columns:repeat(7,1fr);border-bottom:1px solid var(--border-light);min-height:80px;">
            ${week.map(d => {
              const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              const isToday = dateStr === todayStr;
              const isCurrentMonth = d.getMonth() === month;
              const dayTs = d.getTime();

              // Find projects that include this day
              const dayProjects = monthProjects.filter(p => {
                const pStart = p.startDate?.toDate ? p.startDate.toDate().getTime() : (p.startDate?.seconds ? p.startDate.seconds * 1000 : 0);
                const pEnd = p.endDate?.toDate ? p.endDate.toDate().getTime() : (p.endDate?.seconds ? p.endDate.seconds * 1000 : Date.now() + 86400000 * 365);
                return dayTs >= pStart && dayTs <= pEnd;
              });

              return `<div style="padding:4px;border-right:1px solid var(--border-light);${isToday ? 'background:rgba(59,130,246,0.04);' : ''}${!isCurrentMonth ? 'opacity:0.35;' : ''}">
                <div style="font-size:0.75rem;font-weight:600;text-align:right;padding:2px 4px;${isToday ? 'color:#fff;background:var(--brand-blue);border-radius:50%;width:22px;height:22px;display:inline-flex;align-items:center;justify-content:center;float:right;' : `color:${isCurrentMonth ? 'var(--text-primary)' : 'var(--text-secondary)'};`}">${d.getDate()}</div>
                <div style="clear:both;display:flex;flex-direction:column;gap:2px;margin-top:2px;">
                  ${dayProjects.slice(0, 3).map(p => {
                    const color = p.status === 'completed' ? '#6B7280' : p.status === 'archived' ? '#9CA3AF' : getProjectColor(p.id, projects);
                    return `<div onclick="window.ProjectsModule.openProjectDetail('${p.id}')" style="cursor:pointer;font-size:0.65rem;font-weight:500;color:#fff;background:${color};border-radius:4px;padding:2px 5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" title="${escAttr(p.name)}">${escHtml(p.name)}</div>`;
                  }).join('')}
                  ${dayProjects.length > 3 ? `<div style="font-size:0.6rem;color:var(--text-secondary);padding-left:4px;">+${dayProjects.length - 3} more</div>` : ''}
                </div>
              </div>`;
            }).join('')}
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

// ============================================
// TASK-9392 — customer job requests on the project folder
// ============================================
// The data is dispatch.js's live customerRequestDrafts listener (the Dispatch
// Board strip's), read through window.DispatchModule — never an import
// (Pitfall #1). It returns nothing when the tenant has intake off or the viewer
// cannot read drafts (rules deny → empty list), so neither the pill nor the
// section ever shows for them. Customer text is untrusted: every value escaped.
function _projectCustomerRequestDrafts(projectId) {
  try { return window.DispatchModule?.getCustomerRequestDraftsForProject?.(projectId) || []; } catch (_) { return []; }
}

function renderProjectJobRequestPill(projectId) {
  let n = 0;
  try { n = window.DispatchModule?.getCustomerRequestCounts?.()?.byProject?.[projectId] || 0; } catch (_) { n = 0; }
  if (!n) return '';
  return `
      <div title="Customer job requests waiting to be added to dispatch" style="display:inline-flex;align-items:center;gap:4px;background:#FEE2E2;color:#991B1B;border:1px solid #FECACA;border-radius:10px;padding:2px 8px;font-size:0.7rem;font-weight:600;margin-bottom:4px;">
        <span class="material-icons" style="font-size:12px;">inbox</span>
        ${n} job request${n !== 1 ? 's' : ''} to dispatch
      </div>`;
}

function _fmtRequestAge(ts) {
  const ms = ts?.toMillis?.();
  if (!ms) return '';
  const mins = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 48 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
}

function renderProjectCustomerRequests(projectId) {
  const drafts = _projectCustomerRequestDrafts(projectId);
  if (!drafts.length) return '';
  let canApprove = false;
  try { canApprove = !!window.DispatchModule?.customerRequestCanApprove?.(); } catch (_) { canApprove = false; }
  const note = window.DispatchModule?.customerRequestApproverNote || '';
  const rows = drafts.map(d => {
    const f = d.fields || {};
    const id = escAttr(String(d.id || ''));
    const po = f.poNumber ? `PO ${escHtml(String(f.poNumber))}` : (f.poDeclined ? 'No PO (customer said none)' : 'No PO');
    const items = Array.isArray(d.requestedItems) && d.requestedItems.length
      ? d.requestedItems.map(i => `${escHtml(String(i?.quantity ?? ''))}x ${escHtml(String(i?.name ?? ''))}`).join(', ') : '';
    const approving = d.status === 'approving';
    const actions = !canApprove
      ? `<span style="font-size:0.75rem;color:var(--text-secondary);max-width:260px;">${escHtml(note)}</span>`
      : approving
        ? `<span style="font-size:0.75rem;color:var(--text-secondary);">Being added — see the Dispatch Board if it stalls.</span>`
        : `<button class="btn btn-primary btn-sm" data-id="${id}" title="Opens the job form filled in from this request and this project's New job defaults"
              onclick="window.DispatchModule.addCustomerRequestToDispatch(this.dataset.id)">
              <span class="material-icons" style="font-size:16px;">playlist_add</span> Add to dispatch
           </button>
           <button class="btn btn-secondary btn-sm" data-id="${id}"
              onclick="window.DispatchModule.declineCustomerRequest(this.dataset.id)">Decline</button>`;
    return `
        <div style="padding:10px;border:1px solid var(--border-light, #E2E8F0);border-radius:6px;background:var(--bg-card, #fff);display:flex;flex-wrap:wrap;justify-content:space-between;gap:8px;">
          <div style="min-width:0;flex:1 1 240px;">
            <div style="font-weight:600;color:var(--text-primary);">${escHtml(String(f.jobName || 'Request'))}</div>
            <div style="font-size:0.8rem;color:var(--text-secondary);margin-top:2px;">${escHtml(String(d.resolved?.whenStr || ''))} · ${po}</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">Asked by ${escHtml(String(d.requesterName || 'customer'))} via the customer portal${d.readyAt ? ` · ${escHtml(_fmtRequestAge(d.readyAt))}` : ''}</div>
            ${items ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">Items: ${items}</div>` : ''}
            <details style="margin-top:6px;">
              <summary style="cursor:pointer;font-size:0.75rem;color:var(--brand-blue, #3B82F6);">What the customer wrote</summary>
              <div style="white-space:pre-wrap;font-size:0.8rem;color:var(--text-primary);margin-top:4px;">${escHtml(String(d.verbatimText || ''))}</div>
            </details>
          </div>
          <div style="display:flex;gap:6px;align-items:flex-start;flex-wrap:wrap;">${actions}</div>
        </div>`;
  }).join('');
  return `
      <div style="margin:0 0 12px 0;padding:12px;border:1px solid #FECACA;border-left:4px solid #DC2626;border-radius:8px;background:#FEF2F2;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;font-weight:600;color:#991B1B;">
          <span class="material-icons" style="font-size:20px;">inbox</span>
          Customer requests (${drafts.length}) — waiting to be added to dispatch
        </div>
        <div style="display:flex;flex-direction:column;gap:8px;">${rows}</div>
      </div>`;
}

// Called by dispatch.js on every customer-request snapshot. List on screen →
// re-render it (same guard as _onProjectsChanged: never over a modal). Detail
// on screen → repaint ONLY the requests section, so a tab someone is reading
// is not rebuilt under them.
function onCustomerRequestsChanged() {
  try {
    const onScreen = document.querySelector('.view-btn[data-view="projects"].dispatcher-only')?.classList.contains('active');
    if (!onScreen) return;
    if (ProjectState.viewMode === 'list') {
      if (!document.querySelector('.modal:not(.hidden)')) renderProjectsView();
      return;
    }
    const host = document.getElementById('project-customer-requests');
    if (host && ProjectState.currentProject && host.dataset.projectId === ProjectState.currentProject.id) {
      host.innerHTML = renderProjectCustomerRequests(ProjectState.currentProject.id);
    }
  } catch (err) {
    console.warn('[Projects] customer-request refresh failed:', err?.message || err);
  }
}

function renderProjectCard(project, opts = {}) {
  const health = calculateHealth(project, []); // Quick health without loading jobs
  const endDays = daysFromNow(project.endDate);
  const run = projectRunState(project, new Date(), tenantTimeZone()); // TASK-9605
  // TASK-9552 — in By Customer order the card prints the GROUP's label (the
  // same name AR/AP shows), so two cards of one customer never read differently.
  const customerName = opts.groupLabel || project.primaryCustomerName || getCustomerName(project.primaryCustomerId);
  const tags = project.tags || [];
  const cardColor = project.status === 'completed' ? '#6B7280' : project.status === 'archived' ? '#9CA3AF' : getProjectColor(project.id, ProjectState.projects);

  // Progress calculation
  let progressPct = 0;
  let progressLabel = '';
  // TASK-9552 — also read string dates (demo-seeded projects store
  // 'YYYY-MM-DD'), so a dated project never gets the empty "no dates" track.
  // Parsed with new Date(v), the SAME parse formatDateShort/projectDayStatus use,
  // so the bar and the date/chip text on one card never disagree by a day.
  const tsMs = (v) => {
    if (!v) return null;
    if (v.toDate) return v.toDate().getTime();
    if (v.seconds) return v.seconds * 1000;
    if (typeof v === 'string') { const t = new Date(v).getTime(); return Number.isFinite(t) ? t : null; }
    return null;
  };
  const startTs = tsMs(project.startDate);
  const endTs = tsMs(project.endDate);
  if (startTs && endTs && endTs > startTs) {
    const now = Date.now();
    progressPct = Math.max(0, Math.min(100, Math.round((now - startTs) / (endTs - startTs) * 100)));
    if (project.status === 'completed') { progressPct = 100; progressLabel = 'Complete'; }
    // TASK-9506 — 100% elapsed is not overdue: the bar hits 100% at the noon
    // anchor on the last day. Overdue only once that day is over.
    // TASK-9605 — past the end date the status marker lives in ONE place (the
    // dates row); the bar carries no second label. "Last day" likewise.
    else if (endDays !== null && endDays <= 0) { progressLabel = ''; }
    else { progressLabel = `${progressPct}%`; }
  } else if (project.status === 'completed') {
    progressPct = 100; progressLabel = 'Complete';
  }

  // Stats. TASK-9552 — every card carries the same rows (dates, progress track,
  // jobs, value) with '—' / an empty track when blank, so cards line up.
  const jobCount = project._jobCount || 0;
  const activeJobs = project._activeJobCount || 0;
  // Number-coerced: a non-numeric value written outside the edit form must never
  // reach innerHTML as text (toLocaleString on a string returns it unchanged).
  const revenueNum = Number(project.estimatedRevenue);
  const revenue = Number.isFinite(revenueNum) && revenueNum !== 0 ? revenueNum : 0;

  // TASK-9552 — one status signal: active is the default and says nothing (the
  // health dot carries it); only a non-active state earns a word.
  const statusWord = project.status && project.status !== 'active' ? String(project.status).replace(/[-_]/g, ' ') : '';
  // TASK-9552 — a location that IS a coordinate pair shows as "Pinned location"
  // (link from the STORED validated numbers only) instead of raw digits. Until a
  // pin is stored the raw text + amber chip stay, so an unsaved pair is visible.
  const pinUrl = parseProjectCoordString(project.location) ? projectPinMapUrl(project) : '';
  // The digits stay readable (relayed to drivers / 811 calls) — from the stored numbers.
  const pinText = pinUrl ? `${project.locationLat}, ${project.locationLng}` : '';

  return `
    <div class="project-card"${opts.groupKey ? ` data-group-key="${escAttr(opts.groupKey)}"` : ''} onclick="window.ProjectsModule.openProjectDetail('${project.id}')" style="cursor:pointer;border-left:4px solid ${cardColor};">
      <div class="project-card-header">
        <div class="project-card-title-row">
          ${healthDot(health)}
          <h3 class="project-card-title">${escHtml(project.name)}</h3>
        </div>
        ${statusWord ? `<span class="project-status-badge project-status-${escAttr(project.status)}">${escHtml(statusWord)}</span>` : ''}
      </div>

      <div class="project-card-customer">
        <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">business</span>
        <span>${escHtml(customerName)}</span>
      </div>

      ${renderProjectJobRequestPill(project.id)}

      ${(project.pendingCustomerRequestCount || 0) > 0 ? `
        <div style="display:inline-flex;align-items:center;gap:4px;background:#FEF3C7;color:#92400E;border:1px solid #FDE68A;border-radius:10px;padding:2px 8px;font-size:0.7rem;font-weight:600;margin-bottom:4px;">
          <span class="material-icons" style="font-size:12px;">notifications_active</span>
          ${project.pendingCustomerRequestCount} customer request${project.pendingCustomerRequestCount !== 1 ? 's' : ''}
        </div>
      ` : ''}

      ${project.location ? `
        <div class="project-card-location">
          <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">location_on</span>
          ${pinUrl ? `<a class="project-card-pin-link" href="${escAttr(pinUrl)}" target="_blank" rel="noopener noreferrer" onclick="event.stopPropagation()" title="Open the pin in Google Maps">Pinned location</a><span class="project-card-pin-coords">${escHtml(pinText)}</span>`
            : `<span>${escHtml(project.location)}</span>`}
        </div>
        ${projectAddressUnmapped(project) ? renderAddressUnmappedChip() : ''}
      ` : ''}

      <div class="project-card-dates">
        <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">date_range</span>
        <span>${formatDateShort(project.startDate)} — ${project.endDate ? formatDateShort(project.endDate) : 'Ongoing'}</span>
        ${endDays !== null && endDays > 0 && endDays <= 14 ? `<span style="color:#F59E0B;font-size:0.75rem;font-weight:600;">${endDays}d left</span>` : ''}
        ${endDays === 0 && project.status === 'active' ? `<span style="color:#F59E0B;font-size:0.75rem;font-weight:600;">Last day</span>` : ''}
        ${run.state === 'ranlong' ? `<span style="color:#EF4444;font-size:0.75rem;font-weight:600;">Ran past end · last day ${escHtml(formatDateShort({ toDate: () => new Date(run.lastDayMs) }))}</span>` : ''}
        ${run.state === 'wrapped' ? `<span style="color:#6B7280;font-size:0.75rem;font-weight:600;">Wrapped</span>` : ''}
      </div>

      ${startTs && endTs ? `
        <div class="project-progress" style="position:relative;">
          <div class="project-progress-fill" style="width:${progressPct}%;background:linear-gradient(90deg, ${cardColor}, ${cardColor}dd);"></div>
          ${progressLabel ? `<span style="position:absolute;right:0;top:-14px;font-size:0.65rem;font-weight:600;color:var(--text-secondary);">${progressLabel}</span>` : ''}
        </div>
      ` : `
        <div class="project-progress project-progress-empty" title="No start/end date — no progress to show"></div>
      `}

      <div class="project-card-stats">
        <div class="project-card-stat">
          <span class="material-icons" style="font-size:13px;">work</span>
          <span data-project-jobcount="${escAttr(project.id)}">${projectJobCountLabel(jobCount)}</span>
        </div>
        <div class="project-card-stat" data-project-value>
          <span class="material-icons" style="font-size:13px;">attach_money</span>
          <span>${revenue ? `$${revenue.toLocaleString()}` : '—'}</span>
        </div>
      </div>

      ${tags.length > 0 ? `
        <div class="project-card-tags">
          ${tags.slice(0, 3).map(t => `<span class="project-tag">${escHtml(t)}</span>`).join('')}
          ${tags.length > 3 ? `<span class="project-tag">+${tags.length - 3}</span>` : ''}
        </div>
      ` : ''}

      ${run.state === 'wrapped' ? `
        <div style="margin-top:8px;">
          <button type="button" class="btn btn-secondary btn-sm" data-project-id="${escAttr(project.id)}" style="min-height:44px;min-width:44px;"
                  onclick="event.stopPropagation();window.ProjectsModule.markWrappedComplete(this.dataset.projectId)">
            <span class="material-icons" style="font-size:16px;">check_circle</span> Mark complete
          </button>
        </div>
      ` : ''}
    </div>
  `;
}

// ============================================
// RENDER: PROJECT DETAIL VIEW
// ============================================
async function openProjectDetail(projectId) {
  ProjectState.currentProject = ProjectState.projects.find(p => p.id === projectId) || null;
  if (!ProjectState.currentProject) {
    // Try loading from Firestore
    try {
      const snap = await getDoc(doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId));
      if (snap.exists()) {
        ProjectState.currentProject = { id: snap.id, ...snap.data() };
      }
    } catch (err) {
      console.error('[Projects] Error loading project detail:', err);
    }
  }
  if (!ProjectState.currentProject) {
    window.showToast?.('Project not found', 'error');
    return;
  }

  ProjectState.viewMode = 'detail';
  ProjectState.detailTab = 'overview';
  // TASK-9674 — opening a project always reads its tickets fresh; the date
  // range on the Field totals card belongs to the project it was set on.
  clearProjectTicketCache();
  ProjectState.fieldTotalsRange = null;
  ProjectState.fieldTotalsData = null;
  renderProjectsView();
}

async function renderProjectDetail(content) {
  const project = ProjectState.currentProject;
  if (!project) return;

  // Show loading state
  content.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;padding:60px;">
      <div class="loading-spinner"></div>
      <span style="margin-left:12px;color:var(--text-secondary);">Loading project...</span>
    </div>
  `;

  // Load all data in parallel
  const [jobs, notes, changeOrders, clockData] = await Promise.all([
    loadProjectJobs(project.id),
    loadProjectNotes(project.id),
    loadChangeOrders(project.id),
    loadProjectClockHours(project.id)
  ]);

  // TASK-9675 — ticket photos are only read when the Docs tab is open.
  // TASK-9650 — the project library is read with the Docs tab, alongside the
  // tickets (its own rules; no jobs needed).
  const libLoad = ProjectState.detailTab === 'documents' ? loadProjectLibrary(project.id) : null;
  let ticketLoad;
  if (ProjectState.detailTab === 'documents' && ProjectState.jobsLoadFailedFor !== project.id && jobs.length) {
    ticketLoad = await loadProjectTicketsForDocs(jobs.map(j => j.id));
    ticketLoad.clockEntries = (clockData && clockData.entries) || [];
    // COD — the extra await widens the window in which the user switches tab,
    // project or back to the list; a superseded render must not paint over it.
    if (ProjectState.viewMode !== 'detail' || ProjectState.currentProject?.id !== project.id
        || ProjectState.detailTab !== 'documents') return;
  }
  if (libLoad) {
    await libLoad; // never throws (failed: true instead)
    if (ProjectState.viewMode !== 'detail' || ProjectState.currentProject?.id !== project.id
        || ProjectState.detailTab !== 'documents') return;
  }

  const health = calculateHealth(project, jobs);
  const customerName = project.primaryCustomerName || getCustomerName(project.primaryCustomerId);
  const activeJobs = jobs.filter(j => j.status === 'active');
  const completedJobs = jobs.filter(j => j.status !== 'active');
  const approvedCOs = changeOrders.filter(co => co.status === 'approved');
  const coTotal = approvedCOs.reduce((sum, co) => sum + (co.amount || 0), 0);
  const tab = ProjectState.detailTab;

  const tabDefs = [
    { key: 'overview', icon: 'dashboard', label: 'Overview', count: null },
    { key: 'jobs', icon: 'work', label: 'Jobs', count: jobs.length },
    { key: 'timeline', icon: 'timeline', label: 'Timeline', count: null },
    { key: 'inventory', icon: 'inventory_2', label: 'Inventory', count: null },
    { key: 'documents', icon: 'folder', label: 'Docs', count: null },
    { key: 'notes', icon: 'sticky_note_2', label: 'Notes', count: notes.length },
    { key: 'changes', icon: 'swap_horiz', label: 'Changes', count: changeOrders.length }
  ];
  const tabsHtml = tabDefs.map(t =>
    `<button class="project-tab ${tab === t.key ? 'active' : ''}" onclick="window.ProjectsModule.setDetailTab('${t.key}')">
      <span class="material-icons">${t.icon}</span>
      <span class="project-tab-label">${t.label}</span>
      ${t.count !== null ? `<span class="project-tab-count">${t.count}</span>` : ''}
    </button>`
  ).join('');

  content.innerHTML = `
    <div class="project-detail">
      <!-- Back + Header -->
      <div class="project-detail-header">
        <button class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.backToList()" style="margin-right:12px;">
          <span class="material-icons" style="font-size:18px;">arrow_back</span> Projects
        </button>
        <div class="project-detail-title-area">
          <div style="display:flex;align-items:center;gap:8px;">
            ${healthDot(health)}
            <!-- TASK-4758: .project-detail-title was dead CSS — the rule existed in
                 styles.css but nothing carried the class, so the responsive title
                 sizing never fired. The class now lives here and the inline
                 font-size/weight are gone; edit the size in styles.css, not here. -->
            <h2 class="project-detail-title">${escHtml(project.name)}</h2>
            <span class="project-status-badge project-status-${project.status}">${project.status}</span>
          </div>
          <div style="font-size:0.85rem;color:var(--text-secondary);margin-top:2px;">
            ${escHtml(customerName)} ${project.location ? `· ${escHtml(project.location)}` : ''}
            ${project.poNumber ? `· <span style="font-weight:600;color:var(--text-primary);" title="Standing PO — applied to new jobs on this project that have no PO of their own">PO ${escHtml(project.poNumber)}</span>` : ''}
          </div>
        </div>
        <div class="project-detail-actions">
          <!-- Quick Actions -->
          <button class="btn btn-primary btn-sm" onclick="window.ProjectsModule.scheduleJobFromProject('${project.id}')" title="Schedule New Job">
            <span class="material-icons" style="font-size:16px;">add_circle</span> New Job
          </button>
          ${project.location ? `
            <a class="btn btn-secondary btn-sm" href="https://maps.google.com/?q=${encodeURIComponent(project.location)}" target="_blank" title="Get Directions">
              <span class="material-icons" style="font-size:16px;">near_me</span>
            </a>
          ` : ''}
          ${project.onSiteContacts?.[0]?.phone ? `
            <a class="btn btn-secondary btn-sm" href="tel:${escAttr(String(project.onSiteContacts[0].phone))}" title="Call ${escAttr(project.onSiteContacts[0].name || 'On-site contact')}">
              <span class="material-icons" style="font-size:16px;">call</span>
            </a>
          ` : ''}
          <button class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.showEditProject('${project.id}')" title="Edit Project">
            <span class="material-icons" style="font-size:16px;">edit</span>
          </button>
          <button class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.duplicateProject('${project.id}')" title="Duplicate">
            <span class="material-icons" style="font-size:16px;">content_copy</span>
          </button>
          <button class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.showProjectMenu('${project.id}')" title="More">
            <span class="material-icons" style="font-size:16px;">more_vert</span>
          </button>
        </div>
      </div>

      <!-- TASK-9392: customer job requests waiting for this project. Repainted
           in place by onCustomerRequestsChanged (never a full re-render). -->
      <div id="project-customer-requests" data-project-id="${escAttr(project.id)}">${renderProjectCustomerRequests(project.id)}</div>

      <!-- Stats Bar -->
      <div class="project-stats-bar">
        <div class="project-stat">
          <span class="project-stat-value">${jobs.length}</span>
          <span class="project-stat-label">Jobs</span>
        </div>
        <div class="project-stat">
          <span class="project-stat-value">${activeJobs.length}</span>
          <span class="project-stat-label">Active</span>
        </div>
        <div class="project-stat">
          <span class="project-stat-value">${clockData.restricted ? '--' : `${clockData.totalHours.toFixed(1)}h`}</span>
          <span class="project-stat-label">${clockData.restricted ? 'Hours (no access)' : 'Hours'}</span>
        </div>
        <div class="project-stat">
          <span class="project-stat-value">${changeOrders.length}</span>
          <span class="project-stat-label">Change Orders</span>
        </div>
        ${Number.isFinite(Number(project.estimatedRevenue)) && Number(project.estimatedRevenue) !== 0 ? `
          <div class="project-stat">
            <span class="project-stat-value">$${Number(project.estimatedRevenue).toLocaleString()}</span>
            <span class="project-stat-label">Est. Revenue</span>
          </div>
        ` : ''}
        ${coTotal > 0 ? `
          <div class="project-stat">
            <span class="project-stat-value" style="color:#F59E0B;">+$${coTotal.toLocaleString()}</span>
            <span class="project-stat-label">Change Orders</span>
          </div>
        ` : ''}
        <div class="project-stat">
          <span class="project-stat-value">${notes.length}</span>
          <span class="project-stat-label">Notes</span>
        </div>
      </div>

      ${(project.pendingCustomerRequestCount || 0) > 0 ? `
      <div style="background:#FEF3C7;border:1px solid #FDE68A;border-radius:8px;padding:10px 16px;margin-bottom:12px;display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="color:#D97706;">notifications_active</span>
          <span style="font-weight:600;font-size:0.875rem;color:#92400E;">
            ${project.pendingCustomerRequestCount} pending customer request${project.pendingCustomerRequestCount !== 1 ? 's' : ''}
          </span>
        </div>
        <button class="btn btn-sm" style="background:#D97706;color:#fff;border:none;"
          onclick="window.ProjectsModule.setDetailTab('changes')">Review</button>
      </div>` : ''}

      <!-- Tabs -->
      <div class="project-tabs">
        ${tabsHtml}
      </div>

      <!-- Tab Content -->
      <div class="project-detail-content" id="project-tab-content">
        ${renderDetailTabContent(tab, project, jobs, notes, changeOrders, clockData, health, ticketLoad)}
      </div>
    </div>
  `;
  if (tab === 'documents') _hydrateProjectDocThumbs(content.querySelector('#project-tab-content'));
  // TASK-9674 — the Field totals card reads tickets AFTER the Overview paints
  // (never blocks it), through the same loader/cache as the Docs tab.
  ProjectState.fieldTotalsJobs = { projectId: project.id, jobs };
  if (tab === 'overview') paintProjectFieldTotals(project, jobs);
}

function renderDetailTabContent(tab, project, jobs, notes, changeOrders, clockData, health, ticketLoad) {
  // TASK-10006 — "Out on this project" + Create return job on the tabs where
  // Guy looks for it (Overview, Jobs, Inventory); filled async, empty when
  // nothing is out.
  const outHost = () => {
    setTimeout(() => renderProjectRentalsOut(project.id), 0);
    return `<div id="project-rentals-out" data-project-id="${escAttr(project.id)}"></div>`;
  };
  switch (tab) {
    case 'overview': return outHost() + renderOverviewTab(project, jobs, notes, changeOrders, clockData, health);
    case 'jobs': return outHost() + renderJobsTab(project, jobs);
    case 'timeline': return renderTimelineTab(project, jobs);
    case 'inventory': return outHost() + renderInventoryTab(project);
    case 'documents': return renderDocumentsTab(project, jobs, ticketLoad);
    case 'notes': return renderNotesTab(project, notes);
    case 'changes': return renderChangesTab(project, changeOrders);
    default: return outHost() + renderOverviewTab(project, jobs, notes, changeOrders, clockData, health);
  }
}

// ── OVERVIEW TAB ──────────────────────────────────────
function renderOverviewTab(project, jobs, notes, changeOrders, clockData, health) {
  const contacts = project.onSiteContacts || [];
  const tags = project.tags || [];

  return `
    <div class="project-overview-grid">
      <!-- Left Column: Details -->
      <div class="project-overview-left">
        <!-- Health -->
        <div class="project-info-card">
          <h4><span class="material-icons" style="font-size:18px;vertical-align:middle;">monitor_heart</span> Health</h4>
          <div style="display:flex;align-items:center;gap:8px;font-size:0.95rem;">
            ${healthDot(health)}
            <span style="text-transform:capitalize;font-weight:600;">${health.status}</span>
            <span style="color:var(--text-secondary);font-size:0.85rem;">— ${health.reason}</span>
          </div>
        </div>

        <!-- TASK-4705 People on this job (one list, shared renderer with CRM) -->
        <div class="project-info-card" id="project-people-card">${renderProjectPeopleCardBody(project)}</div>

        <!-- TASK-9549 Project lead -->
        <div class="project-info-card">
          <h4><span class="material-icons" style="font-size:18px;vertical-align:middle;">person_pin</span> Project lead</h4>
          <div style="font-size:0.9rem;">${projectLeadLabel(project)}</div>
        </div>

        <!-- Description -->
        ${project.description ? `
          <div class="project-info-card">
            <h4><span class="material-icons" style="font-size:18px;vertical-align:middle;">description</span> Description</h4>
            <p style="color:var(--text-secondary);line-height:1.5;margin:0;">${escHtml(project.description)}</p>
          </div>
        ` : ''}

        <!-- Dates -->
        <div class="project-info-card">
          <h4><span class="material-icons" style="font-size:18px;vertical-align:middle;">date_range</span> Schedule</h4>
          <div style="display:flex;gap:24px;font-size:0.9rem;">
            <div><span style="color:var(--text-secondary);">Start:</span> <strong>${formatDate(project.startDate)}</strong></div>
            <div><span style="color:var(--text-secondary);">End:</span> <strong>${project.endDate ? formatDate(project.endDate) : 'Ongoing'}</strong></div>
          </div>
        </div>

        <!-- Location -->
        ${project.location ? `
          <div class="project-info-card">
            <h4><span class="material-icons" style="font-size:18px;vertical-align:middle;">location_on</span> Location</h4>
            <p style="margin:0;">${escHtml(project.location)}</p>
            ${projectAddressUnmapped(project) ? `<div style="margin-top:4px;">${renderAddressUnmappedChip()}</div>` : ''}
            <a href="${escAttr((parseProjectCoordString(project.location) && projectPinMapUrl(project)) || `https://maps.google.com/?q=${encodeURIComponent(project.location)}`)}" target="_blank" rel="noopener noreferrer" style="font-size:0.8rem;color:var(--brand-blue);text-decoration:none;display:inline-flex;align-items:center;gap:4px;margin-top:4px;">
              <span class="material-icons" style="font-size:14px;">near_me</span> Get Directions
            </a>
          </div>
        ` : ''}

        <!-- Tags -->
        ${tags.length > 0 ? `
          <div class="project-info-card">
            <h4><span class="material-icons" style="font-size:18px;vertical-align:middle;">label</span> Tags</h4>
            <div style="display:flex;flex-wrap:wrap;gap:6px;">
              ${tags.map(t => `<span class="project-tag">${escHtml(t)}</span>`).join('')}
            </div>
          </div>
        ` : ''}

        <!-- On-site Contacts -->
        ${contacts.length > 0 ? `
          <div class="project-info-card">
            <h4><span class="material-icons" style="font-size:18px;vertical-align:middle;">contacts</span> On-site Contacts</h4>
            ${contacts.map(c => `
              <div style="display:flex;align-items:center;gap:12px;padding:6px 0;border-bottom:1px solid var(--border-light);">
                <div>
                  <strong>${escHtml(c.name)}</strong>
                  ${c.role ? `<span style="color:var(--text-secondary);font-size:0.8rem;"> · ${escHtml(c.role)}</span>` : ''}
                </div>
                <div style="margin-left:auto;display:flex;gap:8px;">
                  ${c.phone ? `<a href="tel:${escAttr(String(c.phone))}" class="btn-icon-sm" title="Call"><span class="material-icons" style="font-size:16px;">call</span></a>` : ''}
                  ${c.email ? `<a href="mailto:${escAttr(String(c.email))}" class="btn-icon-sm" title="Email"><span class="material-icons" style="font-size:16px;">email</span></a>` : ''}
                </div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>

      <!-- Right Column: Recent Activity -->
      <div class="project-overview-right">
        <!-- TASK-9674: Field totals (filled in place by paintProjectFieldTotals) -->
        ${renderFieldTotalsCardShell(project)}
        <!-- Recent Jobs -->
        <div class="project-info-card">
          <h4><span class="material-icons" style="font-size:18px;vertical-align:middle;">work</span> Recent Jobs</h4>
          ${jobs.length === 0 ? '<p class="empty-state-small" style="color:var(--text-secondary);font-size:0.85rem;margin:0;padding:8px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.55;">link_off</span>No jobs linked yet</p>' : ''}
          ${jobs.slice(0, 5).map(j => `
            <button type="button" class="project-job-open" data-job-id="${escAttr(j.id)}"
                    onclick="window.ProjectsModule.openProjectJob(this.dataset.jobId)"
                    style="${PROJECT_JOB_OPEN_STYLE}border-bottom:1px solid var(--border-light);">
              <span class="project-status-dot project-status-dot-${j.status === 'active' ? 'green' : 'gray'}"></span>
              <span style="flex:1;min-width:0;font-size:0.85rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml(j.title)}</span>
              <span style="color:var(--text-secondary);font-size:0.75rem;">${formatDateShort(j.startDate)}</span>
              <span class="material-icons" aria-hidden="true" style="font-size:18px;color:var(--text-secondary);">chevron_right</span>
            </button>
          `).join('')}
          ${jobs.length > 5 ? `<p style="color:var(--brand-blue);font-size:0.8rem;cursor:pointer;margin:8px 0 0;" onclick="window.ProjectsModule.setDetailTab('jobs')">View all ${jobs.length} jobs →</p>` : ''}
        </div>

        <!-- Recent Notes -->
        <div class="project-info-card">
          <h4><span class="material-icons" style="font-size:18px;vertical-align:middle;">sticky_note_2</span> Recent Notes</h4>
          ${notes.length === 0 ? '<p style="color:var(--text-secondary);font-size:0.85rem;margin:0;">No notes yet</p>' : ''}
          ${notes.slice(0, 3).map(n => `
            <div style="padding:6px 0;border-bottom:1px solid var(--border-light);">
              <div style="font-size:0.85rem;line-height:1.4;">${escHtml(n.text?.substring(0, 120))}${(n.text?.length || 0) > 120 ? '...' : ''}</div>
              <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:2px;">${escHtml(n.authorName || n.createdByName || 'Unknown')} · ${formatDateShort(n.createdAt)} ${renderNoteVisibilityBadge(n)}</div>
            </div>
          `).join('')}
          ${notes.length > 3 ? `<p style="color:var(--brand-blue);font-size:0.8rem;cursor:pointer;margin:8px 0 0;" onclick="window.ProjectsModule.setDetailTab('notes')">View all ${notes.length} notes →</p>` : ''}
        </div>
      </div>
    </div>
  `;
}

// >>> TASK-9674 FIELD TOTALS START >>>
// Project "Field totals": an admin picks ticket data points and the Overview
// sums them across every ticket on the project's jobs.
//
// WHERE EACH NUMBER IS READ FROM — exactly ONE place per recorded value, so
// nothing is counted twice:
//   - A custom ticket-section number field (Field Activity › Dumps, …) is read
//     from the ticket's customSectionData. Repeatable sections sum every
//     instance.
//   - An item (price-book line, consumable, fuel surcharge, …) is read from the
//     ticket's lines (ticketLines, type FIELD_ITEM or MISC), by item code, else
//     by its description.
//   - A line the server DERIVED from a bound field (derivedFromBinding) is a
//     copy of that field's value, so it is NEVER summed. A field bound to an
//     item counts toward that item from customSectionData instead. That also
//     covers older tickets whose derived line was never made (invoiced before
//     the binding existed).
//   - `fieldTicketData.lineQuantities` is a copy of the close-out lines and is
//     never read.
//   - The legacy top-level `dumps` / `waterRefills` are read ONLY by the two
//     "built-in" points, and only on tickets with no customSectionData (see
//     FT_BUILTIN) — on any other ticket they are a stale copy (2026-10-06
//     probe: 19 of 51 TITS tickets disagree with customSectionData).
// Deleted tickets (status 'deleted') are skipped. A ticket is counted once by
// id even if a read returns it twice. Units are shown as each line records
// them (rateUnit); a point whose lines carry different units shows one total
// per unit, never a sum across units.
const FT_MAX_POINTS = 20;
const FT_LINE_TYPES = ['FIELD_ITEM', 'MISC'];
// The product's ORIGINAL Dumps / Water Refills boxes wrote top-level
// ticket.dumps / ticket.waterRefills. Probe 2026-10-06, all tenants: 185
// tickets carry a value > 0 there and have NO customSectionData at all — for
// them it is the only record. Where a ticket HAS customSectionData, Build
// Ticket's save rewrites the top-level pair as 0 (its #field-dumps box is gone),
// so there it is a stale copy and is never read. (The public ticket page, app.js
// #pub-field-dumps, still writes the pair; the probe found no ticket with both.)
const FT_BUILTIN = [
  { key: 'dumps', label: 'Dumps (built-in ticket field)' },
  { key: 'waterRefills', label: 'Water refills (built-in ticket field)' },
];

function _ftOptId(p) {
  if (p.kind === 'field') return `f|${p.csId}|${p.fieldId}`;
  if (p.kind === 'builtin') return `b|${p.key}`;
  return `i|${p.key}`;
}

function _ftHasCsd(t) {
  const csd = t && t.customSectionData;
  return !!csd && typeof csd === 'object' && Object.keys(csd).length > 0;
}

function _ftNorm(s) {
  return String(s == null ? '' : s).normalize('NFKC')
    .replace(/[ ​-‍﻿]/g, ' ').trim().replace(/\s+/g, ' ').toLowerCase();
}

// 'code:DUMPS' when there is an item code, else 'desc:<normalized description>'.
function _ftItemKey(code, desc) {
  const c = String(code == null ? '' : code).trim();
  if (c) return 'code:' + c.toUpperCase();
  const d = _ftNorm(desc);
  return d ? 'desc:' + d : '';
}

function _ftStr(v, max) {
  return typeof v === 'string' ? v.slice(0, max) : '';
}

// The stored setting, cleaned: at most FT_MAX_POINTS, no duplicates, every
// entry either { kind:'field', csId, fieldId, label } or { kind:'item', key, label }.
function normalizeFieldTotalPoints(raw) {
  const list = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.points) ? raw.points : []);
  const out = [];
  const seen = new Set();
  for (const p of list) {
    if (!p || typeof p !== 'object') continue;
    let pt = null;
    if (p.kind === 'field') {
      const csId = _ftStr(p.csId, 200);
      const fieldId = _ftStr(p.fieldId, 200);
      if (csId && fieldId) pt = { kind: 'field', csId, fieldId, label: _ftStr(p.label, 200) };
    } else if (p.kind === 'item') {
      const key = _ftStr(p.key, 300);
      if (/^(code|desc):./.test(key)) pt = { kind: 'item', key, label: _ftStr(p.label, 200) };
    } else if (p.kind === 'builtin') {
      const b = FT_BUILTIN.find(x => x.key === p.key);
      if (b) pt = { kind: 'builtin', key: b.key, label: b.label };
    }
    if (!pt) continue;
    const id = _ftOptId(pt);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(pt);
    if (out.length >= FT_MAX_POINTS) break;
  }
  return out;
}

// Every NUMBER field in the tenant's custom ticket sections.
function fieldTotalsNumberFields(settings) {
  const sections = Array.isArray(settings && settings.customTicketSections) ? settings.customTicketSections : [];
  const out = [];
  for (const cs of sections) {
    if (!cs || !cs.id) continue;
    for (const f of (Array.isArray(cs.fields) ? cs.fields : [])) {
      if (!f || !f.id || f.type !== 'number') continue;
      const b = f.lineItemBinding && (f.lineItemBinding.itemCode || f.lineItemBinding.description) ? f.lineItemBinding : null;
      out.push({
        csId: String(cs.id), fieldId: String(f.id),
        sectionName: String(cs.name || cs.title || 'Ticket section'),
        fieldLabel: String(f.label || f.id),
        unit: typeof f.unit === 'string' ? f.unit.trim() : '',
        binding: b ? { itemCode: String(b.itemCode || ''), description: String(b.description || '') } : null,
      });
    }
  }
  return out;
}

function _ftFieldLabel(nf) {
  return `${nf.sectionName} › ${nf.fieldLabel}`;
}

// Every value a ticket recorded for one custom-section field.
function _ftCsdValues(csd, csId, fieldId) {
  const block = csd && typeof csd === 'object' ? csd[csId] : null;
  if (!block || typeof block !== 'object') return [];
  const instances = (block._repeatable && Array.isArray(block.instances)) ? block.instances : [block];
  const vals = [];
  for (const inst of instances) {
    if (!inst || typeof inst !== 'object') continue;
    const raw = inst[fieldId];
    if (raw === undefined || raw === null || raw === '' || typeof raw === 'boolean') continue;
    const n = Number(raw);
    if (Number.isFinite(n)) vals.push(n);
  }
  return vals;
}

function _ftCountableLine(l) {
  return !!l && typeof l === 'object' && FT_LINE_TYPES.includes(l.type) && !l.derivedFromBinding;
}

function _ftLineUnit(l) {
  return String(l.rateUnit || l.unit || '').trim();
}

// YYYY-MM-DD of the ticket's work day (workDate, else its date in the tenant zone).
function _ftTicketDay(t, tz) {
  if (typeof t.workDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(t.workDate)) return t.workDate.slice(0, 10);
  const d = t.date && typeof t.date.toDate === 'function' ? t.date.toDate() : null;
  if (!d || isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch (_) { return ''; }
}

// The tickets that count: not deleted, on one of the project's jobs, once each.
function _ftLiveTickets(tickets, jobIds) {
  const jobSet = jobIds ? new Set(jobIds) : null;
  const seen = new Set();
  const out = [];
  for (const t of (tickets || [])) {
    if (!t || typeof t !== 'object' || !t.id || seen.has(t.id)) continue;
    if (t.status === 'deleted') continue;
    if (jobSet && !jobSet.has(t.jobId)) continue;
    seen.add(t.id);
    out.push(t);
  }
  return out;
}

// A description-only line folds into an item code when, on this project, that
// description belongs to exactly ONE code (a coded line or a field binding).
function _ftDescFold(tickets, linesByTicket, numberFields) {
  const codesByDesc = new Map();
  const add = (desc, code) => {
    const d = _ftNorm(desc); const c = String(code || '').trim().toUpperCase();
    if (!d || !c) return;
    if (!codesByDesc.has(d)) codesByDesc.set(d, new Set());
    codesByDesc.get(d).add(c);
  };
  for (const nf of numberFields) if (nf.binding) add(nf.binding.description, nf.binding.itemCode);
  for (const t of tickets) {
    for (const l of ((linesByTicket && linesByTicket.get(t.id)) || [])) {
      if (_ftCountableLine(l)) add(l.description, l.itemCode);
    }
  }
  const fold = new Map();
  for (const [d, codes] of codesByDesc) if (codes.size === 1) fold.set('desc:' + d, 'code:' + [...codes][0]);
  return fold;
}

function _ftLineKeys(l, fold) {
  const raw = _ftItemKey(l.itemCode, l.description);
  const keys = new Set();
  if (raw) keys.add(raw);
  if (fold.has(raw)) keys.add(fold.get(raw));
  const d = _ftNorm(l.description);
  if (d) { keys.add('desc:' + d); if (fold.has('desc:' + d)) keys.add(fold.get('desc:' + d)); }
  // A name typed in the picker matches a line's item code too.
  if (l.itemCode) keys.add('desc:' + _ftNorm(l.itemCode));
  return keys;
}

function _ftBindingKeys(nf, fold) {
  const keys = new Set();
  if (!nf.binding) return keys;
  const c = _ftItemKey(nf.binding.itemCode, '');
  if (c) { keys.add(c); keys.add('desc:' + _ftNorm(nf.binding.itemCode)); }
  const d = _ftNorm(nf.binding.description);
  if (d) { keys.add('desc:' + d); if (fold.has('desc:' + d)) keys.add(fold.get('desc:' + d)); }
  return keys;
}

// The items an admin can pick: every countable line on the project's live
// tickets, grouped by (folded) key, with the units and ticket count seen.
function fieldTotalsItemCandidates(tickets, linesByTicket, settings, jobIds) {
  const live = _ftLiveTickets(tickets, jobIds);
  const fold = _ftDescFold(live, linesByTicket, fieldTotalsNumberFields(settings));
  const byKey = new Map();
  for (const t of live) {
    for (const l of ((linesByTicket && linesByTicket.get(t.id)) || [])) {
      if (!_ftCountableLine(l)) continue;
      const raw = _ftItemKey(l.itemCode, l.description);
      if (!raw) continue;
      const key = fold.get(raw) || raw;
      let c = byKey.get(key);
      if (!c) { c = { key, label: '', units: new Set(), tickets: new Set() }; byKey.set(key, c); }
      if (!c.label) c.label = String(l.description || l.itemCode || '').trim();
      c.units.add(_ftLineUnit(l));
      c.tickets.add(t.id);
    }
  }
  return [...byKey.values()]
    .map(c => ({ key: c.key, label: c.label || c.key.slice(5), units: [...c.units].filter(Boolean).sort(), ticketCount: c.tickets.size }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// THE aggregator. linesByTicket: Map ticketId -> line objects (only needed for
// item points). range: { from, to } as YYYY-MM-DD, either optional.
function computeFieldTotals({ points, tickets, linesByTicket, settings, jobIds, range, tz }) {
  const pts = normalizeFieldTotalPoints(points);
  const numberFields = fieldTotalsNumberFields(settings);
  const nfById = new Map(numberFields.map(nf => [`${nf.csId}|${nf.fieldId}`, nf]));
  const live = _ftLiveTickets(tickets, jobIds);
  const fold = _ftDescFold(live, linesByTicket, numberFields);
  // Every item code on this project, so a name typed in the picker that IS a
  // code ("TPOST") also takes the name-only lines folded into that code.
  const codeKeys = new Set(fold.values());
  for (const nf of numberFields) if (nf.binding && nf.binding.itemCode) codeKeys.add(_ftItemKey(nf.binding.itemCode, ''));
  for (const t of live) {
    for (const l of ((linesByTicket && linesByTicket.get(t.id)) || [])) {
      if (_ftCountableLine(l) && l.itemCode) codeKeys.add(_ftItemKey(l.itemCode, ''));
    }
  }
  const from = range && /^\d{4}-\d{2}-\d{2}$/.test(range.from || '') ? range.from : '';
  const to = range && /^\d{4}-\d{2}-\d{2}$/.test(range.to || '') ? range.to : '';
  let undated = 0;
  const inRange = [];
  for (const t of live) {
    if (from || to) {
      const day = _ftTicketDay(t, tz);
      if (!day) { undated++; continue; }
      if ((from && day < from) || (to && day > to)) continue;
    }
    inRange.push(t);
  }
  const deletedCount = (tickets || []).filter(t => t && t.status === 'deleted').length;

  const rows = pts.map(p => {
    const byUnit = new Map();
    const contributing = new Set();
    const addQty = (unit, n, tid) => {
      if (!Number.isFinite(n)) return;
      byUnit.set(unit, (byUnit.get(unit) || 0) + n);
      if (n !== 0) contributing.add(tid);
    };
    let label = p.label;
    let missing = false;
    let needsLines = false;
    if (p.kind === 'field') {
      const nf = nfById.get(`${p.csId}|${p.fieldId}`);
      if (nf) label = _ftFieldLabel(nf); else missing = true;
      const unit = nf ? nf.unit : '';
      for (const t of inRange) for (const v of _ftCsdValues(t.customSectionData, p.csId, p.fieldId)) addQty(unit, v, t.id);
    } else if (p.kind === 'builtin') {
      label = (FT_BUILTIN.find(x => x.key === p.key) || {}).label || label;
      for (const t of inRange) {
        if (_ftHasCsd(t)) continue; // the top-level copy is stale once a ticket has customSectionData
        const raw = t[p.key];
        if (typeof raw !== 'number' && (typeof raw !== 'string' || raw === '')) continue;
        addQty('', Number(raw), t.id);
      }
    } else {
      needsLines = true;
      // Fields bound to this item: their customSectionData values ARE this item.
      const want = new Set([p.key]);
      const asCode = p.key.startsWith('desc:') ? 'code:' + p.key.slice(5).toUpperCase() : '';
      if (asCode && codeKeys.has(asCode)) want.add(asCode);
      const hit = (keys) => { for (const k of want) if (keys.has(k)) return true; return false; };
      const bound = numberFields.filter(nf => hit(_ftBindingKeys(nf, fold)));
      for (const t of inRange) {
        for (const nf of bound) for (const v of _ftCsdValues(t.customSectionData, nf.csId, nf.fieldId)) addQty(nf.unit, v, t.id);
        for (const l of ((linesByTicket && linesByTicket.get(t.id)) || [])) {
          if (!_ftCountableLine(l) || !hit(_ftLineKeys(l, fold))) continue;
          const q = Number(l.quantity);
          if (l.quantity === null || l.quantity === undefined || l.quantity === '' || !Number.isFinite(q)) continue;
          addQty(_ftLineUnit(l), q, t.id);
        }
      }
      if (!label) label = p.key.slice(5);
    }
    const totals = [...byUnit.entries()]
      .map(([unit, qty]) => ({ unit, qty: Math.round(qty * 10000) / 10000 }))
      .sort((a, b) => a.unit.localeCompare(b.unit));
    return { kind: p.kind, label: label || '(unnamed)', missing, needsLines, totals, ticketCount: contributing.size };
  });
  return { rows, ticketsCounted: inRange.length, undatedSkipped: undated, deletedSkipped: deletedCount };
}

function _ftFmtQty(n) {
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 4 });
}

// MODULE access, never a role (Chris 2026-09-24, "hardline that rule"): the
// tenant OWNER via tenants/{tid}.ownerId, OR a member whose `modules` include
// 'dispatch' — the module whose firestore.rules arm lets a member update a
// project doc (hasModule 'dispatch' && !isViewUploadOnly). members.role is not
// read. A view-upload-only, suspended or removed member gets no button; so does
// demo (no member doc). Mirrors dispatch.js _customerRequestCanApprove.
// TASK-9651 — the rules' tenant-wide project arm is now projMgr: 'dispatch'
// AND 'projects', so this needs both.
function fieldTotalsCanEdit() {
  const S = window.AppState || {};
  if (S.isDemo || S.tenant === 'demo') return false;
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const m = S.memberAccess;
  if (!m || m.status === 'suspended' || m.status === 'removed' || m.viewUploadOnly === true) return false;
  return Array.isArray(m.modules) && m.modules.includes('dispatch') && m.modules.includes('projects');
}

function _ftSettings() {
  return window.AppState?.settings || window.AppState?.tenantData?.settings || {};
}

// Ticket lines for the totals — read only when an ITEM point is chosen (field
// points need none). One query per ticket, 8 at a time, FIELD_ITEM/MISC only.
// Cached per ticket for the same window as the project's ticket load. Any
// failure fails the WHOLE read: a partial sum is never shown as the total.
const _ftLineCache = new Map(); // `${tid}/${ticketId}` -> { at, lines }
async function loadTicketLinesForTotals(tickets, opts = {}) {
  const tid = ProjectState.tenantId;
  if (!tid) return { linesByTicket: new Map(), failed: true };
  const linesByTicket = new Map();
  const todo = [];
  if (opts.fresh) _ftLineCacheGen++; // an older read still in flight must not overwrite this one
  const gen = _ftLineCacheGen;
  for (const t of (tickets || [])) {
    if (!t || !t.id || t.status === 'deleted' || linesByTicket.has(t.id)) continue;
    const c = _ftLineCache.get(`${tid}/${t.id}`);
    if (!opts.fresh && c && Date.now() - c.at < PROJECT_TICKET_CACHE_MS) linesByTicket.set(t.id, c.lines);
    else { linesByTicket.set(t.id, []); todo.push(t.id); }
  }
  try {
    for (let i = 0; i < todo.length; i += 8) {
      await Promise.all(todo.slice(i, i + 8).map(async (id) => {
        const snap = await getDocs(query(
          collection(db, 'tenants', tid, 'tickets', id, 'ticketLines'),
          where('type', 'in', FT_LINE_TYPES)
        ));
        const lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        linesByTicket.set(id, lines);
        if (gen === _ftLineCacheGen) _ftLineCache.set(`${tid}/${id}`, { at: Date.now(), lines });
      }));
    }
    return { linesByTicket, failed: false };
  } catch (err) {
    console.error('[Projects] Error loading ticket lines for field totals:', err && err.code);
    return { linesByTicket: new Map(), failed: true };
  }
}

// Card shell: painted with the Overview, filled in by paintProjectFieldTotals.
function renderFieldTotalsCardShell(project) {
  const points = normalizeFieldTotalPoints(project.fieldTotals);
  const canEdit = fieldTotalsCanEdit();
  if (!points.length && !canEdit) return '';
  return `<div class="project-info-card" id="project-field-totals" data-project-id="${escAttr(project.id)}">
    ${_ftCardHeader(canEdit)}
    <div id="project-field-totals-body" style="font-size:0.85rem;color:var(--text-secondary);">
      ${points.length ? 'Loading totals…' : 'Pick the ticket numbers to add up across this project (dumps, water, T-posts…).'}
    </div>
  </div>`;
}

function _ftCardHeader(canEdit) {
  return `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
      <h4 style="margin:0;flex:1;"><span class="material-icons" style="font-size:18px;vertical-align:middle;">functions</span> Field totals</h4>
      ${canEdit ? `<button type="button" class="btn btn-secondary btn-sm" style="min-height:44px;" onclick="window.ProjectsModule.showFieldTotalsPicker()">
        <span class="material-icons" style="font-size:16px;">tune</span> Choose data points</button>` : ''}
    </div>`;
}

function _ftRangeHtml() {
  const r = ProjectState.fieldTotalsRange || {};
  return `<div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin:8px 0;">
      <label style="font-size:0.8rem;color:var(--text-secondary);">From <input type="date" id="ft-range-from" value="${escAttr(r.from || '')}" onchange="window.ProjectsModule.setFieldTotalsRange()" style="min-height:44px;"></label>
      <label style="font-size:0.8rem;color:var(--text-secondary);">To <input type="date" id="ft-range-to" value="${escAttr(r.to || '')}" onchange="window.ProjectsModule.setFieldTotalsRange()" style="min-height:44px;"></label>
      ${(r.from || r.to) ? `<button type="button" class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.setFieldTotalsRange(true)">All dates</button>` : ''}
      <button type="button" class="btn btn-secondary btn-sm" title="Re-read the tickets" onclick="window.ProjectsModule.refreshFieldTotals()">
        <span class="material-icons" style="font-size:16px;">refresh</span></button>
    </div>`;
}

function renderFieldTotalsBody(result, linesFailed) {
  if (!result.rows.length) return '';
  const rows = result.rows.map(r => {
    let value;
    if (r.needsLines && linesFailed) value = '<span title="Couldn\'t read the ticket lines">—</span>';
    else if (!r.totals.length) value = '0';
    else value = r.totals.map(t => `${escHtml(_ftFmtQty(t.qty))}${t.unit ? ` <span style="color:var(--text-secondary);font-weight:400;">${escHtml(t.unit)}</span>` : ''}`).join(' · ');
    return `<div style="display:flex;align-items:baseline;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
        <span style="flex:1;color:var(--text-primary);">${escHtml(r.label)}${r.missing ? ' <span style="color:var(--text-secondary);font-size:0.75rem;">(field no longer on tickets)</span>' : ''}</span>
        <strong style="color:var(--text-primary);">${value}</strong>
      </div>`;
  }).join('');
  // COD Domain — say which tickets count, so nobody quotes a draft as invoiced.
  const notes = [`${result.ticketsCounted} ticket${result.ticketsCounted === 1 ? '' : 's'} (drafts, ready and invoiced)`, 'deleted tickets left out'];
  if (result.undatedSkipped) notes.push(`${result.undatedSkipped} without a date left out of the date range`);
  return `${rows}
    ${linesFailed ? '<p style="margin:8px 0 0;font-size:0.8rem;color:var(--text-primary);">Couldn\'t read the ticket lines, so item totals aren\'t shown. Try refresh.</p>' : ''}
    <p style="margin:8px 0 0;font-size:0.75rem;color:var(--text-secondary);">${escHtml(notes.join(' · '))}</p>`;
}

let _ftPaintSeq = 0;
function _ftStillOn(projectId, seq) {
  return seq === _ftPaintSeq && ProjectState.viewMode === 'detail'
    && ProjectState.currentProject?.id === projectId && ProjectState.detailTab === 'overview';
}

// Reads the project's tickets (the SAME loader + cache as the Docs tab), the
// lines only when an item point is chosen, then paints the card in place. A
// superseded paint (other project / tab / list, or a newer paint) never lands.
async function paintProjectFieldTotals(project, jobs, opts = {}) {
  const seq = ++_ftPaintSeq;
  const body = () => {
    const card = document.getElementById('project-field-totals');
    return card && card.dataset.projectId === project.id ? document.getElementById('project-field-totals-body') : null;
  };
  const points = normalizeFieldTotalPoints(project.fieldTotals);
  if (!points.length || !body()) return;
  if (ProjectState.jobsLoadFailedFor === project.id) {
    body().innerHTML = '<span style="color:var(--text-primary);">Couldn\'t load this project\'s jobs, so totals can\'t be shown. Reload and try again.</span>';
    return;
  }
  const jobIds = (jobs || []).map(j => j.id);
  if (!jobIds.length) { body().innerHTML = 'No jobs on this project yet.'; return; }
  const ticketLoad = await loadProjectTicketsForDocs(jobIds, { fresh: !!opts.fresh });
  if (!_ftStillOn(project.id, seq) || !body()) return;
  if (ticketLoad.failed) {
    body().innerHTML = `${_ftRangeHtml()}<span style="color:var(--text-primary);">Couldn't load this project's tickets. Try refresh.</span>`;
    return;
  }
  let linesByTicket = new Map();
  let linesFailed = false;
  if (points.some(p => p.kind === 'item')) {
    const live = _ftLiveTickets(ticketLoad.tickets, jobIds);
    if (live.length) {
      body().innerHTML = `Reading ${live.length} ticket${live.length === 1 ? '' : 's'}…`;
      const lr = await loadTicketLinesForTotals(live, { fresh: !!opts.fresh });
      if (!_ftStillOn(project.id, seq) || !body()) return;
      linesByTicket = lr.linesByTicket;
      linesFailed = lr.failed;
    }
  }
  ProjectState.fieldTotalsData = { projectId: project.id, tickets: ticketLoad.tickets, linesByTicket, linesFailed, jobIds };
  _ftRepaintFromData(project);
}

function _ftRepaintFromData(project) {
  const data = ProjectState.fieldTotalsData;
  const el = document.getElementById('project-field-totals-body');
  if (!data || data.projectId !== project.id || !el) return;
  const result = computeFieldTotals({
    points: project.fieldTotals, tickets: data.tickets, linesByTicket: data.linesByTicket,
    settings: _ftSettings(), jobIds: data.jobIds, range: ProjectState.fieldTotalsRange, tz: tenantTimeZone(),
  });
  el.innerHTML = `${_ftRangeHtml()}${renderFieldTotalsBody(result, data.linesFailed)}`;
}

function setFieldTotalsRange(clear) {
  const project = ProjectState.currentProject;
  if (!project) return;
  ProjectState.fieldTotalsRange = clear === true ? { from: '', to: '' } : {
    from: document.getElementById('ft-range-from')?.value || '',
    to: document.getElementById('ft-range-to')?.value || '',
  };
  _ftRepaintFromData(project); // a date filter needs no new reads
}

function refreshFieldTotals() {
  const project = ProjectState.currentProject;
  if (!project) return;
  const jobs = ProjectState.fieldTotalsJobs?.projectId === project.id ? ProjectState.fieldTotalsJobs.jobs : [];
  paintProjectFieldTotals(project, jobs, { fresh: true });
}

// ── Picker ──
let _ftPicker = null; // { projectId, options: [point], ready, itemsLoaded, itemsBusy }
// Opens with the ticket fields + the current choice straight away (no reads).
// Items found on the project's tickets cost one read per ticket, so they load
// only when the admin asks ("Find items…") — COD: read cost on big projects.
function showFieldTotalsPicker() {
  const project = ProjectState.currentProject;
  if (!project || !fieldTotalsCanEdit()) return;
  const chosen = normalizeFieldTotalPoints(project.fieldTotals);
  const options = [];
  for (const nf of fieldTotalsNumberFields(_ftSettings())) {
    options.push({ kind: 'field', csId: nf.csId, fieldId: nf.fieldId, label: _ftFieldLabel(nf), hint: nf.binding ? `billed as ${nf.binding.description || nf.binding.itemCode}` : '' });
  }
  for (const b of FT_BUILTIN) options.push({ kind: 'builtin', key: b.key, label: b.label, hint: 'counts only tickets with no custom ticket-section data' });
  // Chosen points not offered by the fields list (items, removed fields) stay listed.
  const offered = new Set(options.map(_ftOptId));
  for (const p of chosen) if (!offered.has(_ftOptId(p))) options.push({ ...p, hint: p.kind === 'field' ? 'field no longer on tickets' : '' });
  // Every chosen point is an option from the first paint, so Save can never
  // write an empty list over the current choice.
  _ftPicker = { projectId: project.id, options, ready: true, itemsLoaded: false, itemsBusy: false };

  let modal = document.getElementById('modal-field-totals');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-field-totals';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `<div class="modal-content modal-medium" style="text-align:left;max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3>Field totals</h3>
        <button class="btn-icon" onclick="document.getElementById('modal-field-totals').classList.add('hidden')"><span class="material-icons">close</span></button>
      </div>
      <p style="margin:0 0 8px;font-size:0.85rem;color:var(--text-primary);">Pick what to add up across every ticket on this project's jobs (draft, ready and invoiced; deleted tickets are left out). Everyone who can open the project sees the totals.</p>
      <div id="ft-picker-list" style="font-size:0.9rem;"></div>
      <div class="form-group" style="margin-top:12px;">
        <label for="ft-picker-other">Another item (item code or exact name on the ticket)</label>
        <div style="display:flex;gap:8px;">
          <input type="text" id="ft-picker-other" maxlength="120" placeholder="e.g. T-posts" style="flex:1;min-height:44px;">
          <button type="button" class="btn btn-secondary" style="min-height:44px;" onclick="window.ProjectsModule.addFieldTotalsOther()">Add</button>
        </div>
      </div>
      <div class="form-actions">
        <button class="btn btn-secondary" onclick="document.getElementById('modal-field-totals').classList.add('hidden')">Cancel</button>
        <button class="btn btn-primary" id="ft-picker-save" onclick="window.ProjectsModule.saveFieldTotalsPicker()">Save</button>
      </div>
    </div>`;
  modal.classList.remove('hidden');
  _ftPaintPickerList(new Set(chosen.map(_ftOptId)), '');
}

// Reads the project's tickets (shared loader) and their lines, then adds every
// item they record to the list. Keeps whatever is already ticked.
async function findFieldTotalsItems() {
  const me = _ftPicker;
  const project = ProjectState.currentProject;
  if (!me || !project || me.projectId !== project.id || me.itemsBusy) return;
  const jobs = ProjectState.fieldTotalsJobs?.projectId === project.id ? ProjectState.fieldTotalsJobs.jobs : [];
  const jobIds = jobs.map(j => j.id);
  me.itemsBusy = true;
  _ftPaintPickerList(_ftPickerCheckedIds(), '');
  let items = [];
  let note = '';
  if (jobIds.length) {
    const tl = await loadProjectTicketsForDocs(jobIds);
    if (tl.failed) note = "Couldn't read this project's tickets. Try again.";
    else {
      const live = _ftLiveTickets(tl.tickets, jobIds);
      const lr = live.length ? await loadTicketLinesForTotals(live) : { linesByTicket: new Map(), failed: false };
      if (lr.failed) note = "Couldn't read the ticket lines. Try again.";
      else items = fieldTotalsItemCandidates(tl.tickets, lr.linesByTicket, _ftSettings(), jobIds);
    }
  }
  // A re-opened picker (or another project) owns the list now.
  if (_ftPicker !== me || ProjectState.currentProject?.id !== project.id) return;
  const checked = _ftPickerCheckedIds(); // read BEFORE options grow: indexes stay stable (append only)
  for (const it of items) {
    const hint = `${it.units.length ? it.units.join(', ') + ' · ' : ''}on ${it.ticketCount} ticket${it.ticketCount === 1 ? '' : 's'}`;
    const existing = me.options.find(o => o.kind === 'item' && o.key === it.key);
    if (existing) existing.hint = hint;
    else me.options.push({ kind: 'item', key: it.key, label: it.label, hint });
  }
  me.itemsBusy = false;
  me.itemsLoaded = !note;
  _ftPaintPickerList(checked, note || (items.length ? '' : 'No items on this project\'s tickets yet.'));
}

function _ftPaintPickerList(chosenIds, itemsNote) {
  const el = document.getElementById('ft-picker-list');
  if (!el || !_ftPicker) return;
  const row = (p, i) => `<label style="display:flex;gap:8px;align-items:flex-start;padding:6px 0;min-height:44px;cursor:pointer;">
      <input type="checkbox" class="ft-picker-cb" data-idx="${i}" ${chosenIds.has(_ftOptId(p)) ? 'checked' : ''} style="margin-top:3px;">
      <span><span style="color:var(--text-primary);">${escHtml(p.label)}</span>${p.hint ? `<br><span style="font-size:0.75rem;color:var(--text-secondary);">${escHtml(p.hint)}</span>` : ''}</span>
    </label>`;
  const fieldRows = _ftPicker.options.map((p, i) => (p.kind === 'field' || p.kind === 'builtin') ? row(p, i) : '').join('');
  const itemRows = _ftPicker.options.map((p, i) => p.kind === 'item' ? row(p, i) : '').join('');
  const findBtn = _ftPicker.itemsLoaded ? '' : `<button type="button" class="btn btn-secondary btn-sm" id="ft-picker-find" style="min-height:44px;margin-top:4px;" ${_ftPicker.itemsBusy ? 'disabled' : ''} onclick="window.ProjectsModule.findFieldTotalsItems()">
      <span class="material-icons" style="font-size:16px;">search</span> ${_ftPicker.itemsBusy ? 'Reading tickets…' : "Find items on this project's tickets"}</button>`;
  el.innerHTML = `
    <h4 style="margin:8px 0 4px;font-size:0.85rem;">Ticket fields</h4>
    ${fieldRows || '<p style="margin:0;font-size:0.8rem;color:var(--text-secondary);">No number fields on your ticket sections.</p>'}
    <h4 style="margin:12px 0 4px;font-size:0.85rem;">Items</h4>
    ${itemRows}
    ${findBtn}
    ${itemsNote ? `<p style="margin:4px 0 0;font-size:0.8rem;color:var(--text-primary);">${escHtml(itemsNote)}</p>` : ''}`;
}

function _ftPickerCheckedIds() {
  const ids = new Set();
  document.querySelectorAll('#ft-picker-list .ft-picker-cb').forEach(cb => {
    const p = _ftPicker?.options[Number(cb.dataset.idx)];
    if (cb.checked && p) ids.add(_ftOptId(p));
  });
  return ids;
}

function addFieldTotalsOther() {
  const input = document.getElementById('ft-picker-other');
  const text = (input?.value || '').trim();
  if (!_ftPicker || !_ftPicker.ready || !text) return;
  // Typed text matches a line's item code OR its exact name, so offer it as a name;
  // a line carrying that code folds in through the code/name match on the project.
  const key = _ftItemKey('', text);
  if (!key) return;
  const checked = _ftPickerCheckedIds();
  if (!_ftPicker.options.some(p => p.kind === 'item' && p.key === key)) {
    _ftPicker.options.push({ kind: 'item', key, label: text, hint: 'added by name' });
  }
  checked.add(`i|${key}`);
  input.value = '';
  _ftPaintPickerList(checked, '');
}

async function saveFieldTotalsPicker() {
  const project = ProjectState.currentProject;
  if (!project || !_ftPicker || !_ftPicker.ready || _ftPicker.projectId !== project.id) return;
  if (!fieldTotalsCanEdit()) { window.showToast?.("You don't have access to change this", 'error'); return; }
  const picked = [];
  document.querySelectorAll('#ft-picker-list .ft-picker-cb').forEach(cb => {
    const p = _ftPicker.options[Number(cb.dataset.idx)];
    if (cb.checked && p) picked.push(p.kind === 'field'
      ? { kind: 'field', csId: p.csId, fieldId: p.fieldId, label: p.label }
      : { kind: p.kind === 'builtin' ? 'builtin' : 'item', key: p.key, label: p.label });
  });
  if (picked.length > FT_MAX_POINTS) { window.showToast?.(`Pick at most ${FT_MAX_POINTS}`, 'error'); return; }
  const points = normalizeFieldTotalPoints(picked);
  const btn = document.getElementById('ft-picker-save');
  if (btn) btn.disabled = true;
  const fieldTotals = { points, updatedAt: Timestamp.now(), updatedBy: window.AppState?.user?.uid || null };
  const ok = await updateProject(project.id, { fieldTotals });
  if (btn) btn.disabled = false;
  if (!ok) return;
  project.fieldTotals = fieldTotals; // currentProject may be a separate object from the list cache
  document.getElementById('modal-field-totals')?.classList.add('hidden');
  window.showToast?.('Field totals saved', 'success');
  if (ProjectState.detailTab === 'overview' && ProjectState.currentProject?.id === project.id) renderProjectsView();
}
// <<< TASK-9674 FIELD TOTALS END <<<

// ── JOBS TAB ──────────────────────────────────────────
function renderJobsTab(project, jobs) {
  const activeJobs = jobs.filter(j => j.status === 'active');
  const otherJobs = jobs.filter(j => j.status !== 'active');

  return `
    <div class="project-jobs-tab">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h4 style="margin:0;">Linked Jobs</h4>
        <div style="display:flex;gap:8px;">
          <button class="btn btn-primary btn-sm" onclick="window.ProjectsModule.scheduleJobFromProject('${project.id}')">
            <span class="material-icons" style="font-size:16px;">add_circle</span> Schedule New Job
          </button>
          <button class="btn btn-secondary btn-sm" onclick="window.ProjectsModule.showLinkExistingJob('${project.id}')">
            <span class="material-icons" style="font-size:16px;">link</span> Link Existing
          </button>
        </div>
      </div>

      ${activeJobs.length > 0 ? `
        <h5 style="color:var(--text-secondary);font-size:0.8rem;text-transform:uppercase;letter-spacing:0.5px;margin:16px 0 8px;">Active (${activeJobs.length})</h5>
        ${activeJobs.map(j => renderJobRow(j, project.id)).join('')}
      ` : ''}

      ${otherJobs.length > 0 ? `
        <h5 style="color:var(--text-secondary);font-size:0.8rem;text-transform:uppercase;letter-spacing:0.5px;margin:16px 0 8px;">Completed / Cancelled (${otherJobs.length})</h5>
        ${otherJobs.map(j => renderJobRow(j, project.id)).join('')}
      ` : ''}

      ${jobs.length === 0 ? `
        <div style="text-align:center;padding:40px;color:var(--text-secondary);">
          <span class="material-icons" style="font-size:48px;display:block;margin-bottom:8px;">work_off</span>
          No jobs linked to this project yet
        </div>
      ` : ''}
    </div>
  `;
}

// TASK-10006 — what this project still has out, from the SAME count the job
// screen and "Create return job" use (window.DispatchModule.projectRentalsOut
// → the shared rental math; never a second derivation here — the Inventory
// tab below adds deploys and returns together and is not a "still out").
// Empty when nothing is out or Dispatch isn't available to this login.
async function renderProjectRentalsOut(projectId) {
  const host = document.getElementById('project-rentals-out');
  const api = window.DispatchModule;
  if (!host || host.dataset.projectId !== projectId || !api || typeof api.projectRentalsOut !== 'function') return;
  if (typeof window.checkModuleAccess === 'function' && window.checkModuleAccess('dispatch', true) !== true) return;
  let r = null;
  try { r = await api.projectRentalsOut(projectId); } catch (e) {
    console.warn('[Projects] TASK-10006: rentals-out count failed', e?.code || e?.message || e);
    return;
  }
  const now = document.getElementById('project-rentals-out');
  if (!now || now.dataset.projectId !== projectId || !r || !r.model) return;
  const m = r.model;
  const btn = (m.pickable > 0 && r.canCreate && r.baseJobId)
    ? `<button type="button" class="btn btn-primary btn-sm" style="white-space:nowrap;min-height:40px;" data-project-id="${escAttr(projectId)}" data-job-id="${escAttr(r.baseJobId)}" onclick="window.DispatchModule.createReturnJobForProject(this.dataset.projectId, this.dataset.jobId)"><span class="material-icons" aria-hidden="true" style="font-size:16px;vertical-align:-3px;">assignment_return</span> Create return job</button>`
    : '';
  now.innerHTML = `
    <div class="rentals-out-strip" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:0 0 12px;padding:10px 12px;border:1px solid var(--border-light, #E2E8F0);border-left:4px solid var(--brand-blue, #3B82F6);border-radius:6px;background:var(--bg-secondary, #F8FAFC);color:var(--text-primary);min-width:0;">
      <span class="material-icons" aria-hidden="true" style="font-size:20px;color:var(--brand-blue, #3B82F6);">inventory_2</span>
      <div style="flex:1 1 220px;min-width:0;">
        <div style="font-weight:600;font-size:0.9rem;overflow-wrap:anywhere;">${escHtml(m.title)}</div>
        ${(m.lines || []).map(l => `<div style="font-size:0.8rem;color:var(--text-secondary);overflow-wrap:anywhere;">${escHtml(l)}</div>`).join('')}
      </div>
      ${btn}
    </div>`;
}

// Every job click in a project lands here. It used to call
// DispatchModule.openJobDetails, which dispatch.js never exported, and the
// `?.()` swallowed the miss — a dead click. openJobDetailsById also loads a job
// that is outside the board window or opened before Dispatch was visited.
// tests/project-job-click-exports.unit.cjs fails if this drifts again.
function openProjectJob(jobId) {
  const open = window.DispatchModule && window.DispatchModule.openJobDetailsById;
  if (typeof open !== 'function') {
    window.showToast?.('Job details are not available right now. Refresh the page and try again.', 'error');
    return;
  }
  // Harvest q15 (Chris 2026-10-12: "Warn") — a project lists finished and
  // long-past jobs next to live ones, and the details modal edits either. The
  // opener confirms once (Continue / Cancel) before an old job opens; it stays
  // fully editable after Continue.
  open(jobId, null, { warnIfClosed: true });
}

// A real <button> (keyboard + screen reader), 48px tall for gloves.
const PROJECT_JOB_OPEN_STYLE = 'display:flex;align-items:center;gap:10px;width:100%;min-width:0;min-height:48px;padding:8px 4px;margin:0;background:none;border:0;border-radius:6px;text-align:left;cursor:pointer;color:var(--text-primary);font:inherit;';

function renderJobRow(job, projectId) {
  const statusColors = { active: '#10B981', completed: '#6B7280', cancelled: '#EF4444' };
  const title = job.title || 'Untitled job';
  return `
    <div class="project-job-row" style="display:flex;align-items:center;gap:4px;border-bottom:1px solid var(--border-light);">
      <button type="button" class="project-job-open" data-job-id="${escAttr(job.id)}"
              onclick="window.ProjectsModule.openProjectJob(this.dataset.jobId)"
              style="${PROJECT_JOB_OPEN_STYLE}flex:1;">
        <span aria-hidden="true" style="width:8px;height:8px;border-radius:50%;background:${statusColors[job.status] || '#6B7280'};flex-shrink:0;"></span>
        <span style="flex:1;min-width:0;display:block;">
          <span style="display:block;font-weight:600;font-size:0.9rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHtml(title)}</span>
          <span style="display:block;font-size:0.75rem;color:var(--text-secondary);">
            ${formatDateShort(job.startDate)} — ${formatDateShort(job.endDate)}
            ${job.customer ? ` · ${escHtml(job.customer)}` : ''}
          </span>
        </span>
        <span class="material-icons" aria-hidden="true" style="font-size:20px;color:var(--text-secondary);">chevron_right</span>
      </button>
      <button type="button" class="btn-icon-sm project-job-unlink" data-job-id="${escAttr(job.id)}" data-project-id="${escAttr(projectId)}"
              onclick="event.stopPropagation();window.ProjectsModule.unlinkJob(this.dataset.jobId, this.dataset.projectId)"
              title="Unlink from project" aria-label="Unlink ${escAttr(title)} from this project"
              style="min-width:48px;min-height:48px;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;">
        <span class="material-icons" aria-hidden="true" style="font-size:18px;color:var(--text-secondary);">link_off</span>
      </button>
    </div>
  `;
}

// ── TIMELINE TAB ──────────────────────────────────────
function renderTimelineTab(project, jobs) {
  if (jobs.length === 0) {
    return `<div style="text-align:center;padding:40px;color:var(--text-secondary);">
      <span class="material-icons" style="font-size:48px;display:block;margin-bottom:8px;">timeline</span>
      No jobs to show on timeline
    </div>`;
  }

  // Sort jobs by start date
  const sorted = [...jobs].sort((a, b) => {
    const aStart = a.startDate?.seconds || 0;
    const bStart = b.startDate?.seconds || 0;
    return aStart - bStart;
  });

  // Find the overall date range
  const projectStart = project.startDate?.toDate ? project.startDate.toDate() : new Date();
  const allDates = sorted.flatMap(j => {
    const dates = [];
    if (j.startDate?.toDate) dates.push(j.startDate.toDate());
    if (j.endDate?.toDate) dates.push(j.endDate.toDate());
    return dates;
  });
  allDates.push(projectStart);
  if (project.endDate?.toDate) allDates.push(project.endDate.toDate());

  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime()), Date.now()));
  const totalMs = maxDate.getTime() - minDate.getTime() || 86400000;

  const statusColors = { active: '#3B82F6', completed: '#10B981', cancelled: '#EF4444' };

  return `
    <div class="project-timeline">
      <div class="timeline-header">
        <span style="font-size:0.75rem;color:var(--text-secondary);">${formatDateShort(minDate)}</span>
        <div class="timeline-today-marker" style="left:${Math.min(100, Math.max(0, ((Date.now() - minDate.getTime()) / totalMs) * 100))}%;">
          <span style="font-size:0.7rem;color:var(--brand-blue);font-weight:600;">Today</span>
        </div>
        <span style="font-size:0.75rem;color:var(--text-secondary);">${formatDateShort(maxDate)}</span>
      </div>

      <div class="timeline-track">
        <!-- Today line -->
        <div class="timeline-today-line" style="left:${Math.min(100, Math.max(0, ((Date.now() - minDate.getTime()) / totalMs) * 100))}%;"></div>

        ${sorted.map(j => {
          const jStart = j.startDate?.toDate ? j.startDate.toDate() : minDate;
          const jEnd = j.endDate?.toDate ? j.endDate.toDate() : jStart;
          const leftPct = ((jStart.getTime() - minDate.getTime()) / totalMs) * 100;
          const widthPct = Math.max(1, ((jEnd.getTime() - jStart.getTime()) / totalMs) * 100);
          return `
            <button type="button" class="timeline-bar" data-job-id="${escAttr(j.id)}"
                 style="display:block;left:${leftPct}%;width:${widthPct}%;min-width:48px;min-height:44px;margin:4px 0;padding:4px 8px;border:0;border-radius:6px;text-align:left;cursor:pointer;color:#fff;font:inherit;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;background:${statusColors[j.status] || '#6B7280'};"
                 title="${escAttr(j.title)}: ${formatDateShort(j.startDate)} — ${formatDateShort(j.endDate)}"
                 aria-label="Open job ${escAttr(j.title)}, ${formatDateShort(j.startDate)} to ${formatDateShort(j.endDate)}"
                 onclick="window.ProjectsModule.openProjectJob(this.dataset.jobId)">
              <span class="timeline-bar-label">${escHtml(j.title)}</span>
            </button>
          `;
        }).join('')}
      </div>
    </div>
  `;
}

// ── INVENTORY TAB ──────────────────────────────────────
function renderInventoryTab(project) {
  // Placeholder — will load async
  const container = `
    <div id="project-inventory-content" style="min-height:100px;">
      <div style="display:flex;align-items:center;gap:8px;padding:20px;color:var(--text-secondary);">
        <div class="loading-spinner" style="width:20px;height:20px;"></div> Loading inventory...
      </div>
    </div>
  `;

  // TASK-9263 — body lives in the LAZY views/projInventory.js (perf budget appColdLoadGzip; deps handed over, pitfall #1).
  setTimeout(() => {
    __ddChunkWrap(import('./projInventory.js?v=92630001'))
      .then((m) => m.fillProjectInventory(project, { collection, db, escHtml, getDocs, loadProjectJobs, ProjectState, query, where }))
      .catch((e) => {
        console.error('[Projects] projInventory:', e);
        const el = document.getElementById('project-inventory-content');
        if (el) el.textContent = 'Could not load inventory.';
      });
  }, 0);

  return container;
}

// ── DOCUMENTS TAB ──────────────────────────────────────
// TASK-9647 — the Docs tab used to print `href="[object Object]"` for every
// file: stored rows carry `fullUrl` (uploadJobPhotos / _uploadOnePermit), the
// tab read `photo.url || photo` and so put the whole object in the href,
// unescaped, inside a grid class that has no CSS. Now: each card is a button
// that opens the file through the same read-both fallback as dispatch.js
// previewExistingPermit (stored URL first, else getDownloadURL on the path;
// PDF tab opened synchronously so the popup blocker allows it), and a stored
// URL is only ever used when it is a Firebase Storage https URL — a
// `javascript:` or foreign URL saved on a job never becomes a link.
// integ/9647 COD: pinned to THIS app's bucket (not any bucket on the host),
// so a link to someone else's bucket saved on a job never opens directly.
function _projectDocUrlPrefixes() {
  const bucket = storage?.app?.options?.storageBucket;
  if (typeof bucket !== 'string' || !bucket) return [];
  return [`https://firebasestorage.googleapis.com/v0/b/${bucket}/o/`];
}
function _isSafeProjectDocUrl(u) {
  return typeof u === 'string' && _projectDocUrlPrefixes().some(pfx => u.startsWith(pfx));
}
// A stored path must be a bare object path. `gs://other-bucket/...` or a full
// https URL would make ref() reach outside the allowlist above.
function _isSafeProjectDocPath(p) {
  return typeof p === 'string' && !!p.trim() && !/^[a-z][a-z0-9+.-]*:/i.test(p.trim());
}
// TASK-9649 — a file's day for the row label: its own uploadedAt, else the
// job's start. { label: 'Oct 8', sortKey }.
function _projectDocDay(...cands) {
  for (const v of cands) {
    if (!v) continue;
    let d = null;
    if (typeof v.toDate === 'function') d = v.toDate();
    else if (typeof v.seconds === 'number') d = new Date(v.seconds * 1000);
    else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) d = new Date(v + 'T12:00:00');
    else if (typeof v === 'string' || typeof v === 'number' || v instanceof Date) d = new Date(v);
    if (d && !isNaN(d.getTime())) {
      return { label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), sortKey: d.getTime() };
    }
  }
  return { label: '', sortKey: 0 };
}
function _projectJobNo(job) {
  return job && job.jobNumber != null && job.jobNumber !== '' ? `J-${job.jobNumber}` : '';
}
function _projectDocRows(jobs) {
  const rows = [];
  for (const job of (jobs || [])) {
    const add = (type, f) => {
      if (!f) return;
      // Legacy rows may be a bare URL string.
      const file = typeof f === 'string' ? { fullUrl: f } : f;
      if (typeof file !== 'object') return;
      // Same detection as dispatch.js permit preview, incl. the filename
      // fallback for legacy rows with no mimeType/isPdf.
      const isPdf = file.mimeType === 'application/pdf' || file.isPdf === true
        || (typeof file.name === 'string' && file.name.toLowerCase().endsWith('.pdf'));
      const day = _projectDocDay(file.uploadedAt, job.startDate, job.createdAt);
      rows.push({
        type, isPdf, file,
        name: (typeof file.name === 'string' && file.name.trim()) ? file.name.trim() : (type === 'photo' ? 'Photo' : 'Permit'),
        jobIds: [job.id], jobTitle: job.title || '', jobNumber: _projectJobNo(job),
        dateLabel: day.label, sortKey: day.sortKey,
      });
    };
    (Array.isArray(job.photos) ? job.photos : []).forEach(f => add('photo', f));
    (Array.isArray(job.permits) ? job.permits : []).forEach(f => add(isJobDocEntry(f, job) ? 'jobdoc' : 'permit', f));
  }
  return rows;
}

// TASK-9649 — the 811 tickets the project's JOBS carry (jobs.permitIds, the
// TASK-9648 list, + the legacy single jobs.permitId), ONE row per ticket with
// every job that holds it ("Linked to J-1195, J-1199"). The ids resolve
// against the permits snapshot dispatch.js already keeps (permits are
// canReadTenant, same as jobs — nothing here is readable that the viewer
// could not already open). `skipIds` = the project's own tickets, already
// listed (and managed) in the 811 section above. Once the snapshot has loaded
// an id with no permit doc is a deleted ticket and is not shown.
function _projectJob811Rows(jobs, skipIds) {
  const skip = skipIds instanceof Set ? skipIds : new Set(skipIds || []);
  const byId = new Map(); // id -> [job]
  for (const job of (jobs || [])) {
    if (!job) continue;
    const ids = [];
    for (const raw of [...(Array.isArray(job.permitIds) ? job.permitIds : []), job.permitId]) {
      const id = typeof raw === 'string' ? raw.trim() : '';
      if (id && !ids.includes(id)) ids.push(id);
    }
    for (const id of ids) {
      if (skip.has(id)) continue;
      if (!byId.has(id)) byId.set(id, []);
      byId.get(id).push(job);
    }
  }
  const { loaded, permits } = _project811Permits();
  const rows = [];
  for (const [id, holders] of byId) {
    const p = permits.find(x => x && x.id === id) || null;
    if (!p && loaded) continue;
    const name = p ? String(p.name || p.pdfName || '811 ticket') : '811 ticket';
    const day = _projectDocDay(p && p.createdAt);
    const nums = holders.map(_projectJobNo).filter(Boolean);
    rows.push({
      type: '811', isPdf: true, pending: !p, permitId: id,
      file: p ? { fullUrl: p.pdfUrl, fullPath: p.storagePath, name: p.pdfName || name, isPdf: true } : {},
      name, ticketNo: p && p.ticketNumber ? String(p.ticketNumber) : '',
      jobIds: holders.map(j => j.id), jobNumber: nums.join(', '),
      jobTitle: holders.length === 1 ? (holders[0].title || '') : '',
      linkedLabel: nums.length ? `Linked to ${nums.join(', ')}` : '',
      dateLabel: day.label, sortKey: day.sortKey,
      badge: p ? project811ExpiryBadge(p) : null,
    });
  }
  return rows.sort((a, b) => b.sortKey - a.sortKey);
}

// TASK-9675 — a Firebase download URL carries its object path in /o/<enc>.
// Ticket photos store ONLY `url` (jobboard/clock upload: {url,name,uploadedAt},
// no path), so a URL from an older bucket name has no path to fall back to —
// derive it, and it opens through ref() in THIS app's bucket (rules-checked).
function _projectDocPathFromUrl(u) {
  if (typeof u !== 'string') return null;
  const m = u.match(/^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/?#]+\/o\/([^?#]+)/);
  if (!m) return null;
  let p;
  try { p = decodeURIComponent(m[1]); } catch (_e) { return null; }
  if (!_isSafeProjectDocPath(p)) return null;
  // COD hardening — a URL on a ticket is driver-writable; only derive a path
  // inside THIS tenant's own prefixes (Storage rules are still the real gate).
  const tid = ProjectState.tenantId;
  if (!tid || !(p.startsWith(`tickets/${tid}/`) || p.startsWith(`tenants/${tid}/`))) return null;
  return p;
}
// The thumbnail URL for a card, or null. Only a safe stored URL — no
// getDownloadURL per tile; a path-only file shows its icon and still opens.
// TASK-9855 — this now only DECIDES whether a card gets a thumb; the bytes come
// through the Storage SDK on the path (_projectDocFileRef), never this URL.
function _projectDocThumbUrl(row) {
  if (!row || row.isPdf || row.type === 'permit') return null;
  const f = row.file || {};
  return [f.thumbUrl, f.fullUrl, f.url, f.downloadUrl].find(_isSafeProjectDocUrl) || null;
}
// TASK-9855 slice 1 — read BOTH: the stored path wins, else the path inside the
// stored URL (tenant-fenced by _projectDocPathFromUrl). Returns {path, url} for
// window.StorageFile (the url rides along only as its transitional
// network-failure fallback — a rules DENIAL never opens it), or null.
function _projectDocFileRef(row, wantThumb) {
  const f = (row && row.file) || {};
  const fullPath = [f.fullPath, f.storagePath].find(_isSafeProjectDocPath)
    || [f.fullUrl, f.url, f.downloadUrl].map(_projectDocPathFromUrl).find(Boolean) || null;
  const thumbPath = [f.thumbPath].find(_isSafeProjectDocPath) || _projectDocPathFromUrl(f.thumbUrl) || null;
  const path = wantThumb ? (thumbPath || fullPath) : (fullPath || (row && row.isPdf ? null : thumbPath));
  if (!path) return null;
  const urls = wantThumb && thumbPath ? [f.thumbUrl] : [f.fullUrl, f.url, f.downloadUrl, f.thumbUrl];
  const url = urls.find(u => _isSafeProjectDocUrl(u) && _projectDocPathFromUrl(u) === path) || null;
  return { path: path.trim(), url };
}
function _projectTicketDay(t) {
  let d = null;
  const wd = t && typeof t.workDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(t.workDate) ? t.workDate : null;
  if (wd) {
    const [y, mo, da] = wd.split('-').map(Number);
    d = new Date(y, mo - 1, da, 12); // calendar day, noon local — no TZ slide
  } else if (t && t.date) {
    d = t.date.toDate ? t.date.toDate() : new Date(t.date);
  }
  if (!d || isNaN(d.getTime())) return { label: '', sortKey: 0 };
  return { label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), sortKey: d.getTime() };
}
// Bridged copies of the JOB's own files (billing _bridgeJobDocsInto) are
// already listed from the job — never twice. TASK-9649: also matched by the
// bridge ids and by the job file's own path/URL (a copy that lost its source).
const _PROJECT_TICKET_DOC_SKIP = new Set(['job_permit', 'job_photo']);
// TASK-9675 — the photos drivers attach to their tickets (Build Ticket /
// clock-out), one row per file. Reads BOTH stores: `photos[]` ({url}) and
// `documents[]` ({fullUrl, fullPath|null}); a clock-out photo the biller
// reviewed lives in both with the same URL, so rows are de-duplicated by URL.
// Driver = who clocked onto that ticket (clock entries already loaded for the
// project), else the ticket's own employees[] names.
// TASK-9649 — photos[] and `clock_photo` copies are 'ticketphoto' (everyone
// who can open the project, as TASK-9675 shipped). Every other documents[]
// file is a 'ticketdoc' and is built ONLY when opts.withDocs (the host passes
// "has the billing module"; tickets are canReadTenant, so rules can't gate it).
function _projectTicketDocRows(tickets, jobs, clockEntries, opts = {}) {
  const withDocs = opts.withDocs === true;
  const jobFileKeys = opts.jobFileKeys instanceof Set ? opts.jobFileKeys : new Set();
  const jobsById = new Map((jobs || []).map(j => [j && j.id, j]));
  const crewByTicket = new Map();
  for (const e of (clockEntries || [])) {
    const nm = e && typeof e.personName === 'string' ? e.personName.trim() : '';
    if (!e || !e.ticketId || !nm) continue;
    if (!crewByTicket.has(e.ticketId)) crewByTicket.set(e.ticketId, []);
    const list = crewByTicket.get(e.ticketId);
    if (!list.includes(nm)) list.push(nm);
  }
  const rows = [];
  for (const t of (tickets || [])) {
    if (!t || typeof t !== 'object' || t.status === 'deleted') continue;
    const job = jobsById.get(t.jobId) || {};
    let crew = crewByTicket.get(t.id) || [];
    if (!crew.length) {
      crew = [];
      for (const e of (Array.isArray(t.employees) ? t.employees : [])) {
        const raw = e && (e.personName || e.name);
        const nm = typeof raw === 'string' ? raw.trim() : '';
        if (nm && !crew.includes(nm)) crew.push(nm);
      }
    }
    const driver = crew.length > 1 ? `${crew[0]} +${crew.length - 1}` : (crew[0] || '');
    const day = _projectTicketDay(t);
    const seen = new Set();
    const add = (f, isDocRow) => {
      if (!f) return;
      const file = typeof f === 'string' ? { url: f } : f;
      if (typeof file !== 'object') return;
      if (isDocRow && (_PROJECT_TICKET_DOC_SKIP.has(file.source) || file.jobPermitId || file.jobPhotoId)) return;
      const keys = [file.fullUrl, file.url, file.downloadUrl, file.fullPath].filter(v => typeof v === 'string' && v);
      const key = keys[0] || '';
      if (!key || keys.some(k => seen.has(k))) return;
      if (isDocRow && keys.some(k => jobFileKeys.has(k))) return;
      const type = !isDocRow || file.source === 'clock_photo' ? 'ticketphoto' : 'ticketdoc';
      if (type === 'ticketdoc' && !withDocs) return;
      keys.forEach(k => seen.add(k));
      const name = typeof file.name === 'string' ? file.name.trim() : '';
      let urlPdf = false;
      try { urlPdf = decodeURIComponent(String(key).split('?')[0]).toLowerCase().endsWith('.pdf'); } catch (_e) { /* keep false */ }
      const isPdf = file.mimeType === 'application/pdf' || file.isPdf === true || name.toLowerCase().endsWith('.pdf') || urlPdf;
      rows.push({
        type, isPdf, file,
        name: name || (isPdf ? 'Ticket file' : 'Ticket photo'),
        ticketNo: t.ticketNo != null && t.ticketNo !== '' ? `#${t.ticketNo}` : '',
        dateLabel: day.label, sortKey: day.sortKey, driver,
        jobIds: t.jobId ? [t.jobId] : [],
        jobTitle: job.title || t.jobName || '', jobNumber: _projectJobNo(job),
      });
    };
    (Array.isArray(t.photos) ? t.photos : []).forEach(f => add(f, false));
    (Array.isArray(t.documents) ? t.documents : []).forEach(f => add(f, true));
  }
  // Newest day first; stable within a day (upload order).
  return rows.map((r, i) => [r, i]).sort((a, b) => (b[0].sortKey - a[0].sortKey) || (a[1] - b[1])).map(x => x[0]);
}

// TASK-9649 — "Ticket docs" (files on tickets: scale tickets, receipts, signed
// field tickets) are billing records. Same shape as fieldTotalsCanEdit: owner
// via ownerId, else a live member whose modules include 'billing'. Modules,
// not roles (Chris 9/24).
function projectDocsCanSeeTicketDocs() {
  const S = window.AppState || {};
  if (S.isDemo === true || S.tenant === 'demo') return true;
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const m = S.memberAccess;
  if (!m || m.status === 'suspended' || m.status === 'removed') return false;
  return Array.isArray(m.modules) && m.modules.includes('billing');
}

// ── TASK-9649 — Docs tab library (HR Company Documents look) ───────────────
// Type chips · job filter · plain-text search over ONE indexed row list
// (ProjectState.docRows). Filtering never rebuilds the rows, so an index in
// an onclick always points at the same file. Photos render as a thumbnail
// strip so they don't bury permits.
const PROJECT_DOC_TYPES = [
  { key: 'all', label: 'All', types: null },
  { key: '811', label: '811', types: ['811'] },
  { key: 'permits', label: 'Load permits', types: ['permit'] },
  { key: 'jobdocs', label: 'Driver docs', types: ['jobdoc'] }, // TASK-9694
  { key: 'photos', label: 'Photos', types: ['photo', 'ticketphoto'] },
  { key: 'ticketdocs', label: 'Ticket docs', types: ['ticketdoc'] },
];
const _PROJECT_PHOTO_TYPES = new Set(['photo', 'ticketphoto']);
const _PROJECT_DOC_SOURCE = { '811': '811', permit: 'Load permit', jobdoc: 'Driver doc', photo: 'Job photo', ticketphoto: 'Ticket photo', ticketdoc: 'Ticket doc' };

function _projectDocsFilterFor(projectId) {
  if (!ProjectState.docsFilter || ProjectState.docsFilterFor !== projectId) {
    ProjectState.docsFilter = { type: 'all', jobId: '', q: '' };
    ProjectState.docsFilterFor = projectId;
  }
  return ProjectState.docsFilter;
}
function _projectDocHaystack(r) {
  return [r.name, r.jobNumber, r.jobTitle, r.ticketNo, r.driver, r.linkedLabel,
    r.file && typeof r.file.name === 'string' ? r.file.name : '']
    .filter(v => typeof v === 'string' && v).join(' ').toLowerCase();
}
// Pure. [[row, index]] matching the job + search filters (type applied by caller).
function _projectDocsMatch(rows, f) {
  const words = String((f && f.q) || '').toLowerCase().split(/\s+/).filter(Boolean);
  const jobId = (f && f.jobId) || '';
  const out = [];
  (rows || []).forEach((r, i) => {
    if (!r) return;
    if (jobId && !(r.jobIds || []).includes(jobId)) return;
    if (words.length) {
      const hay = _projectDocHaystack(r);
      if (!words.every(w => hay.includes(w))) return;
    }
    out.push([r, i]);
  });
  return out;
}
function _projectDocTypeOf(key) {
  return PROJECT_DOC_TYPES.find(t => t.key === key) || PROJECT_DOC_TYPES[0];
}
function _projectDocsChipsHtml(matched, f, chipKeys) {
  return PROJECT_DOC_TYPES.filter(t => chipKeys.includes(t.key)).map(t => {
    const n = t.types ? matched.filter(([r]) => t.types.includes(r.type)).length : matched.length;
    const active = f.type === t.key;
    return `<button type="button" class="hr-docs-category-chip${active ? ' hr-docs-category-chip-active' : ''}" data-doc-type="${t.key}" aria-pressed="${active}"
        style="min-height:44px;" onclick="window.ProjectsModule.setProjectDocsFilter('type','${t.key}')">${escHtml(t.label)} <span class="hr-docs-category-chip-count">${n}</span></button>`;
  }).join('');
}
function _projectDocRowHtml(d, i) {
  const icon = d.type === '811' ? 'assignment' : d.isPdf ? 'picture_as_pdf' : (d.type === 'permit' || d.type === 'ticketdoc') ? 'description' : 'image';
  const color = d.type === '811' ? '#B45309' : d.type === 'permit' ? '#059669' : d.type === 'ticketdoc' ? '#7C3AED' : d.isPdf ? '#DC2626' : '#2563EB';
  const title = d.type === 'ticketphoto' || d.type === 'ticketdoc'
    ? ([d.ticketNo ? `Ticket ${d.ticketNo}` : 'Ticket', d.name].filter(Boolean).join(' · '))
    : d.name;
  const sub = d.type === '811'
    ? [d.ticketNo ? `#${d.ticketNo}` : '', d.linkedLabel, d.dateLabel].filter(Boolean).join(' · ')
    : [d.jobNumber, d.dateLabel, d.driver, d.jobTitle].filter(Boolean).join(' · ');
  const badgeCss = { expired: 'background:#FEE2E2;color:#991B1B;', soon: 'background:#FEF3C7;color:#92400E;', ok: 'background:#E0F2FE;color:#075985;' };
  const badge = d.badge && badgeCss[d.badge.tone]
    ? `<span class="project-doc-badge" style="font-size:0.72rem;font-weight:600;padding:2px 8px;border-radius:999px;white-space:nowrap;${badgeCss[d.badge.tone]}">${escHtml(d.badge.text)}</span>` : '';
  // TASK-9675 — the thumbnail <img> carries NO src in the markup (TASK-9647:
  // no URL in the HTML at all); _hydrateProjectDocThumbs sets it by row index.
  // opacity, NOT display:none — Chrome never fetches a lazy <img> with no box.
  const thumb = _projectDocThumbUrl(d) ? `<img data-project-doc-thumb="${i}" alt="" loading="lazy" decoding="async" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:8px;opacity:0;">` : '';
  return `
        <button type="button" class="project-doc-row" data-doc-type="${escAttr(d.type)}" onclick="window.ProjectsModule.openProjectDoc(${i})"
          title="${escAttr(escHtml(title))}" style="display:flex;align-items:center;gap:12px;width:100%;min-height:56px;padding:8px 12px;text-align:left;font:inherit;color:var(--text-primary);background:var(--bg-elevated,#fff);border:1px solid var(--border-light);border-radius:var(--radius-md,8px);cursor:pointer;">
          <span style="position:relative;flex-shrink:0;width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;background:${color}14;">
            <span class="material-icons" aria-hidden="true" style="font-size:22px;color:${color};">${icon}</span>${thumb}
          </span>
          <span style="display:flex;flex-direction:column;min-width:0;flex:1;">
            <span class="project-doc-name" style="font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml(title)}</span>
            <span class="project-doc-job" style="font-size:0.8rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"><span class="project-doc-source" style="font-size:0.7rem;font-weight:600;padding:1px 6px;margin-right:4px;border-radius:999px;background:${color}14;color:${color};">${escHtml(_PROJECT_DOC_SOURCE[d.type] || '')}</span>${escHtml(sub)}</span>
          </span>
          ${badge}
          <span style="flex-shrink:0;font-size:0.85rem;font-weight:600;color:var(--brand-blue);">View</span>
        </button>`;
}
function _projectPhotoTileHtml(d, i) {
  const label = [d.ticketNo ? `Ticket ${d.ticketNo}` : d.name, d.driver, d.jobNumber, d.dateLabel, d.jobTitle].filter(Boolean).join(' · ');
  const thumb = _projectDocThumbUrl(d) ? `<img data-project-doc-thumb="${i}" alt="" loading="lazy" decoding="async" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:8px;opacity:0;">` : '';
  return `
        <button type="button" class="project-doc-photo" data-doc-type="${escAttr(d.type)}" onclick="window.ProjectsModule.openProjectDoc(${i})"
          title="${escAttr(escHtml(label))}" aria-label="View ${escAttr(escHtml(label))}"
          style="position:relative;flex-shrink:0;width:88px;height:88px;padding:0;border:1px solid var(--border-light);border-radius:8px;background:var(--bg-page,#F1F5F9);cursor:pointer;display:flex;align-items:center;justify-content:center;">
          <span class="material-icons" aria-hidden="true" style="font-size:28px;color:${d.isPdf ? '#DC2626' : '#94A3B8'};">${d.isPdf ? 'picture_as_pdf' : 'image'}</span>${thumb}
        </button>`;
}
// Pure. The list area for the current filter (indexes into `rows`).
function _projectDocsListHtml(rows, f) {
  const matched = _projectDocsMatch(rows, f);
  const t = _projectDocTypeOf(f.type);
  const inType = t.types ? matched.filter(([r]) => t.types.includes(r.type)) : matched;
  if (!inType.length) {
    const filtered = !!(f.q || f.jobId || f.type !== 'all');
    return `
      <div style="text-align:center;padding:28px 12px;color:var(--text-secondary);">
        <span class="material-icons" aria-hidden="true" style="font-size:40px;display:block;margin-bottom:6px;">search_off</span>
        ${filtered ? 'No documents match.' : 'No documents from linked jobs'}
        ${filtered ? `<div><button type="button" class="btn btn-secondary btn-sm" style="min-height:44px;margin-top:10px;" onclick="window.ProjectsModule.setProjectDocsFilter('reset')">Clear filters</button></div>` : ''}
      </div>`;
  }
  const docs = inType.filter(([r]) => !_PROJECT_PHOTO_TYPES.has(r.type));
  const photos = inType.filter(([r]) => _PROJECT_PHOTO_TYPES.has(r.type));
  const docsHtml = docs.length
    ? `<div class="project-docs-rows" style="display:flex;flex-direction:column;gap:6px;margin-bottom:14px;">${docs.map(([r, i]) => _projectDocRowHtml(r, i)).join('')}</div>` : '';
  let photosHtml = '';
  if (photos.length) {
    const wrap = f.type === 'photos';
    photosHtml = `
      ${wrap ? '' : `<div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);margin:4px 0 8px;">Photos (${photos.length})</div>`}
      <div class="project-docs-photo-strip" style="display:flex;gap:8px;${wrap ? 'flex-wrap:wrap;' : 'overflow-x:auto;padding-bottom:6px;-webkit-overflow-scrolling:touch;'}">
        ${photos.map(([r, i]) => _projectPhotoTileHtml(r, i)).join('')}
      </div>`;
  }
  return docsHtml + photosHtml;
}
// Called after the tab's HTML is in the DOM. src is a DOM property set from
// the row (never parsed from markup) and only for a this-bucket https URL.
function _hydrateProjectDocThumbs(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  if (!ProjectState.currentProject || ProjectState.docRowsFor !== ProjectState.currentProject.id) return;
  root.querySelectorAll('img[data-project-doc-thumb]').forEach(img => {
    const row = (ProjectState.docRows || [])[Number(img.getAttribute('data-project-doc-thumb'))];
    // TASK-9855 — bytes through the Storage SDK (rules-checked), not the token URL.
    const fref = _projectDocThumbUrl(row) ? _projectDocFileRef(row, true) : null;
    if (!fref || !window.StorageFile) { img.remove(); return; }
    img.onload = () => { img.style.opacity = '1'; };
    img.onerror = () => { img.remove(); };
    window.StorageFile.blobUrl(fref).then((u) => { if (img.isConnected) img.src = u; }).catch(() => img.remove());
  });
}

// Chip / job / search handler. Repaints only the chips and the list, never
// the search box (typing keeps focus).
function setProjectDocsFilter(key, value) {
  const project = ProjectState.currentProject;
  if (!project || ProjectState.docRowsFor !== project.id) return;
  const f = _projectDocsFilterFor(project.id);
  if (key === 'reset') {
    f.type = 'all'; f.jobId = ''; f.q = '';
    const s = document.getElementById('project-docs-search'); if (s) s.value = '';
    const j = document.getElementById('project-docs-job'); if (j) j.value = '';
  } else if (key === 'type') {
    if (!(ProjectState.docChipKeys || []).includes(value)) return;
    f.type = value;
  } else if (key === 'jobId') {
    f.jobId = typeof value === 'string' ? value : '';
  } else if (key === 'q') {
    f.q = String(value || '').slice(0, 120);
  } else return;
  const rows = ProjectState.docRows || [];
  const chips = document.getElementById('project-docs-chips');
  if (chips) chips.innerHTML = _projectDocsChipsHtml(_projectDocsMatch(rows, f), f, ProjectState.docChipKeys || ['all']);
  const list = document.getElementById('project-docs-list');
  if (list) {
    list.innerHTML = _projectDocsListHtml(rows, f);
    _hydrateProjectDocThumbs(list);
  }
}

// ticketLoad (TASK-9675, optional): { tickets, clockEntries, failed }.
function renderDocumentsTab(project, jobs, ticketLoad) {
  if (ProjectState.jobsLoadFailedFor === project.id) {
    return renderProjectLibSection(project, []) + `
      <div style="text-align:center;padding:40px;color:var(--text-secondary);">
        <span class="material-icons" style="font-size:48px;display:block;margin-bottom:8px;">error_outline</span>
        Couldn't load this project's jobs, so its documents can't be shown. Reload and try again.
      </div>
    `;
  }
  // TASK-9767 — the project's own 811 tickets head the tab (link / upload /
  // unlink live there); the library below lists the JOBS' tickets, minus the
  // ones already above, so a ticket shows once.
  // TASK-9650 — the project's own library sits under 811, above "From jobs".
  const sec811 = renderProject811Section(project);
  const secLib = renderProjectLibSection(project, jobs);
  const canTicketDocs = projectDocsCanSeeTicketDocs();
  const jobDocs = _projectDocRows(jobs);
  const jobFileKeys = new Set();
  for (const r of jobDocs) {
    for (const k of [r.file.fullUrl, r.file.url, r.file.fullPath]) if (typeof k === 'string' && k) jobFileKeys.add(k);
  }
  // TASK-9994 — nothing 811 while the company has it off.
  const rows811 = _project811Enabled() ? _projectJob811Rows(jobs, sec811 ? projectPermitIdsOf(project) : []) : [];
  const ticketRows = ticketLoad && !ticketLoad.failed
    ? _projectTicketDocRows(ticketLoad.tickets, jobs, ticketLoad.clockEntries, { withDocs: canTicketDocs, jobFileKeys }) : [];
  // One indexed list. Documents: 811 → load permits → ticket docs (newest
  // first inside each); photos: job photos then ticket photos.
  const byDay = (a, b) => b.sortKey - a.sortKey;
  const docs = [
    ...rows811,
    ...jobDocs.filter(r => r.type === 'permit').sort(byDay),
    ...jobDocs.filter(r => r.type === 'jobdoc').sort(byDay),
    ...ticketRows.filter(r => r.type === 'ticketdoc'),
    ...jobDocs.filter(r => r.type === 'photo').sort(byDay),
    ...ticketRows.filter(r => r.type === 'ticketphoto'),
  ];
  // The click handler reads the row back by index (no URL or name ever goes
  // into an inline handler).
  ProjectState.docRows = docs;
  ProjectState.docRowsFor = project.id; // a stale index never opens another project's file
  // "Couldn't look" is not "none" — said in words, never an empty section.
  const ticketFailNote = ticketLoad && ticketLoad.failed ? `
      <div style="padding:10px 12px;margin-bottom:12px;border:1px solid #FDE68A;background:#FFFBEB;color:#92400E;border-radius:8px;font-size:0.85rem;">
        Couldn't load ticket photos${canTicketDocs ? ' and ticket docs' : ''} for this project, so this list is partial. Reload to try again.
      </div>` : '';

  if (docs.length === 0) {
    return sec811 + secLib + ticketFailNote + `
      <div style="text-align:center;padding:40px;color:var(--text-secondary);">
        <span class="material-icons" style="font-size:48px;display:block;margin-bottom:8px;">folder_off</span>
        No documents from linked jobs
      </div>
    `;
  }

  const chipKeys = _project811Enabled() ? ['all', '811', 'permits', 'photos'] : ['all', 'permits', 'photos']; // TASK-9994
  if (canTicketDocs) chipKeys.push('ticketdocs');
  if (docs.some(r => r.type === 'jobdoc')) chipKeys.push('jobdocs'); // TASK-9694
  ProjectState.docChipKeys = chipKeys;
  const f = _projectDocsFilterFor(project.id);
  if (!chipKeys.includes(f.type)) f.type = 'all';
  const jobOpts = [];
  for (const j of (jobs || [])) {
    if (!j || !docs.some(r => (r.jobIds || []).includes(j.id))) continue;
    jobOpts.push(`<option value="${escAttr(escHtml(j.id))}"${f.jobId === j.id ? ' selected' : ''}>${escHtml([_projectJobNo(j), j.title || ''].filter(Boolean).join(' · ') || 'Job')}</option>`);
  }
  if (jobOpts.length < 2) f.jobId = '';
  return `
    ${sec811}
    ${secLib}
    ${ticketFailNote}
    <div class="project-docs-lib" id="project-docs-lib">
      <div style="display:flex;align-items:center;gap:8px;margin:4px 0 10px;">
        <span style="font-weight:600;font-size:0.95rem;color:var(--text-primary);">From jobs</span>
        <span class="hr-docs-category-chip-count" id="project-docs-count">${docs.length}</span>
      </div>
      <div class="hr-docs-category-strip" id="project-docs-chips" role="toolbar" aria-label="Document type">${_projectDocsChipsHtml(_projectDocsMatch(docs, f), f, chipKeys)}</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px;">
        ${jobOpts.length > 1 ? `<select id="project-docs-job" class="form-control" aria-label="Filter by job" style="flex:1 1 180px;min-height:44px;max-width:100%;"
          onchange="window.ProjectsModule.setProjectDocsFilter('jobId', this.value)"><option value="">All jobs</option>${jobOpts.join('')}</select>` : ''}
        <input type="search" id="project-docs-search" class="form-control" placeholder="Search name, job #, ticket #" aria-label="Search documents"
          value="${escAttr(escHtml(f.q))}" autocomplete="off" style="flex:2 1 220px;min-height:44px;max-width:100%;"
          oninput="window.ProjectsModule.setProjectDocsFilter('q', this.value)">
      </div>
      <div id="project-docs-list">${_projectDocsListHtml(docs, f)}</div>
    </div>
  `;
}

async function openProjectDoc(idx) {
  if (!ProjectState.currentProject || ProjectState.docRowsFor !== ProjectState.currentProject.id) return;
  const row = (ProjectState.docRows || [])[Number(idx)];
  if (!row) return;
  // TASK-9649 — an 811 row whose permit doc has not arrived yet.
  if (row.pending) { window.showToast?.('Still loading 811 tickets. Try again in a moment.', 'info'); return; }
  // TASK-9855 slice 1 — every file opens through the Storage SDK on its PATH
  // (stored path, else the path inside the stored URL), so storage.rules decide.
  // The stored token URL is never opened directly any more.
  const fref = _projectDocFileRef(row, false);
  if (!fref || !window.StorageFile) {
    window.showToast?.('This file has no stored link. Re-upload it on the job.', 'error');
    return;
  }
  // Opened before the await so the popup blocker allows it; `noopener` can't be
  // passed here (it makes window.open return null), so the opener is cut by hand
  // before the tab is ever navigated.
  const win = row.isPdf ? window.open('', '_blank') : null;
  if (win) { try { win.opener = null; } catch (_e) { /* ignore */ } }
  try {
    const url = await window.StorageFile.blobUrl(fref);
    if (row.isPdf) {
      if (win) win.location = url; else window.open(url, '_blank', 'noopener');
    } else if (window.PhotoLightbox?.show) {
      window.PhotoLightbox.show(url);
    } else {
      window.open(url, '_blank', 'noopener');
    }
  } catch (err) {
    try { win?.close(); } catch (_e) { /* ignore */ }
    console.error('[Projects] Document open failed:', err && (err.sfReason || err.code));
    window.showToast?.(err && err.sfReason === 'denied' ? "You don't have access to that file" : 'Could not open that file', 'error');
  }
}

// ── TASK-9650 — Project library ("+ Add Doc", folders, "Use on jobs") ──────
// projects/{pid}/documents/{docId} = { name, category (flat folder, HR-style),
//   kind (site_map | msa | sds | other), fileName, mimeType, sizeBytes,
//   uploadedBy, uploadedByName, uploadedAt, useOnJobs: bool }.
// The FILE is ONE storage object per doc at a path DERIVED from ids
// (projectLibPath) — no storagePath field, so nothing stored can point a reader
// at another file. Rules: owner or the 'projects' module (firestore + storage);
// the file is also readable by any tenant session while useOnJobs is true, so a
// driver opens it from a job's projectDocs refs [{projectId, docId, name,
// mimeType}]. 811 tickets and load permits are NOT library kinds (they expire;
// they stay in the 811 flow / job.permits[]). A file is never replaced in place.
const PROJECT_LIB_KINDS = [
  { key: 'site_map', label: 'Site map' },
  { key: 'msa', label: 'MSA' },
  { key: 'sds', label: 'SDS' },
  { key: 'other', label: 'Other' },
];
const PROJECT_LIB_MAX_BYTES = 10 * 1024 * 1024; // storage.rules: < 10 MB
const PROJECT_LIB_DEFAULT_FOLDER = 'General';
const _PROJECT_LIB_ID = /^[A-Za-z0-9_-]{1,128}$/;

// Same shape as projectDocsCanSeeTicketDocs, keyed on 'projects' (TASK-9651):
// owner via ownerId, else a live member holding the module. Demo has no
// member doc, so the rules would refuse — no library there.
function projectLibAccess() {
  const S = window.AppState || {};
  if (S.isDemo === true || S.tenant === 'demo') return { see: false, edit: false };
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return { see: true, edit: true };
  const m = S.memberAccess;
  if (!m || ['suspended', 'removed', 'revoked', 'inactive', 'pending'].includes(m.status)) return { see: false, edit: false };
  const has = Array.isArray(m.modules) && m.modules.includes('projects');
  return { see: has, edit: has && m.viewUploadOnly !== true };
}

// Pure. '' for anything that is not a plain id (never builds a path from junk).
function projectLibPath(tenantId, projectId, docId) {
  return [tenantId, projectId, docId].every(s => typeof s === 'string' && _PROJECT_LIB_ID.test(s))
    ? `tenants/${tenantId}/project-docs/${projectId}/${docId}` : '';
}
function _projectLibFolderOf(d) {
  const c = d && typeof d.category === 'string' ? d.category.trim() : '';
  return c || PROJECT_LIB_DEFAULT_FOLDER;
}
function _projectLibKindLabel(k) {
  return (PROJECT_LIB_KINDS.find(x => x.key === k) || PROJECT_LIB_KINDS[3]).label;
}
function _projectLibIsPdf(d) {
  return !!d && (d.mimeType === 'application/pdf' || /\.pdf$/i.test(String(d.fileName || d.name || '')));
}
// Pure. Folder chips: [{ name, n }] by name, case-insensitive.
function _projectLibFolders(docs) {
  const by = new Map();
  for (const d of (docs || [])) {
    if (!d) continue;
    const f = _projectLibFolderOf(d);
    const k = f.toLowerCase();
    if (!by.has(k)) by.set(k, { name: f, n: 0 });
    by.get(k).n++;
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
}
// Pure. [[doc, index]] for the folder + search filter (index into `docs`).
function _projectLibMatch(docs, f) {
  const folder = String((f && f.folder) || '').toLowerCase();
  const words = String((f && f.q) || '').toLowerCase().split(/\s+/).filter(Boolean);
  const out = [];
  (docs || []).forEach((d, i) => {
    if (!d) return;
    if (folder && _projectLibFolderOf(d).toLowerCase() !== folder) return;
    if (words.length) {
      const hay = [d.name, d.fileName, _projectLibFolderOf(d), _projectLibKindLabel(d.kind)]
        .filter(v => typeof v === 'string').join(' ').toLowerCase();
      if (!words.every(w => hay.includes(w))) return;
    }
    out.push([d, i]);
  });
  return out;
}
// Pure. docId -> number of the given jobs that reference it.
function _projectLibUsage(jobs, projectId) {
  const n = new Map();
  for (const j of (jobs || [])) {
    for (const r of (Array.isArray(j && j.projectDocs) ? j.projectDocs : [])) {
      if (r && r.projectId === projectId && typeof r.docId === 'string') n.set(r.docId, (n.get(r.docId) || 0) + 1);
    }
  }
  return n;
}
function _projectLibFilterFor(projectId) {
  if (!ProjectState.libFilter || ProjectState.libFilterFor !== projectId) {
    ProjectState.libFilter = { folder: '', q: '' };
    ProjectState.libFilterFor = projectId;
  }
  return ProjectState.libFilter;
}
function _projectLibSort(a, b) {
  return _projectLibFolderOf(a).localeCompare(_projectLibFolderOf(b)) || String(a.name || '').localeCompare(String(b.name || ''));
}

// ProjectState.lib = { for, docs, failed, off }. Read on Docs tab open only.
async function loadProjectLibrary(projectId) {
  const acc = projectLibAccess();
  if (!acc.see || !ProjectState.tenantId || !projectId) {
    ProjectState.lib = { for: projectId, docs: [], failed: false, off: true };
    return ProjectState.lib;
  }
  try {
    const snap = await getDocs(collection(db, 'tenants', ProjectState.tenantId, 'projects', projectId, 'documents'));
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // TASK-9652 — a customer upload is NOT a library doc until it is accepted:
    // pending ones go to the review list, rejected ones stay out of both.
    const docs = all.filter(d => !_isUnacceptedCustomerUpload(d)).sort(_projectLibSort);
    // 'accepting' = a reviewer's accept is mid-flight (or crashed; the server lets it be re-taken after 2 min).
    const customer = all.filter(d => d.source === 'customer' && (d.status === 'pending_review' || d.status === 'accepting'))
      .sort((a, b) => _tsMs(b.uploadedAt) - _tsMs(a.uploadedAt));
    // TASK-9652 S2 — what was turned down (the file is gone; the record keeps
    // who / when / why so the office can answer "did you get my document?").
    const rejected = all.filter(d => d.source === 'customer' && d.status === 'rejected')
      .sort((a, b) => (_tsMs(b.reviewedAt) || _tsMs(b.uploadedAt)) - (_tsMs(a.reviewedAt) || _tsMs(a.uploadedAt)));
    ProjectState.lib = { for: projectId, docs, customer, rejected, failed: false };
  } catch (err) {
    console.warn('[Projects] library load failed:', err && err.code);
    ProjectState.lib = { for: projectId, docs: [], customer: [], rejected: [], failed: true };
  }
  return ProjectState.lib;
}

// ── TASK-9652 — customer uploads from the project portal ──────────────────
// The portal writes projects/{pid}/documents/{id} server-side with
// source:'customer', status:'pending_review', useOnJobs:false; the file sits in
// quarantine at tenants/{t}/portal-uploads/{pid}/{id} (readable by the same
// owner / projects-module people as the library, never a driver). Accept and
// Reject go through the reviewCustomerUpload callable ONLY — it moves the file
// into the library path and records who decided. Until then the upload is
// invisible to the library, the job form and drivers.
function _isUnacceptedCustomerUpload(d) {
  return !!d && d.source === 'customer' && d.status !== 'accepted';
}
function _tsMs(v) {
  if (!v) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return 0;
}
function _custUploadPath(tenantId, projectId, docId) {
  return [tenantId, projectId, docId].every(s => typeof s === 'string' && _PROJECT_LIB_ID.test(s))
    ? `tenants/${tenantId}/portal-uploads/${projectId}/${docId}` : '';
}
function _fmtBytes(n) {
  const b = Number(n) || 0;
  return b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
}
// TASK-9652 S2 — canned reject reasons. MIRROR of REJECT_REASONS in
// functions/src/services/portalCustomerUploads.js (a browser module cannot
// require it; tests/task9652-portal-uploads.unit.cjs asserts the two match).
// Chips fill the reason box; the text stays editable and the server still cleans it.
const CUSTOMER_UPLOAD_REJECT_REASONS = Object.freeze([
  "Can't read it. Please send a clearer photo or scan.",
  'Wrong project or site. Please send it on the right project.',
  "This isn't the document we asked for.",
  'Pages or a signature are missing. Please send the complete document.',
  'We already have this one.',
]);
// TASK-9652 S2 — does any active person hold the tag the upload notice, the
// reminder and (fix/1018) the customer change-request notice go to? CRM's
// renderOrderNotificationWarning test, plus what the server's resolvers skip
// anyway (COD r1 Code Reviewer): muted people, and people with neither an
// email nor a phone, cannot receive anything.
function customerDocsRecipientsExist(people) {
  if (!Array.isArray(people) || !people.length) return true; // no roster loaded (yet) = unknown, not "nobody" — say nothing
  return people.some(p => {
    if (!p || p.active !== true) return false; // the server's resolvers query active == true (COD r3)
    if (p.notifyMuted || (p.notifications && p.notifications.muted)) return false;
    if (!p.email && !p.phone) return false;
    const tags = (p.notifications && Array.isArray(p.notifications.tags) && p.notifications.tags.length ? p.notifications.tags : p.notifyTags) || [];
    return tags.includes('ALL') || tags.includes('CUSTOMER_DOCS');
  });
}
// S2 — the banner's "Open Roster" (COD r1 UX): closes the Edit Project modal
// if it is up, then the app's own view switch (window handle, never an import).
function openRosterForCustomerDocs() {
  document.getElementById('modal-edit-project')?.classList.add('hidden');
  if (typeof window.navigateToView === 'function') window.navigateToView('roster');
  else window.showToast?.('Open Roster → Edit Person → Notifications', 'info');
}
// `what` picks the sentence for the surface: 'uploads' (Docs tab), 'requests'
// (Changes tab) or both (Edit Project). Same tag, same fix, same button.
const _CUSTOMER_DOCS_NOBODY_COPY = Object.freeze({
  uploads: ['Nobody will be notified when a customer uploads a document.', 'Uploads still land on this Docs tab; nobody is told.'],
  requests: ['Nobody will be notified when a customer sends a change request.', 'Requests still land on this Changes tab; nobody is told.'],
  both: ['Nobody will be notified when a customer uploads a document or sends a change request.', 'Uploads and requests still land on the project; nobody is told.'],
});
function _customerDocsNobodyWarning(margin, what) {
  const [headline, tail] = _CUSTOMER_DOCS_NOBODY_COPY[what] || _CUSTOMER_DOCS_NOBODY_COPY.both;
  return `
    <div class="pcu-nobody-warning" style="margin:${margin || '8px 0 0'};padding:8px 10px;background:#FEF3C7;border:1px solid #F59E0B40;border-radius:var(--radius-sm,6px);display:flex;align-items:flex-start;gap:8px;">
      <span class="material-icons" aria-hidden="true" style="font-size:18px;color:#D97706;flex-shrink:0;margin-top:1px;">warning</span>
      <div style="font-size:0.8rem;line-height:1.4;">
        <strong style="color:#92400E;">${headline}</strong>
        <div style="color:#78350F;margin-top:2px;">Go to <strong>Roster → Edit Person → Notifications</strong> and turn on the <strong>"Customer documents &amp; requests"</strong> tag for at least one person. ${tail}</div>
        <button type="button" class="btn btn-secondary btn-sm" style="min-height:36px;margin-top:6px;" onclick="window.ProjectsModule.openRosterForCustomerDocs()">Open Roster</button>
      </div>
    </div>`;
}
// TASK-9652 S2 — the Projects nav badge's share: pending customer uploads
// across every project this login can see (customerUploadsPending is kept on
// the project doc by the server inside the filing / deciding transaction).
function getPendingCustomerUploadCount() {
  let n = 0;
  for (const p of (ProjectState.projects || [])) {
    const v = p && p.customerUploadsPending;
    if (Number.isFinite(v) && v > 0) n += Math.floor(v);
  }
  return n;
}
function renderCustomerUploadsSection(project) {
  const lib = ProjectState.lib;
  if (!project || !lib || lib.for !== project.id || lib.off || lib.failed) return '';
  const list = lib.customer || [];
  return (list.length ? _renderCustomerPendingCard(list) : '') + _renderCustomerRejectedList(lib.rejected || []);
}
// S2 — what was turned down, collapsed. Open does nothing here: the file was
// deleted at reject (the customer sees the reason on the portal; the record is
// the office's answer to "did you get my document?").
function _renderCustomerRejectedList(list) {
  if (!list.length) return '';
  const SHOW = 20;
  const rows = list.slice(0, SHOW).map(d => `
        <div style="display:flex;flex-direction:column;gap:2px;padding:8px 10px;background:var(--bg-elevated,#fff);border:1px solid var(--border-light);border-radius:8px;">
          <div style="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;">
            <span style="font-weight:600;overflow-wrap:anywhere;">${escHtml(d.name || 'Customer document')}</span>
            <span style="font-size:0.8rem;color:var(--text-secondary);">${escHtml([d.uploadedByName || 'Customer', d.uploadedAt ? `sent ${formatDateShort(d.uploadedAt)}` : ''].filter(Boolean).join(' · '))}</span>
          </div>
          <div style="font-size:0.8rem;color:var(--text-primary);overflow-wrap:anywhere;"><strong>Reason:</strong> ${escHtml(d.rejectReason || '')}</div>
          <div style="font-size:0.8rem;color:var(--text-secondary);">${escHtml(['Rejected', d.reviewedByName ? `by ${d.reviewedByName}` : '', d.reviewedAt ? formatDateShort(d.reviewedAt) : ''].filter(Boolean).join(' '))}</div>
        </div>`).join('');
  return `
    <details class="project-cust-rejected" id="project-cust-rejected" style="margin-bottom:18px;">
      <summary style="cursor:pointer;min-height:44px;display:flex;align-items:center;gap:8px;font-size:0.9rem;color:var(--text-secondary);">
        <span class="material-icons" aria-hidden="true" style="font-size:18px;">block</span>
        Rejected customer uploads
        <span class="hr-docs-category-chip-count">${list.length}</span>
      </summary>
      <div style="display:flex;flex-direction:column;gap:6px;margin-top:6px;">${rows}
        ${list.length > SHOW ? `<div style="font-size:0.8rem;color:var(--text-secondary);padding:4px 10px;">and ${list.length - SHOW} more</div>` : ''}
      </div>
    </details>`;
}
function _renderCustomerPendingCard(list) {
  const canEdit = projectLibAccess().edit;
  return `
    <div class="project-cust-uploads" id="project-cust-uploads" style="margin-bottom:18px;padding:12px;border:1px solid #FDE68A;background:#FFFBEB;border-radius:var(--radius-md,8px);">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;flex-wrap:wrap;">
        <span class="material-icons" aria-hidden="true" style="font-size:20px;color:#92400E;">upload_file</span>
        <span style="font-weight:600;font-size:0.95rem;color:var(--text-primary);">From the customer: waiting for review</span>
        <span class="hr-docs-category-chip-count">${list.length}</span>
      </div>
      <div style="font-size:0.8rem;color:var(--text-primary);margin-bottom:10px;">Uploaded on the customer portal. Open each file before you accept it. Accepted files go to the library (folder "From customer"), off jobs until you turn "Use on jobs" on. A rejected file is deleted and the customer sees your reason.</div>
      ${customerDocsRecipientsExist(getPeople()) ? '' : _customerDocsNobodyWarning('0 0 10px', 'uploads')}
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${list.map((d, i) => `
        <div class="project-cust-upload-row" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;min-height:56px;padding:8px 10px;background:var(--bg-elevated,#fff);border:1px solid var(--border-light);border-radius:8px;">
          <button type="button" onclick="window.ProjectsModule.viewCustomerUpload(${i})" style="display:flex;flex-direction:column;align-items:flex-start;flex:1 1 200px;min-width:0;min-height:44px;padding:0;text-align:left;font:inherit;color:var(--text-primary);background:none;border:0;cursor:pointer;">
            <span style="font-weight:600;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml(d.name || 'Customer document')}</span>
            <span style="font-size:0.8rem;color:var(--text-secondary);">${escHtml([d.uploadedByName || 'Customer', _projectLibIsPdf(d) ? 'PDF' : 'Photo', _fmtBytes(d.sizeBytes), d.uploadedAt ? formatDateShort(d.uploadedAt) : ''].filter(Boolean).join(' · '))}</span>
          </button>
          <button type="button" class="btn btn-secondary btn-sm" style="min-height:44px;" onclick="window.ProjectsModule.viewCustomerUpload(${i})">Open</button>
          ${canEdit ? `
          <button type="button" class="btn btn-primary btn-sm" style="min-height:44px;" onclick="window.ProjectsModule.reviewCustomerUpload(${i}, 'accept', this)">Accept</button>
          <button type="button" class="btn btn-secondary btn-sm" style="min-height:44px;color:#B91C1C;" onclick="window.ProjectsModule.showRejectCustomerUpload(${i})">Reject</button>` : ''}
        </div>`).join('')}
      </div>
    </div>`;
}
async function viewCustomerUpload(idx) {
  const cur = _projectLibCurrent();
  const d = cur && (cur.lib.customer || [])[Number(idx)];
  if (!d) return;
  const path = _custUploadPath(ProjectState.tenantId || '', cur.project.id, d.id);
  if (!path) { window.showToast?.('This file has no stored link', 'error'); return; }
  const pdf = _projectLibIsPdf(d);
  const win = pdf ? window.open('', '_blank') : null;
  if (win) { try { win.opener = null; } catch (_e) { /* ignore */ } }
  try {
    const url = await getDownloadURL(ref(storage, path));
    if (pdf) { if (win) win.location = url; else window.open(url, '_blank', 'noopener'); }
    else if (window.PhotoLightbox?.show) window.PhotoLightbox.show(url);
    else window.open(url, '_blank', 'noopener');
  } catch (err) {
    try { win?.close(); } catch (_e) { /* ignore */ }
    console.error('[Projects] customer upload open failed:', err && err.code);
    window.showToast?.(err && err.code === 'storage/object-not-found' ? 'That file is gone. Ask the customer to send it again.' : 'Could not open that file', 'error');
  }
}
async function reviewCustomerUpload(idx, decision, btn, reason) {
  const cur = _projectLibCurrent();
  const d = cur && (cur.lib.customer || [])[Number(idx)];
  if (!d || !projectLibAccess().edit) return false;
  if (btn && btn.disabled) return false;
  if (btn) btn.disabled = true;
  try {
    await httpsCallable(functions, 'reviewCustomerUpload')({ projectId: cur.project.id, docId: d.id, decision, reason: reason || '' });
    cur.lib.customer = cur.lib.customer.filter(x => x !== d);
    if (decision === 'accept') {
      cur.lib.docs.push({ ...d, status: 'accepted', useOnJobs: false });
      cur.lib.docs.sort(_projectLibSort);
    }
    _projectUseOnJobsCache.delete(cur.project.id);
    renderProjectsView();
    window.showToast?.(decision === 'accept' ? 'Accepted. It is in the library under "From customer". Turn on "Use on jobs" if crews need it' : 'Rejected. The customer will see your reason', 'success');
    return true;
  } catch (err) {
    console.error('[Projects] customer upload review failed:', err && err.code);
    window.showToast?.((err && err.message && err.code !== 'functions/internal') ? String(err.message).replace(/^FirebaseError:\s*/, '') : 'Could not save that decision', 'error');
    if (btn) btn.disabled = false;
    return false;
  }
}
function showRejectCustomerUpload(idx) {
  const cur = _projectLibCurrent();
  const d = cur && (cur.lib.customer || [])[Number(idx)];
  if (!d || !projectLibAccess().edit) return;
  let modal = document.getElementById('modal-project-cust-reject');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-project-cust-reject';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `
    <div class="modal-content modal-small" style="text-align:left;">
      <div class="modal-header">
        <h3>Reject document</h3>
        <button type="button" class="btn-icon" aria-label="Close" onclick="document.getElementById('modal-project-cust-reject').classList.add('hidden')"><span class="material-icons">close</span></button>
      </div>
      <div style="font-size:0.9rem;margin-bottom:8px;overflow-wrap:anywhere;">${escHtml(d.name || 'Customer document')}</div>
      <div class="form-group">
        <label for="pcu-reject-reason">Reason (the customer sees this) *</label>
        <!-- TASK-9652 S2 — canned reasons: tap one to fill the box, then edit if you like -->
        <div style="display:flex;flex-wrap:wrap;gap:6px;margin:0 0 8px;">
          ${CUSTOMER_UPLOAD_REJECT_REASONS.map((r, k) => `<button type="button" class="btn btn-secondary btn-sm" style="min-height:44px;font-size:0.78rem;white-space:normal;text-align:left;" onclick="window.ProjectsModule.applyRejectReason(${k})">${escHtml(r)}</button>`).join('')}
        </div>
        <textarea id="pcu-reject-reason" maxlength="300" rows="3" style="width:100%;" placeholder="e.g. Wrong pad. Please send the Pad 7 site map."></textarea>
      </div>
      <div class="form-actions" style="flex-wrap:wrap;">
        <button type="button" class="btn btn-secondary" style="min-height:44px;" onclick="document.getElementById('modal-project-cust-reject').classList.add('hidden')">Cancel</button>
        <button type="button" class="btn btn-primary" id="pcu-reject-save" style="min-height:44px;background:#B91C1C;border-color:#B91C1C;" onclick="window.ProjectsModule.submitRejectCustomerUpload(${Number(idx)}, this)">Reject</button>
      </div>
    </div>`;
  modal.classList.remove('hidden');
  setTimeout(() => document.getElementById('pcu-reject-reason')?.focus(), 0);
}
// S2 — a canned-reason chip: fill the box (replacing what is there) and focus it.
function applyRejectReason(k) {
  const r = CUSTOMER_UPLOAD_REJECT_REASONS[Number(k)];
  const ta = document.getElementById('pcu-reject-reason');
  if (!r || !ta) return;
  ta.value = r;
  ta.focus();
}
async function submitRejectCustomerUpload(idx, btn) {
  const reason = String(document.getElementById('pcu-reject-reason')?.value || '').trim().slice(0, 300);
  if (!reason) { window.showToast?.('Tell the customer why', 'error'); return; }
  const ok = await reviewCustomerUpload(idx, 'reject', btn, reason);
  if (ok) document.getElementById('modal-project-cust-reject')?.classList.add('hidden');
}

function _projectLibChipsHtml(docs, f) {
  const chip = (key, label, n) => {
    const active = (f.folder || '') === key;
    return `<button type="button" class="hr-docs-category-chip${active ? ' hr-docs-category-chip-active' : ''}" aria-pressed="${active}" style="min-height:44px;"
        data-lib-folder="${escAttr(escHtml(key))}" onclick="window.ProjectsModule.setProjectLibFilter('folder', this.getAttribute('data-lib-folder'))">${escHtml(label)} <span class="hr-docs-category-chip-count">${n}</span></button>`;
  };
  return chip('', 'All', docs.length) + _projectLibFolders(docs).map(x => chip(x.name.toLowerCase(), x.name, x.n)).join('');
}
function _projectLibRowHtml(d, i, canEdit, usedOn) {
  const pdf = _projectLibIsPdf(d);
  const color = pdf ? '#DC2626' : '#2563EB';
  const sub = [_projectLibFolderOf(d), _projectLibKindLabel(d.kind), d.uploadedAt ? formatDateShort(d.uploadedAt) : '',
    usedOn ? `On ${usedOn} job${usedOn === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ');
  const on = d.useOnJobs === true;
  return `
        <div class="project-lib-row" data-lib-idx="${i}" style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-height:56px;padding:8px 12px;background:var(--bg-elevated,#fff);border:1px solid var(--border-light);border-radius:var(--radius-md,8px);">
          <button type="button" onclick="window.ProjectsModule.openProjectLibDoc(${i})" title="${escAttr(escHtml(d.name || 'Document'))}"
            style="display:flex;align-items:center;gap:12px;flex:1 1 200px;min-width:0;min-height:44px;padding:0;text-align:left;font:inherit;color:var(--text-primary);background:none;border:0;cursor:pointer;">
            <span style="flex-shrink:0;width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;background:${color}14;">
              <span class="material-icons" aria-hidden="true" style="font-size:22px;color:${color};">${pdf ? 'picture_as_pdf' : 'image'}</span>
            </span>
            <span style="display:flex;flex-direction:column;min-width:0;flex:1;">
              <span class="project-lib-name" style="font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml(d.name || d.fileName || 'Document')}</span>
              <span style="font-size:0.8rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml(sub)}</span>
            </span>
            <span style="flex-shrink:0;font-size:0.85rem;font-weight:600;color:var(--brand-blue);">View</span>
          </button>
          <label class="project-lib-usetoggle" style="display:inline-flex;align-items:center;gap:6px;min-height:44px;padding:0 10px;border-radius:999px;font-size:0.82rem;font-weight:600;cursor:${canEdit ? 'pointer' : 'default'};background:${on ? '#DCFCE7' : 'var(--bg-page,#F1F5F9)'};color:${on ? '#166534' : 'var(--text-primary)'};">
            <input type="checkbox" ${on ? 'checked' : ''} ${canEdit ? '' : 'disabled'} style="width:18px;height:18px;margin:0;"
              onchange="window.ProjectsModule.setProjectLibUseOnJobs(${i}, this.checked, this)"> Use on jobs
          </label>
          ${canEdit ? `<button type="button" class="btn-icon" style="min-width:44px;min-height:44px;" aria-label="Edit ${escAttr(escHtml(d.name || 'document'))}" title="Edit" onclick="window.ProjectsModule.showProjectLibDocModal(${i})"><span class="material-icons" aria-hidden="true">edit</span></button>` : ''}
        </div>`;
}
function _projectLibListHtml(docs, f, canEdit, usage) {
  const matched = _projectLibMatch(docs, f);
  if (!matched.length) {
    const filtered = !!(f.q || f.folder);
    return `<div style="text-align:center;padding:20px 12px;color:var(--text-secondary);font-size:0.9rem;">
        ${filtered ? 'No library documents match.' : 'No project documents yet. Add the site maps, MSAs or SDS sheets crews need on this project&#39;s jobs.'}
        ${filtered ? `<div><button type="button" class="btn btn-secondary btn-sm" style="min-height:44px;margin-top:10px;" onclick="window.ProjectsModule.setProjectLibFilter('reset')">Clear filters</button></div>` : ''}
      </div>`;
  }
  return `<div style="display:flex;flex-direction:column;gap:6px;">${matched.map(([d, i]) => _projectLibRowHtml(d, i, canEdit, (usage && usage.get(d.id)) || 0)).join('')}</div>`;
}

// The "Project library" area (above "From jobs"). '' until this project's
// library has been read, and for a login without the projects module.
function renderProjectLibSection(project, jobs) {
  const lib = ProjectState.lib;
  if (!project || !lib || lib.for !== project.id || lib.off) return '';
  const acc = projectLibAccess();
  ProjectState.libUsage = _projectLibUsage(jobs, project.id);
  if (lib.failed) {
    return `<div class="project-lib" id="project-lib" style="padding:10px 12px;margin-bottom:14px;border:1px solid #FDE68A;background:#FFFBEB;color:#92400E;border-radius:8px;font-size:0.85rem;">
        Couldn't load the project library. Reload to try again.</div>`;
  }
  const docs = lib.docs || [];
  const f = _projectLibFilterFor(project.id);
  return renderCustomerUploadsSection(project) + `
    <div class="project-lib" id="project-lib" style="margin-bottom:18px;">
      <div style="display:flex;align-items:center;gap:8px;margin:4px 0 10px;flex-wrap:wrap;">
        <span style="font-weight:600;font-size:0.95rem;color:var(--text-primary);">Project library</span>
        <span class="hr-docs-category-chip-count" id="project-lib-count">${docs.length}</span>
        ${acc.edit ? `<button type="button" class="btn btn-primary btn-sm" id="btn-project-add-doc" style="margin-left:auto;min-height:44px;" onclick="window.ProjectsModule.showProjectLibDocModal()">
          <span class="material-icons" aria-hidden="true" style="font-size:18px;vertical-align:-4px;">add</span> Add Doc</button>` : ''}
      </div>
      <div class="hr-docs-category-strip" id="project-lib-chips" role="toolbar" aria-label="Library folder">${docs.length ? _projectLibChipsHtml(docs, f) : ''}</div>
      ${docs.length ? `
      <input type="search" id="project-lib-search" class="form-control" placeholder="Search library" aria-label="Search project library"
        value="${escAttr(escHtml(f.q))}" autocomplete="off" style="width:100%;min-height:44px;margin-bottom:10px;"
        oninput="window.ProjectsModule.setProjectLibFilter('q', this.value)">` : ''}
      <div id="project-lib-list">${_projectLibListHtml(docs, f, acc.edit, ProjectState.libUsage)}</div>
    </div>`;
}

function _projectLibCurrent() {
  const project = ProjectState.currentProject;
  const lib = ProjectState.lib;
  return project && lib && lib.for === project.id && !lib.off && !lib.failed ? { project, lib } : null;
}
// Repaints count, chips and list (the search box keeps focus). The search box
// only exists once there are docs, so the first add re-renders the tab.
function _repaintProjectLib() {
  const cur = _projectLibCurrent();
  if (!cur) return;
  const docs = cur.lib.docs || [];
  if (docs.length && !document.getElementById('project-lib-search')) { renderProjectsView(); return; }
  const f = _projectLibFilterFor(cur.project.id);
  if (f.folder && !_projectLibFolders(docs).some(x => x.name.toLowerCase() === f.folder)) f.folder = '';
  const chips = document.getElementById('project-lib-chips');
  if (chips) chips.innerHTML = docs.length ? _projectLibChipsHtml(docs, f) : '';
  const count = document.getElementById('project-lib-count');
  if (count) count.textContent = String(docs.length);
  const list = document.getElementById('project-lib-list');
  if (list) list.innerHTML = _projectLibListHtml(docs, f, projectLibAccess().edit, ProjectState.libUsage);
}
function setProjectLibFilter(key, value) {
  const cur = _projectLibCurrent();
  if (!cur) return;
  const f = _projectLibFilterFor(cur.project.id);
  if (key === 'reset') {
    f.folder = ''; f.q = '';
    const s = document.getElementById('project-lib-search'); if (s) s.value = '';
  } else if (key === 'folder') f.folder = String(value || '').toLowerCase();
  else if (key === 'q') f.q = String(value || '').slice(0, 120);
  else return;
  _repaintProjectLib();
}

async function setProjectLibUseOnJobs(idx, checked, el) {
  const cur = _projectLibCurrent();
  const d = cur && cur.lib.docs[Number(idx)];
  if (!d || !projectLibAccess().edit) { if (el) el.checked = !checked; return; }
  const want = checked === true;
  // COD Domain — crews lose a doc mid-shift without warning; say so first.
  const used = (ProjectState.libUsage && ProjectState.libUsage.get(d.id)) || 0;
  if (!want && used && !window.confirm(`"${d.name || 'This document'}" is on ${used} job${used === 1 ? '' : 's'}. Crews will no longer be able to open it there. Turn off?`)) {
    if (el) el.checked = true;
    return;
  }
  try {
    await updateDoc(doc(db, 'tenants', ProjectState.tenantId, 'projects', cur.project.id, 'documents', d.id), { useOnJobs: want });
    d.useOnJobs = want;
    _projectUseOnJobsCache.delete(cur.project.id);
    _repaintProjectLib();
    window.showToast?.(want ? 'Dispatchers can add it to jobs; crews on those jobs can open it' : 'Off jobs. Crews can no longer open it from a job', 'success');
  } catch (err) {
    console.error('[Projects] Use on jobs failed:', err && err.code);
    if (el) el.checked = !want;
    window.showToast?.('Could not change "Use on jobs"', 'error');
  }
}

// Opens a library file by ids. Shared by the library rows and the job form's
// project-doc chips (window.ProjectsModule.openProjectDocRef).
async function openProjectDocRef(r) {
  const path = projectLibPath(ProjectState.tenantId || '', r && r.projectId, r && r.docId);
  if (!path) { window.showToast?.('This document has no stored link', 'error'); return; }
  const pdf = _projectLibIsPdf(r);
  const win = pdf ? window.open('', '_blank') : null;
  if (win) { try { win.opener = null; } catch (_e) { /* ignore */ } }
  try {
    const url = await getDownloadURL(ref(storage, path));
    if (pdf) { if (win) win.location = url; else window.open(url, '_blank', 'noopener'); }
    else if (window.PhotoLightbox?.show) window.PhotoLightbox.show(url);
    else window.open(url, '_blank', 'noopener');
  } catch (err) {
    try { win?.close(); } catch (_e) { /* ignore */ }
    console.error('[Projects] library open failed:', err && err.code);
    window.showToast?.(err && err.code === 'storage/unauthorized'
      ? 'This document is no longer shared to jobs' : 'Could not open that file', 'error');
  }
}
function openProjectLibDoc(idx) {
  const cur = _projectLibCurrent();
  const d = cur && cur.lib.docs[Number(idx)];
  if (!d) return;
  return openProjectDocRef({ projectId: cur.project.id, docId: d.id, mimeType: d.mimeType, fileName: d.fileName, name: d.name });
}

function closeProjectLibModal() {
  document.getElementById('modal-project-lib-doc')?.classList.add('hidden');
}
// File picked → prefill an empty Name from the file name (no extension).
function projectLibFilePicked(input) {
  const n = document.getElementById('plib-name');
  const file = input && input.files && input.files[0];
  if (n && !n.value && file) n.value = String(file.name || '').replace(/\.[^.]+$/, '').slice(0, 120);
}
// Add (no idx) or Edit (idx). Edit changes the record, never the file.
function showProjectLibDocModal(idx) {
  const cur = _projectLibCurrent();
  if (!cur || !projectLibAccess().edit) return;
  const editing = idx !== undefined && idx !== null;
  const d = editing ? cur.lib.docs[Number(idx)] : null;
  if (editing && !d) return;
  let modal = document.getElementById('modal-project-lib-doc');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-project-lib-doc';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  const folders = _projectLibFolders(cur.lib.docs).map(x => `<option value="${escAttr(escHtml(x.name))}"></option>`).join('');
  const kind = (d && d.kind) || 'site_map';
  modal.innerHTML = `
    <div class="modal-content modal-small" style="text-align:left;">
      <div class="modal-header">
        <h3>${editing ? 'Edit document' : 'Add document'}</h3>
        <button type="button" class="btn-icon" aria-label="Close" onclick="window.ProjectsModule.closeProjectLibModal()"><span class="material-icons">close</span></button>
      </div>
      ${editing ? '' : `
      <div class="form-group">
        <label for="plib-file">File (PDF or photo, up to 10 MB) *</label>
        <input type="file" id="plib-file" accept="application/pdf,image/*" style="min-height:44px;" onchange="window.ProjectsModule.projectLibFilePicked(this)">
      </div>`}
      <div class="form-group">
        <label for="plib-name">Name *</label>
        <input type="text" id="plib-name" maxlength="120" value="${escAttr(escHtml((d && d.name) || ''))}" placeholder="e.g. Pad 7 site map">
      </div>
      <div class="form-row" style="gap:8px;flex-wrap:wrap;">
        <div class="form-group flex-1" style="min-width:160px;">
          <label for="plib-folder">Folder</label>
          <input type="text" id="plib-folder" maxlength="60" list="plib-folders" value="${escAttr(escHtml((d && d.category) || ''))}" placeholder="${PROJECT_LIB_DEFAULT_FOLDER}">
          <datalist id="plib-folders">${folders}</datalist>
        </div>
        <div class="form-group flex-1" style="min-width:140px;">
          <label for="plib-kind">Type</label>
          <select id="plib-kind" style="min-height:44px;">${PROJECT_LIB_KINDS.map(k => `<option value="${k.key}"${k.key === kind ? ' selected' : ''}>${k.label}</option>`).join('')}</select>
        </div>
      </div>
      <label style="display:flex;align-items:flex-start;gap:8px;min-height:44px;margin-bottom:12px;cursor:pointer;">
        <input type="checkbox" id="plib-use" ${d && d.useOnJobs === true ? 'checked' : ''} style="width:18px;height:18px;margin-top:2px;">
        <span><strong>Use on jobs</strong><br><span style="font-size:0.8rem;color:var(--text-secondary);">Dispatchers can add it to this project's jobs, and crews on those jobs can open it.</span></span>
      </label>
      <div class="form-actions" style="flex-wrap:wrap;">
        ${editing ? `<button type="button" class="btn btn-secondary" style="color:#B91C1C;margin-right:auto;min-height:44px;" onclick="window.ProjectsModule.deleteProjectLibDoc(${Number(idx)})">Delete</button>` : ''}
        <button type="button" class="btn btn-secondary" style="min-height:44px;" onclick="window.ProjectsModule.closeProjectLibModal()">Cancel</button>
        <button type="button" class="btn btn-primary" id="plib-save" style="min-height:44px;" onclick="window.ProjectsModule.submitProjectLibDoc(${editing ? Number(idx) : 'null'})">${editing ? 'Save' : 'Upload'}</button>
      </div>
    </div>`;
  modal.classList.remove('hidden');
}

// Pure. The file checks the storage rule makes (PDF or non-SVG image, < 10 MB).
function _projectLibFileProblem(file) {
  if (!file) return 'Choose a file';
  const t = String(file.type || '').toLowerCase();
  if (!(t === 'application/pdf' || (t.startsWith('image/') && !t.includes('svg')))) return 'PDF or photo only';
  if (!(file.size > 0) || file.size >= PROJECT_LIB_MAX_BYTES) return 'File must be under 10 MB';
  return '';
}

async function submitProjectLibDoc(idx) {
  const cur = _projectLibCurrent();
  if (!cur || !projectLibAccess().edit) return;
  const btn = document.getElementById('plib-save');
  if (btn && btn.disabled) return;
  const name = String(document.getElementById('plib-name')?.value || '').trim().slice(0, 120);
  const category = String(document.getElementById('plib-folder')?.value || '').trim().slice(0, 60);
  const kindVal = document.getElementById('plib-kind')?.value;
  const kind = PROJECT_LIB_KINDS.some(k => k.key === kindVal) ? kindVal : 'other';
  const useOnJobs = document.getElementById('plib-use')?.checked === true;
  if (!name) { window.showToast?.('Name required', 'error'); return; }
  const editing = idx !== undefined && idx !== null;
  const pid = cur.project.id;
  const t = ProjectState.tenantId;
  let file = null;
  if (!editing) {
    file = document.getElementById('plib-file')?.files?.[0] || null;
    const problem = _projectLibFileProblem(file);
    if (problem) { window.showToast?.(problem, 'error'); return; }
  }
  if (btn) { btn.disabled = true; btn.textContent = editing ? 'Saving…' : 'Uploading…'; }
  try {
    if (editing) {
      const d = cur.lib.docs[Number(idx)];
      if (!d) return;
      await updateDoc(doc(db, 'tenants', t, 'projects', pid, 'documents', d.id), { name, category, kind, useOnJobs });
      Object.assign(d, { name, category, kind, useOnJobs });
    } else {
      const docRef = doc(collection(db, 'tenants', t, 'projects', pid, 'documents'));
      const path = projectLibPath(t, pid, docRef.id);
      if (!path) throw new Error('bad path');
      const fileName = String(file.name || 'document').replace(/[^\w.\- ]+/g, '_').slice(0, 120);
      await uploadBytes(ref(storage, path), file, { contentType: file.type, contentDisposition: `inline; filename="${fileName}"` });
      const rec = {
        name, category, kind, useOnJobs, fileName, mimeType: file.type, sizeBytes: file.size,
        uploadedBy: window.AppState?.user?.uid || null,
        uploadedByName: window.AppState?.user?.displayName || window.AppState?.user?.email || '',
        uploadedAt: serverTimestamp(),
      };
      try { await setDoc(docRef, rec); }
      catch (e) { deleteObject(ref(storage, path)).catch(() => {}); throw e; }
      cur.lib.docs.push({ id: docRef.id, ...rec, uploadedAt: new Date() });
    }
    cur.lib.docs.sort(_projectLibSort);
    _projectUseOnJobsCache.delete(pid);
    closeProjectLibModal();
    _repaintProjectLib();
    window.showToast?.(editing ? 'Document saved' : 'Document added', 'success');
  } catch (err) {
    console.error('[Projects] library save failed:', err && err.code);
    window.showToast?.(editing ? 'Could not save the document' : 'Upload failed. Nothing was added.', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = editing ? 'Save' : 'Upload'; }
  }
}

async function deleteProjectLibDoc(idx) {
  const cur = _projectLibCurrent();
  const d = cur && cur.lib.docs[Number(idx)];
  if (!d || !projectLibAccess().edit) return;
  const used = (ProjectState.libUsage && ProjectState.libUsage.get(d.id)) || 0;
  const msg = `Delete "${d.name || 'this document'}"?` + (used ? ` It is on ${used} job${used === 1 ? '' : 's'}; crews will no longer be able to open it there.` : '');
  if (!window.confirm(msg)) return;
  const pid = cur.project.id;
  try {
    // Record first: once it is gone no driver can read the file (the storage
    // rule reads it), so a failed object delete leaves an orphan, never a leak.
    await deleteDoc(doc(db, 'tenants', ProjectState.tenantId, 'projects', pid, 'documents', d.id));
    const path = projectLibPath(ProjectState.tenantId, pid, d.id);
    if (path) deleteObject(ref(storage, path)).catch(e => console.warn('[Projects] library file delete failed:', e && e.code));
    cur.lib.docs = cur.lib.docs.filter(x => x !== d);
    _projectUseOnJobsCache.delete(pid);
    closeProjectLibModal();
    _repaintProjectLib();
    window.showToast?.('Document deleted', 'success');
  } catch (err) {
    console.error('[Projects] library delete failed:', err && err.code);
    window.showToast?.('Could not delete the document', 'error');
  }
}

// For the job form (dispatch.js, via window): the project's "Use on jobs"
// docs as refs, or null when this login cannot read the library (no projects
// module), false when the read failed — the form then shows only what the job carries.
const _projectUseOnJobsCache = new Map(); // projectId -> { at, p }
function getUseOnJobsDocs(projectId) {
  if (!projectId || !ProjectState.tenantId || !projectLibAccess().see) return Promise.resolve(null);
  const hit = _projectUseOnJobsCache.get(projectId);
  if (hit && Date.now() - hit.at < 60000) return hit.p;
  const p = getDocs(query(collection(db, 'tenants', ProjectState.tenantId, 'projects', projectId, 'documents'), where('useOnJobs', '==', true)))
    .then(snap => snap.docs.filter(d => !_isUnacceptedCustomerUpload(d.data())).map(d => {
      const x = d.data() || {};
      return { projectId, docId: d.id, name: String(x.name || x.fileName || 'Document').slice(0, 120), mimeType: x.mimeType || '' };
    }).sort((a, b) => a.name.localeCompare(b.name)))
    .catch(err => { console.warn('[Projects] use-on-jobs read failed:', err && err.code); _projectUseOnJobsCache.delete(projectId); return false; });
  _projectUseOnJobsCache.set(projectId, { at: Date.now(), p });
  return p;
}

// ── TASK-9767 — 811 locate tickets on a PROJECT ────────────────────────────
// A project runs many jobs at one site; the locate ticket belongs to the
// project and every job under it shows it (read-only) next to its own.
//   projects/{id}.permitIds: string[]  THE link — ids only, no copied file.
// Built on TASK-9648 (jobs.permitIds + the picker in dispatch.js, reached via
// window.DispatchModule — Pitfall #1). Deleting a ticket takes it off every
// project too (dispatch.js deletePermit → projectsHoldingPermit /
// unlinkPermitFromProjects below). A rename rewrites nothing: readers re-read
// the name from the permits snapshot.
const MAX_PROJECT_PERMIT_IDS = 20;

// Pure. Strings only, trimmed, no blanks, no repeats, order kept, capped.
function _normProjectPermitIds(ids, cap = MAX_PROJECT_PERMIT_IDS) {
  const out = [];
  for (const raw of (Array.isArray(ids) ? ids : [])) {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (!id || out.includes(id)) continue;
    out.push(id);
    if (out.length >= cap) break;
  }
  return out;
}

// Pure. A project's 811 ticket ids (a project from before TASK-9767 has none).
function projectPermitIdsOf(project) {
  return project ? _normProjectPermitIds(project.permitIds) : [];
}

// For dispatch.js's job readers: the 811 ticket ids of a job's project.
function getProjectPermitIds(projectId) {
  if (!projectId) return [];
  const p = (ProjectState.projects || []).find(x => x && x.id === projectId)
    || (ProjectState.currentProject && ProjectState.currentProject.id === projectId ? ProjectState.currentProject : null);
  return projectPermitIdsOf(p);
}

function _project811Enabled() {
  return window.AppState?.settings?.enable811 === true || isDemoMode();
}

// Who may link/unlink: the same people firestore.rules lets update a project
// AND write a permit — tenant owner (ownerId) or a 'dispatch'-module member.
// A project-role manager (projScopedUpdateOk's allow-list has no permitIds) and
// an 811-only member see the list read-only.
function project811CanEdit() {
  return fieldTotalsCanEdit();
}

function _project811Permits() {
  const snap = window.DispatchModule?.getPermitsSnapshot?.() || {};
  return { loaded: snap.loaded === true, permits: Array.isArray(snap.permits) ? snap.permits : [] };
}

function _project811Date(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(v + 'T12:00:00');
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

// TODO(TASK-9767 × 811-window lane): `permits.expiresOn` is being added by the
// parallel 811-window build. This reads it when present (YYYY-MM-DD string or
// Timestamp) — align the format with that lane at merge.
function project811ExpiryBadge(permit, now = new Date()) {
  // TASK-9862 — renewed tickets are history; dates compare in the COMPANY zone.
  const L = typeof window !== 'undefined' ? window.Locates811 : null;
  if (permit && (permit.status === 'renewed' || permit.renewedById)) return { tone: 'ok', text: 'Renewed' };
  if (L && L.validDateKey(permit && permit.expiresOn)) {
    const tz = ((typeof window !== 'undefined' && window.AppState?.settings) || {}).timezone || 'America/Chicago';
    const today = L.todayKey(tz, now);
    const exp = permit.expiresOn;
    const days = Math.round((Date.parse(exp + 'T00:00:00Z') - Date.parse(today + 'T00:00:00Z')) / 86400000);
    const label = new Date(exp + 'T12:00:00Z').toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
    // TASK-9922 — expired at the end of the good-through day in the dig SITE's zone.
    // Same answer as the Locates page (one rule); the date-only check without it.
    const expired = typeof L.expiresAt === 'function'
      ? L.effectiveStatus(permit, today, -1, { now: now instanceof Date ? now.getTime() : Date.now(), tz }) === 'expired'
      : days < 0;
    if (expired) return { tone: 'expired', text: 'Expired ' + label };
    if (days <= 3) return { tone: 'soon', text: 'Good through ' + label };
    return { tone: 'ok', text: 'Good through ' + label };
  }
  const d = _project811Date(permit && permit.expiresOn);
  if (!d) return null;
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const days = Math.round((day - today) / 86400000);
  const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (days < 0) return { tone: 'expired', text: 'Expired ' + label };
  if (days <= 3) return { tone: 'soon', text: 'Expires ' + label };
  return { tone: 'ok', text: 'Expires ' + label };
}

// The project's tickets, resolved against the permits snapshot. Once it has
// loaded, an id with no permit doc is a DELETED ticket and is not shown.
function _project811Rows(project) {
  const ids = projectPermitIdsOf(project);
  const { loaded, permits } = _project811Permits();
  const rows = [];
  for (const id of ids) {
    const p = permits.find(x => x && x.id === id);
    if (!p && loaded) continue;
    rows.push({ id, permit: p || null });
  }
  return rows;
}

function _project811SafeUrl(permit) {
  const url = String((permit && permit.pdfUrl) || '');
  return /^https?:\/\//i.test(url) ? url : '';
}

// An id inside a single-quoted JS string inside an HTML attribute.
function _p811JsAttr(s) {
  return typeof window.escJsAttr === 'function' ? window.escJsAttr(s) : escAttr(String(s).replace(/[\\'"]/g, ''));
}

// ── 811 waiting tickets (TASK-9859, Chris 10/14 12:00) ─────────────────────
// A ticket saved with a project NAME that did not exist yet (proposedProjectName)
// or pointing at this project (proposedProjectId) is OFFERED to the project —
// after a create / name-or-customer edit, and in Docs. Never attached without
// the click. Matcher = dispatch.js _p811Similarity (window.DispatchModule).
// Pure: the permits this project should offer.
function _p811WaitMatches(project, permits, sim, min) {
  if (!project || !project.id) return [];
  const custs = new Set([...(Array.isArray(project.customerIds) ? project.customerIds : []), project.primaryCustomerId].filter(Boolean));
  const linked = new Set(Array.isArray(project.permitIds) ? project.permitIds : []);
  // TASK-9862 — the ticket's ONE site field (siteName, else the old
  // pendingJobName / proposedProjectName) is what is matched; renewed tickets
  // are history and never offered.
  const site = (p) => String((p && (p.siteName || p.pendingJobName || p.proposedProjectName)) || '').trim();
  return (permits || []).filter(p => p && p.id && !linked.has(p.id) && p.status !== 'renewed' && !p.renewedById && (p.proposedProjectId === project.id
    || (!p.proposedProjectId && site(p) && (!p.customerId || custs.has(p.customerId))
      && typeof sim === 'function' && sim(site(p), project.name) >= min)));
}
function _p811WaitingFor(project) {
  const DM = window.DispatchModule;
  if (!project || typeof DM?.p811Similarity !== 'function') return [];
  const free = _project811Permits().permits.filter(p => p && !getPermitProjectIds(p.id).some(id => id !== project.id));
  return _p811WaitMatches(project, free, DM.p811Similarity, DM.P811_MATCH_MIN || 0.8);
}
function _p811WaitListHtml(list, prefix) {
  return list.map(p => `
      <label style="display:flex;align-items:center;gap:10px;min-height:48px;padding:6px 10px;border:1px solid var(--border-light,#E2E8F0);border-radius:8px;cursor:pointer;">
        <input type="checkbox" class="${prefix}-cb" value="${escAttr(p.id)}" checked style="width:22px;height:22px;flex-shrink:0;">
        <span style="display:flex;flex-direction:column;min-width:0;">
          <span style="font-weight:600;color:var(--text-primary);">${escHtml(p.name || p.pdfName || '811 ticket')}</span>
          <span style="font-size:0.8rem;color:var(--text-secondary);">${escHtml([(p.siteName || p.pendingJobName || p.proposedProjectName) ? 'Site: ' + (p.siteName || p.pendingJobName || p.proposedProjectName) : 'Suggested for this project', p.ticketNumber ? '#' + p.ticketNumber : '', p.customerName || ''].filter(Boolean).join(' · '))}</span>
        </span>
      </label>`).join('');
}
// The "Attach them?" dialog after a create / name-or-customer edit.
function p811OfferWaiting(projectId, fallback) {
  if (!_project811Enabled() || !project811CanEdit()) return; // TASK-9994: nothing 811 while it is off
  const project = _project811Find(projectId) || (fallback ? { id: projectId, ...fallback } : null);
  const list = _p811WaitingFor(project);
  if (!list.length) return;
  document.getElementById('modal-p811-waiting')?.remove();
  const el = document.createElement('div');
  el.className = 'modal';
  el.id = 'modal-p811-waiting';
  el.innerHTML = `
    <div class="modal-content modal-medium" style="display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;overflow:hidden;">
      <div class="modal-header" style="flex-shrink:0;"><h3><span class="material-icons">assignment</span> 811 tickets waiting</h3></div>
      <div class="modal-body" style="flex:1;min-height:0;overflow-y:auto;">
        <p style="margin:0 0 10px;color:var(--text-primary);">${list.length} locate ticket${list.length === 1 ? ' is' : 's are'} waiting for ${escHtml(project.name || '')}. Attach ${list.length === 1 ? 'it' : 'all'}?</p>
        <div style="display:flex;flex-direction:column;gap:6px;">${_p811WaitListHtml(list, 'p811w')}</div>
      </div>
      <div class="modal-footer" style="flex-shrink:0;display:flex;gap:8px;justify-content:flex-end;">
        <button type="button" class="btn btn-secondary" style="min-height:44px;" onclick="document.getElementById('modal-p811-waiting')?.remove()">Not now</button>
        <button type="button" class="btn btn-primary" id="btn-p811-attach" style="min-height:44px;" onclick="window.ProjectsModule.p811AttachWaiting('${_p811JsAttr(projectId)}', 'p811w')">Attach</button>
      </div>
    </div>`;
  document.body.appendChild(el);
}
// Attach = the project link writer (cap refused, never truncated) + the ticket
// now names THIS project (proposedProjectId set, proposedProjectName cleared).
let _p811AttachBusy = false;
async function p811AttachWaiting(projectId, prefix) {
  if (_p811AttachBusy || !project811CanEdit()) return;
  const ticked = Array.from(document.querySelectorAll(`input.${prefix}-cb:checked`)).map(cb => cb.value).filter(Boolean);
  if (!ticked.length) { window.showToast?.('Tick at least one ticket.', 'info'); return; }
  const project0 = _project811Find(projectId);
  const stillWaiting = new Set(_p811WaitingFor(project0).map(p => p.id));
  const ids = project0 ? ticked.filter(id => stillWaiting.has(id)) : [];
  const skipped = ticked.length - ids.length;
  if (!ids.length) { window.showToast?.('Those tickets changed since this list opened (another project or customer). Nothing was attached.', 'warning', null, 8000); document.getElementById('modal-p811-waiting')?.remove(); _rerenderProjectDocsIfOpen(projectId); return; }
  _p811AttachBusy = true;
  try {
    await _writeProjectPermitIds(projectId, live => [...live, ...ids.filter(id => !live.includes(id))]);
    let failed = 0;
    for (const id of ids) {
      try { await updateDoc(doc(db, 'tenants', ProjectState.tenantId, 'permits', id), { proposedProjectId: projectId }); }
      catch (e) { failed++; console.warn('[Projects] 811 waiting stamp failed', id, e && e.code); }
    }
    const project = _project811Find(projectId);
    const adopt = project ? await _adoptProjectCustomer(project, ids, { live: true }) : {};
    document.getElementById('modal-p811-waiting')?.remove();
    const warn = (failed ? ` ${failed} still show their old project name.` : '')
      + (adopt && adopt.failed ? ` ${adopt.failed} couldn't take the project's customer — set it in Locates.` : '')
      + (skipped ? ` ${skipped} changed since the list opened and ${skipped === 1 ? 'was' : 'were'} skipped.` : '');
    window.showToast?.(`${ids.length} 811 ticket${ids.length === 1 ? '' : 's'} attached to this project.` + warn, warn ? 'warning' : 'success', null, warn ? 8000 : undefined);
    _rerenderProjectDocsIfOpen(projectId);
  } catch (err) {
    console.error('[Projects] attach waiting 811 failed:', err && err.code);
    _project811Toast(err, "Couldn't attach those 811 tickets.");
  } finally { _p811AttachBusy = false; }
}
// ── end 811 waiting tickets ─────────────────────────────────────────────────

// TASK-9887 — "potential" projects (dispatch.js owns them; reached via
// window.DispatchModule, Pitfall #1). A new project like an open potential gets
// ONE confirm; yes = its tickets go on through the same project link writer
// (_writeProjectPermitIds, cap refused) + the customer stamp, and the potential
// is marked used. Never silent. Returns true when the user USED it; false
// (nothing matched, or "Not this one") lets the waiting-tickets offer run.
async function p811OfferPotential(projectId, data) {
  const DM = window.DispatchModule;
  if (typeof DM?.p811PotentialFor !== 'function' || !project811CanEdit()) return false;
  const pot = DM.p811PotentialFor('project', data && data.primaryCustomerId, data && data.name);
  if (!pot) return false;
  const n = pot.ticketIds.length;
  const msg = `Locates has a potential ${pot.kind === 'project' ? 'project' : 'job'} "${pot.name}"${pot.customerName ? ` for ${pot.customerName}` : ''}`
    + (n ? ` with ${n} 811 ticket${n === 1 ? '' : 's'}${pot.ticketNos && pot.ticketNos.length ? ` (#${pot.ticketNos.slice(0, 4).join(', #')}${pot.ticketNos.length > 4 ? ' …' : ''})` : ''}${pot.sites && pot.sites.length ? ` at ${pot.sites.slice(0, 2).join(', ')}` : ''}.` : '.')
    + (pot.leftOff ? ` ${pot.leftOff} expired or other-customer ticket${pot.leftOff === 1 ? ' is' : 's are'} left off.` : '')
    + (pot.notes ? ` Notes: ${pot.notes.slice(0, 160)}` : '')
    + (n ? ' Attach them to this project?' : ' Mark it as this project?');
  const yes = typeof window.showConfirm === 'function'
    ? !!(await window.showConfirm('Use the potential from Locates?', msg, { confirmText: n ? `Attach ${n}` : 'Use it', cancelText: 'Not this one' }))
    : confirm(msg);
  if (!yes) return false; // COD: declining falls through to the waiting-tickets offer
  try {
    if (n) {
      await _writeProjectPermitIds(projectId, live => [...live, ...pot.ticketIds.filter(id => !live.includes(id))]);
      const project = _project811Find(projectId);
      if (project) await _adoptProjectCustomer(project, pot.ticketIds, { live: true });
    }
    const marked = await DM.p811PotentialMarkUsed(pot.id, 'project', projectId);
    window.showToast?.((n ? `${n} 811 ticket${n === 1 ? '' : 's'} attached from the potential.` : 'Potential used.') + (marked ? '' : ' It may still show as suggested.'), marked ? 'success' : 'warning');
  } catch (err) {
    console.error('[Projects] potential attach failed:', err && err.code);
    _project811Toast(err, "Couldn't attach the potential's 811 tickets. Link them from Docs.");
    _rerenderProjectDocsIfOpen(projectId);
    return false; // COD: a failed attach still gets the waiting-tickets offer
  }
  _rerenderProjectDocsIfOpen(projectId);
  return true;
}

function renderProject811Section(project) {
  if (!_project811Enabled()) return '';
  const rows = _project811Rows(project);
  const canEdit = project811CanEdit();
  if (!rows.length && !canEdit) return '';
  const pid = _p811JsAttr(project.id);
  const badgeCss = { expired: 'background:#FEE2E2;color:#991B1B;', soon: 'background:#FEF3C7;color:#92400E;', ok: 'background:#E0F2FE;color:#075985;' };
  const list = rows.map(({ id, permit }) => {
    const name = permit ? (permit.name || permit.pdfName || '811 ticket') : 'Loading ticket…';
    const dt = permit ? _project811Date(permit.createdAt) : null;
    const meta = [dt ? 'Uploaded ' + dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '', (permit && permit.customerName) || ''].filter(Boolean).join(' · ');
    const badge = permit ? project811ExpiryBadge(permit) : null;
    const url = _project811SafeUrl(permit);
    return `
      <div class="project-811-row" data-permit-id="${escAttr(id)}" style="display:flex;align-items:center;gap:10px;padding:8px 10px;border:1px solid var(--border-color,#E5E7EB);border-radius:8px;min-height:48px;">
        <span class="material-icons" aria-hidden="true" style="color:#B45309;flex-shrink:0;">picture_as_pdf</span>
        <span style="display:flex;flex-direction:column;min-width:0;flex:1;">
          <span class="project-811-name" style="font-weight:600;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml(name)}</span>
          ${meta ? `<span style="font-size:0.8rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml(meta)}</span>` : ''}
        </span>
        ${badge ? `<span class="project-811-badge" style="font-size:0.75rem;font-weight:600;padding:2px 8px;border-radius:999px;white-space:nowrap;${badgeCss[badge.tone]}">${escHtml(badge.text)}</span>` : ''}
        ${url ? `<a class="btn btn-secondary btn-sm" href="${escAttr(url)}" target="_blank" rel="noopener noreferrer" aria-label="View ${escAttr(escHtml(name))}" style="text-decoration:none;min-height:40px;display:inline-flex;align-items:center;gap:4px;"><span class="material-icons" aria-hidden="true" style="font-size:18px;">open_in_new</span> View</a>` : ''}
        ${canEdit ? `<button type="button" class="btn-icon project-811-unlink" title="Remove from this project" aria-label="Remove ${escAttr(escHtml(name))} from this project" onclick="window.ProjectsModule.unlinkProject811('${pid}','${_p811JsAttr(id)}')"><span class="material-icons" aria-hidden="true">close</span></button>` : ''}
      </div>`;
  }).join('');
  const buttons = canEdit ? `
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px;">
        <button type="button" class="btn btn-secondary btn-sm" id="btn-project-811-link" style="min-height:44px;" onclick="window.ProjectsModule.linkProject811('${pid}')"><span class="material-icons" aria-hidden="true">add_link</span> ${rows.length ? 'Link another 811 ticket' : 'Link 811 ticket'}</button>
        <button type="button" class="btn btn-secondary btn-sm" id="btn-project-811-upload" style="min-height:44px;" onclick="window.ProjectsModule.uploadProject811('${pid}')"><span class="material-icons" aria-hidden="true">upload</span> Upload new</button>
      </div>` : '';
  // 811 waiting tickets — offered here too, so a project made from the dropdown
  // path (or a "Not now") still gets them.
  const waiting = canEdit ? _p811WaitingFor(project) : [];
  const suggested = waiting.length ? `
      <div class="project-811-suggested" style="margin-top:10px;padding:10px;border:1px solid #A5F3FC;background:#ECFEFF;border-radius:8px;">
        <div style="font-weight:600;color:#155E75;margin-bottom:6px;">Suggested for this project — attach?</div>
        <div style="display:flex;flex-direction:column;gap:6px;">${_p811WaitListHtml(waiting, 'p811d')}</div>
        <button type="button" class="btn btn-primary btn-sm" id="btn-p811-attach-docs" style="min-height:44px;margin-top:8px;" onclick="window.ProjectsModule.p811AttachWaiting('${pid}', 'p811d')">Attach selected</button>
      </div>` : '';
  return `
    <div class="project-811-section" style="margin-bottom:16px;">
      <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);margin:4px 0 8px;">811 tickets (${rows.length})</div>
      ${rows.length ? `<div style="display:flex;flex-direction:column;gap:6px;">${list}</div>`
        : '<div style="font-size:0.85rem;color:var(--text-secondary);">No 811 tickets on this project. Every job in the project shows the tickets you link here.</div>'}
      ${buttons}
      ${suggested}
    </div>`;
}

// Rewrites a project's link through a transaction on the LIVE doc. The cap is
// refused, never silently applied (TASK-9648 COD 10/12, same rule as jobs).
async function _writeProjectPermitIds(projectId, mutate) {
  const projectRef = doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId);
  let result = [];
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(projectRef);
    if (!snap.exists()) throw Object.assign(new Error('Project not found'), { code: 'not-found' });
    // COD diff: the live list is read UNCAPPED, so an over-cap doc is refused
    // below instead of silently losing tickets on the next edit.
    const wanted = mutate(_normProjectPermitIds((snap.data() || {}).permitIds, Infinity));
    const distinct = new Set((Array.isArray(wanted) ? wanted : []).map(r => (typeof r === 'string' ? r.trim() : '')).filter(Boolean));
    if (distinct.size > MAX_PROJECT_PERMIT_IDS) {
      throw Object.assign(new Error(`A project can hold up to ${MAX_PROJECT_PERMIT_IDS} 811 tickets.`), { code: 'permit-cap' });
    }
    result = _normProjectPermitIds(wanted);
    // TASK-9862 (COD): stamp each ticket being ADDED, so a renew / delete /
    // move transaction that read that ticket retries instead of missing it.
    const before = _normProjectPermitIds((snap.data() || {}).permitIds, Infinity);
    const added = result.filter(id => !before.includes(id));
    const addedSnaps = await Promise.all(added.map(id => tx.get(doc(db, 'tenants', ProjectState.tenantId, 'permits', id))));
    // COD r2: never ADD a ticket that is gone or already renewed.
    for (const ps of addedSnaps) {
      if (!ps.exists()) throw Object.assign(new Error('That 811 ticket was deleted.'), { code: 'permit-gone' });
      const d = ps.data() || {};
      if (d.renewedById || d.status === 'renewed') throw Object.assign(new Error('That 811 ticket was renewed. Use the new ticket.'), { code: 'permit-renewed' });
    }
    tx.update(projectRef, { permitIds: result, updatedAt: Timestamp.now() });
    for (const ps of addedSnaps) tx.update(ps.ref, { linkedAt: Timestamp.now() });
  });
  // Paint now; the projects snapshot confirms it a moment later.
  for (const p of [ProjectState.currentProject, (ProjectState.projects || []).find(x => x && x.id === projectId)]) {
    if (p && p.id === projectId) p.permitIds = result;
  }
  return result;
}

// Chris 10/14: a ticket attached to a project takes the project's MAIN customer
// — when it names none. A ticket that already names a customer keeps it (never
// overwrite a stated customer silently); the user changes it in the 811 window.
async function _adoptProjectCustomer(project, permitIds, opts = {}) {
  const custId = project && project.primaryCustomerId;
  if (!custId) return { stamped: 0, kept: 0 };
  const { permits } = _project811Permits();
  const custName = _project811CustomerName(project) || null;
  // permits is only used to skip ids with no ticket; the decision reads the live doc.
  let stamped = 0, kept = 0, failed = 0;
  for (const id of permitIds) {
    if (!opts.live && !permits.some(x => x && x.id === id)) continue; // 811 project dropdown: live = a fresh upload
    // COD (Code Reviewer): decided on the LIVE permit, so a customer someone
    // set a moment ago is never overwritten.
    try {
      const ref811 = doc(db, 'tenants', ProjectState.tenantId, 'permits', id);
      const r = await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref811);
        if (!snap.exists()) return 'gone';
        const cur = snap.data().customerId;
        if (cur) return cur === custId ? 'same' : 'kept';
        tx.update(ref811, { customerId: custId, customerName: custName });
        return 'stamped';
      });
      if (r === 'stamped') stamped++; else if (r === 'kept') kept++;
    } catch (e) { failed++; console.warn('[Projects] 811 customer stamp failed', id, e && e.code); }
  }
  return { stamped, kept, failed };
}

// The project's primary customer's NAME — never the id (getCustomerName falls
// back to the id, and AppState.customers is not always loaded here).
function _project811CustomerName(project) {
  const id = project && project.primaryCustomerId;
  if (!id) return '';
  const c = (ProjectState.customers || []).find(x => x.id === id) || (window.AppState?.customers || []).find(x => x.id === id);
  return (c && (c.name || c.company)) || project.primaryCustomerName || '';
}

function _rerenderProjectDocsIfOpen(projectId) {
  if (ProjectState.viewMode === 'detail' && ProjectState.currentProject?.id === projectId && ProjectState.detailTab === 'documents') {
    renderProjectsView();
  }
}

function _project811Toast(err, fallback) {
  window.showToast?.(err && err.code === 'permit-cap' ? err.message : fallback, 'error', null, 8000);
}

function _project811Find(projectId) {
  return (ProjectState.currentProject?.id === projectId ? ProjectState.currentProject : null)
    || (ProjectState.projects || []).find(p => p && p.id === projectId) || null;
}

function linkProject811(projectId) {
  const project = _project811Find(projectId);
  if (!project || !project811CanEdit()) return;
  const DM = window.DispatchModule;
  if (typeof DM?.open811Picker !== 'function') { window.showToast?.('811 tickets are not available here.', 'error'); return; }
  const before = projectPermitIdsOf(project);
  const custName = _project811CustomerName(project);
  const where = project.name || 'this project';
  DM.open811Picker({
    ids: before,
    customerId: project.primaryCustomerId || '',
    // The picker's "Upload new ticket" uploads onto THIS project.
    onUpload: () => uploadProject811(projectId),
    subtitle: (custName ? `Showing ${custName}'s tickets first. ` : '') + `Every job in ${where} shows the tickets you pick.`,
    onApply: async (ids) => {
      // COD (Code Reviewer): refuse an over-cap pick before normalizing — the
      // normalizer would silently drop tickets past the cap.
      if (new Set((ids || []).filter(Boolean)).size > MAX_PROJECT_PERMIT_IDS) {
        window.showToast?.(`A project can hold up to ${MAX_PROJECT_PERMIT_IDS} 811 tickets.`, 'error', null, 8000);
        return;
      }
      const picked = _normProjectPermitIds(ids);
      const removed = before.filter(id => !picked.includes(id));
      const added = picked.filter(id => !before.includes(id));
      try {
        // Applied to the LIVE doc as a diff, so a ticket someone else linked
        // while this picker was open is kept.
        await _writeProjectPermitIds(projectId, live => [...live.filter(id => !removed.includes(id)), ...added.filter(id => !live.includes(id))]);
        const { kept, failed } = await _adoptProjectCustomer(project, added);
        const extra = (kept ? ` ${kept} kept ${kept === 1 ? 'its' : 'their'} own customer.` : '')
          + (failed ? ` ${failed} couldn't take the project's customer — set it in Locates.` : '');
        window.showToast?.((added.length || removed.length ? '811 tickets updated on this project.' : 'No change.') + extra, 'success');
      } catch (err) {
        console.error('[Projects] link 811 failed:', err && err.code);
        _project811Toast(err, "Couldn't update this project's 811 tickets.");
      }
      _rerenderProjectDocsIfOpen(projectId);
    },
  });
}

function uploadProject811(projectId) {
  const project = _project811Find(projectId);
  if (!project || !project811CanEdit()) return;
  const DM = window.DispatchModule;
  if (typeof DM?.show811Submission !== 'function') { window.showToast?.('811 tickets are not available here.', 'error'); return; }
  DM.show811Submission({
    customerId: project.primaryCustomerId || '',
    hint: `This ticket will be added to ${project.name || 'this project'} when it uploads.`,
    onUploaded: async (permitId) => {
      await _writeProjectPermitIds(projectId, live => [...live, permitId]);
      _rerenderProjectDocsIfOpen(projectId);
    },
  });
}

async function unlinkProject811(projectId, permitId) {
  if (!project811CanEdit()) return;
  try {
    let at = -1;
    await _writeProjectPermitIds(projectId, live => { at = live.indexOf(permitId); return live.filter(id => id !== permitId); });
    // COD (Domain + UX): a mis-tap takes the ticket off every job in the
    // project, so the toast carries an Undo that puts it back where it was.
    window.showToast?.('Removed from this project. The ticket is still in Locates.', 'success', {
      label: 'Undo',
      callback: () => {
        const { loaded, permits } = _project811Permits();
        if (loaded && !permits.some(p => p && p.id === permitId)) {
          window.showToast?.('That ticket was deleted, so it cannot be put back.', 'warning');
          return;
        }
        _writeProjectPermitIds(projectId, live => {
          if (live.includes(permitId)) return live;
          const next = live.slice(); next.splice(at < 0 ? next.length : Math.min(at, next.length), 0, permitId); return next;
        }).then(() => _rerenderProjectDocsIfOpen(projectId))
          .catch(err => _project811Toast(err, "Couldn't put that ticket back. Link it again."));
      },
    }, 8000);
  } catch (err) {
    console.error('[Projects] unlink 811 failed:', err && err.code);
    _project811Toast(err, "Couldn't remove that ticket from the project.");
  }
  _rerenderProjectDocsIfOpen(projectId);
}

// dispatch.js deletePermit: every project that links a ticket, by QUERY (the
// loaded list is filtered/archived). complete=false → the query was refused
// and the caller must not delete (it would leave the id on a project).
async function projectsHoldingPermit(permitId) {
  const ids = new Set((ProjectState.projects || []).filter(p => projectPermitIdsOf(p).includes(permitId)).map(p => p.id));
  try {
    const snap = await getDocs(query(collection(db, 'tenants', ProjectState.tenantId, 'projects'), where('permitIds', 'array-contains', permitId)));
    snap.forEach(d => ids.add(d.id));
    return { ids, complete: true };
  } catch (e) {
    console.warn('[Projects] 811 holder query failed:', e && e.code);
    return { ids, complete: false };
  }
}

// Returns how many unlinks failed (0 = all done). A project deleted meanwhile
// holds nothing and is not a failure.
async function unlinkPermitFromProjects(permitId, projectIds) {
  let failed = 0;
  for (const pid of projectIds) {
    try { await _writeProjectPermitIds(pid, live => live.filter(id => id !== permitId)); }
    catch (e) { if (e && e.code === 'not-found') continue; failed++; console.warn('[Projects] 811 unlink failed for project', pid, e && e.code); }
  }
  return failed;
}

// ── 811 project dropdown (TASK-9767 follow-up) ─────────────────────────────
// The 811 window (dispatch.js) puts ONE ticket on a project. Same write path as
// the Docs tab: _writeProjectPermitIds (transaction, cap refused) + the
// customer stamp + unlinkPermitFromProjects. Never a second writer.
// Loaded projects that hold the ticket (display only; the save re-checks by query).
function getPermitProjectIds(permitId) {
  if (!permitId) return [];
  return (ProjectState.projects || []).filter(p => p && projectPermitIdsOf(p).includes(permitId)).map(p => p.id);
}

// newProjectId '' = take it off every project. TASK-9862 (COD): ONE
// transaction moves it — the destination gains it and every other holder loses
// it together, so two people moving the same ticket at once can never leave it
// on two projects. The ticket doc is read first and stamped (projectMovedAt):
// a concurrent move that commits in between makes this commit fail and retry,
// and the retry re-queries the holders. Returns { stamped, kept, failedUnlinks }.
// Throws on refusal (err.code 'permit-cap' / 'not-found'); nothing moved then.
async function setPermitProject(permitId, newProjectId) {
  if (!project811CanEdit()) throw Object.assign(new Error('No project access'), { code: 'permission-denied' });
  const t = ProjectState.tenantId;
  const permitRef = doc(db, 'tenants', t, 'permits', permitId);
  let finalIds = null;
  await runTransaction(db, async (tx) => {
    const ps = await tx.get(permitRef);
    if (!ps.exists()) throw Object.assign(new Error('Ticket not found'), { code: 'not-found' });
    const held = await getDocs(query(collection(db, 'tenants', t, 'projects'), where('permitIds', 'array-contains', permitId)));
    const ids = [...new Set([...held.docs.map(d => d.id), newProjectId].filter(Boolean))];
    const snaps = await Promise.all(ids.map(id => tx.get(doc(db, 'tenants', t, 'projects', id))));
    const writes = [];
    for (const s of snaps) {
      if (s.id === newProjectId) {
        if (!s.exists()) throw Object.assign(new Error('Project not found'), { code: 'not-found' });
        const live = _normProjectPermitIds((s.data() || {}).permitIds, Infinity);
        if (live.includes(permitId)) continue;
        if (live.length + 1 > MAX_PROJECT_PERMIT_IDS) throw Object.assign(new Error(`A project can hold up to ${MAX_PROJECT_PERMIT_IDS} 811 tickets.`), { code: 'permit-cap' });
        writes.push([s.ref, [...live, permitId], s.id]);
      } else if (s.exists()) {
        const live = _normProjectPermitIds((s.data() || {}).permitIds, Infinity);
        if (live.includes(permitId)) writes.push([s.ref, live.filter(id => id !== permitId), s.id]);
      }
    }
    for (const [ref811, next] of writes) tx.update(ref811, { permitIds: next, updatedAt: Timestamp.now() });
    tx.update(permitRef, { projectMovedAt: Timestamp.now() });
    finalIds = writes;
  });
  // Paint now; the projects snapshot confirms it a moment later.
  for (const [, next, pid] of (finalIds || [])) {
    for (const p of [ProjectState.currentProject, (ProjectState.projects || []).find(x => x && x.id === pid)]) {
      if (p && p.id === pid) p.permitIds = next;
    }
  }
  let stamp = { stamped: 0, kept: 0 };
  if (newProjectId) {
    const project = _project811Find(newProjectId);
    // live:true — a ticket uploaded a second ago is not in the permits snapshot yet.
    if (project) stamp = await _adoptProjectCustomer(project, [permitId], { live: true });
  }
  return { ...stamp, failedUnlinks: 0 };
}

// ── NOTES TAB ──────────────────────────────────────────
function renderNotesTab(project, notes) {
  const people = getPeople();

  return `
    <div class="project-notes-tab">
      <!-- Add Note -->
      <div class="project-add-note">
        <textarea id="project-note-input" rows="2" placeholder="Add a note... Use @name to mention someone (they'll get an SMS)"
                  style="width:100%;padding:10px;border:1px solid var(--border-light);border-radius:8px;font-size:0.9rem;resize:vertical;"></textarea>
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap;margin-top:8px;">
          <div class="project-note-vis" role="radiogroup" aria-label="Who can see this note" style="display:flex;gap:6px;flex-wrap:wrap;">
            <label class="project-note-vis-opt" style="display:inline-flex;align-items:center;gap:4px;font-size:0.8rem;cursor:pointer;padding:4px 10px;border:1px solid var(--border-light);border-radius:999px;color:var(--text-primary);">
              <input type="radio" name="project-note-visibility" value="internal" checked> &#128274; Internal
            </label>
            <label class="project-note-vis-opt" style="display:inline-flex;align-items:center;gap:4px;font-size:0.8rem;cursor:pointer;padding:4px 10px;border:1px solid var(--border-light);border-radius:999px;color:var(--text-primary);">
              <input type="radio" name="project-note-visibility" value="customer"> &#127760; Customer can see
            </label>
          </div>
          <button class="btn btn-primary btn-sm" onclick="window.ProjectsModule.submitNote('${project.id}')">
            <span class="material-icons" style="font-size:16px;">send</span> Add Note
          </button>
        </div>
        <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">@mention to notify via SMS. Internal notes never show on the customer portal${project.portalEnabled ? '' : ' (the portal is off for this project)'}.</div>
      </div>

      <!-- Notes List -->
      <div class="project-notes-list">
        ${notes.length === 0 ? '<p style="color:var(--text-secondary);text-align:center;padding:20px;">No notes yet</p>' : ''}
        ${notes.map(n => `
          <div class="project-note" data-note-visibility="${noteVisibility(n)}">
            <div class="project-note-header" style="flex-wrap:wrap;">
              <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">person</span>
              <strong style="font-size:0.85rem;">${escHtml(n.authorName || n.createdByName || 'Unknown')}</strong>
              ${renderNoteVisibilityBadge(n)}
              ${noteFromCustomer(n) ? '' : `<button type="button" class="btn-icon-sm project-note-vis-toggle" data-project-id="${escAttr(project.id)}" data-note-id="${escAttr(n.id)}" data-to="${noteVisibility(n) === 'customer' ? 'internal' : 'customer'}"
                onclick="window.ProjectsModule.setNoteVisibility(this.dataset.projectId, this.dataset.noteId, this.dataset.to)"
                title="${noteVisibility(n) === 'customer' ? 'Make internal (hide from customer)' : 'Let the customer see this note'}" style="font-size:0.7rem;color:var(--brand-blue);background:none;border:none;cursor:pointer;padding:2px 4px;">
                ${noteVisibility(n) === 'customer' ? 'Make internal' : 'Share with customer'}</button>`}
              <span style="color:var(--text-secondary);font-size:0.75rem;margin-left:auto;">${formatDate(n.createdAt)}</span>
              <button class="btn-icon-sm" onclick="window.ProjectsModule.deleteNote('${project.id}','${n.id}')" title="Delete note" style="margin-left:4px;">
                <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">close</span>
              </button>
            </div>
            <div class="project-note-text">${formatNoteText(n.text, people)}</div>
            ${n.mentions?.length > 0 ? `
              <div style="font-size:0.7rem;color:var(--brand-blue);margin-top:4px;">
                <span class="material-icons" style="font-size:12px;vertical-align:middle;">notifications_active</span>
                SMS sent to: ${n.mentions.map(id => {
                  const p = people.find(p => p.id === id);
                  return p?.name || id;
                }).join(', ')}
              </div>
            ` : ''}
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

function renderNoteVisibilityBadge(n) {
  if (noteFromCustomer(n)) {
    return '<span class="project-note-badge" style="font-size:0.65rem;background:#E0F2FE;color:#0369A1;padding:1px 8px;border-radius:999px;font-weight:600;">From customer</span>';
  }
  return noteVisibility(n) === 'customer'
    ? '<span class="project-note-badge" style="font-size:0.65rem;background:#DCFCE7;color:#166534;padding:1px 8px;border-radius:999px;font-weight:600;">&#127760; Customer can see</span>'
    : '<span class="project-note-badge" style="font-size:0.65rem;background:#F1F5F9;color:#334155;padding:1px 8px;border-radius:999px;font-weight:600;">&#128274; Internal</span>';
}

function formatNoteText(text, people) {
  if (!text) return '';
  // Highlight @mentions
  let html = escHtml(text);
  for (const p of people) {
    if (p.name) {
      const regex = new RegExp(`@${p.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
      html = html.replace(regex, `<span style="color:var(--brand-blue);font-weight:600;">@${escHtml(p.name)}</span>`);
    }
  }
  return html;
}

function parseMentions(text) {
  const people = getPeople();
  const mentions = [];
  for (const p of people) {
    if (p.name && text.includes(`@${p.name}`)) {
      mentions.push(p.id);
    }
  }
  return mentions;
}

// ── CHANGE ORDERS TAB ──────────────────────────────────
function renderChangesTab(project, changeOrders) {
  const pending = changeOrders.filter(co => co.status === 'pending');
  const approved = changeOrders.filter(co => co.status === 'approved');
  const rejected = changeOrders.filter(co => co.status === 'rejected');

  return `
    <div class="project-changes-tab">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h4 style="margin:0;">Change Orders</h4>
        <button class="btn btn-primary btn-sm" onclick="window.ProjectsModule.showAddChangeOrder('${project.id}')">
          <span class="material-icons" style="font-size:16px;">add</span> Add Change Order
        </button>
      </div>
      ${project.portalSettings?.allowChangeRequests && !customerDocsRecipientsExist(getPeople()) ? _customerDocsNobodyWarning('0 0 12px', 'requests') : ''}

      ${changeOrders.length === 0 ? `
        <div style="text-align:center;padding:40px;color:var(--text-secondary);">
          <span class="material-icons" style="font-size:48px;display:block;margin-bottom:8px;">receipt_long</span>
          No change orders yet
        </div>
      ` : ''}

      ${pending.length > 0 ? `
        <h5 style="color:#F59E0B;font-size:0.8rem;text-transform:uppercase;margin:12px 0 8px;">Pending (${pending.length})</h5>
        ${pending.map(co => renderChangeOrderRow(co, project.id)).join('')}
      ` : ''}

      ${approved.length > 0 ? `
        <h5 style="color:#10B981;font-size:0.8rem;text-transform:uppercase;margin:12px 0 8px;">Approved (${approved.length})</h5>
        ${approved.map(co => renderChangeOrderRow(co, project.id)).join('')}
      ` : ''}

      ${rejected.length > 0 ? `
        <h5 style="color:#EF4444;font-size:0.8rem;text-transform:uppercase;margin:12px 0 8px;">Rejected (${rejected.length})</h5>
        ${rejected.map(co => renderChangeOrderRow(co, project.id)).join('')}
      ` : ''}
    </div>
  `;
}

function renderChangeOrderRow(co, projectId) {
  const statusIcons = { pending: 'pending', approved: 'check_circle', rejected: 'cancel' };
  const statusColors = { pending: '#F59E0B', approved: '#10B981', rejected: '#EF4444' };

  return `
    <div class="project-change-order-row">
      <span class="material-icons" style="font-size:20px;color:${statusColors[co.status]};">${statusIcons[co.status]}</span>
      <div style="flex:1;">
        <div style="font-weight:600;font-size:0.9rem;">
          ${escHtml(co.description)}
          ${co.source === 'customer_portal' ? '<span style="font-size:0.65rem;background:#E0F2FE;color:#0369A1;padding:1px 6px;border-radius:8px;font-weight:600;margin-left:6px;vertical-align:middle;">Customer Request</span>' : ''}
          ${co.source !== 'customer_portal' ? (co.source === 'office' && co.customerVisible === true
            ? '<span style="font-size:0.65rem;background:#DCFCE7;color:#166534;padding:1px 6px;border-radius:8px;font-weight:600;margin-left:6px;vertical-align:middle;">Customer sees this</span>'
            : '<span style="font-size:0.65rem;background:#F1F5F9;color:#334155;padding:1px 6px;border-radius:8px;font-weight:600;margin-left:6px;vertical-align:middle;">Internal</span>') : ''}
        </div>
        ${co.source === 'office' && co.customerVisible === true ? `<button type="button" class="btn btn-sm btn-secondary" style="margin-top:4px;min-height:44px;font-size:0.8rem;" onclick="event.stopPropagation();window.ProjectsModule.unshareChangeOrder('${_p811JsAttr(projectId)}','${_p811JsAttr(co.id)}')">Hide from customer</button>` : ''}
        ${Array.isArray(co.requestedItems) && co.requestedItems.length ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">${co.requestedItems.map(i => `${escHtml(String(i?.quantity ?? ''))}x ${escHtml(i?.name)}`).join(', ')}</div>` : ''}
        ${co.poNumber ? `<div style="font-size:0.75rem;color:var(--text-secondary);">PO: ${escHtml(co.poNumber)}</div>` : ''}
        <div style="font-size:0.75rem;color:var(--text-secondary);">
          ${escHtml(co.createdByName || co.requestedBy || '')} · ${formatDate(co.createdAt)}
          ${co.approvedAt ? ` · Approved ${formatDateShort(co.approvedAt)}` : ''}
        </div>
      </div>
      ${typeof co.amount !== 'number' ? '<div style="font-weight:700;font-size:0.95rem;color:var(--text-secondary);" title="Not priced">—</div>' : `
      <div style="font-weight:700;font-size:0.95rem;${co.amount >= 0 ? 'color:#10B981' : 'color:#EF4444'};"${co.source === 'office' && co.customerVisible === true ? ' title="Locked while the customer sees this change order — hide it, then add a new one to reprice"' : ''}>
        ${co.amount >= 0 ? '+' : ''}$${Math.abs(co.amount || 0).toLocaleString()}
      </div>`}
      ${co.status === 'pending' ? `
        <div style="display:flex;gap:4px;">
          <button class="btn btn-sm" style="background:#10B981;color:#fff;border:none;padding:4px 8px;border-radius:4px;cursor:pointer;font-size:0.75rem;"
                  onclick="event.stopPropagation();window.ProjectsModule.approveChangeOrder('${_p811JsAttr(projectId)}','${_p811JsAttr(co.id)}')">Approve</button>
          <button class="btn btn-sm" style="background:#EF4444;color:#fff;border:none;padding:4px 8px;border-radius:4px;cursor:pointer;font-size:0.75rem;"
                  onclick="event.stopPropagation();window.ProjectsModule.rejectChangeOrder('${_p811JsAttr(projectId)}','${_p811JsAttr(co.id)}')">Reject</button>
        </div>
      ` : ''}
    </div>
  `;
}

// ============================================
// MODALS / FORMS
// ============================================
// TASK-9548 slice 3 — the project customer pickers group Shop/Office under
// "Internal — not billed", then "Customers" A–Z (shared builder, read at call
// time — Pitfall #1). Vendors dropped; a project's current customer kept.
function _projectCustomerOptionsHtml(list, selectedId) {
  return window.PickerGroups.customerOptionsHtml(list, {
    selectedId: selectedId || null,
    nameOf: c => c.name || c.company || '',
    optionAttrs: c => ` data-name="${escAttr(c.name || c.company)}"`,
  });
}

function showCreateProject() {
  // P0 2026-10-06 — customers only: _projectCustomerOptionsHtml drops vendors.
  const templates = ProjectState.templates || [];

  // Create modal dynamically
  let modal = document.getElementById('modal-create-project');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-create-project';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }

  const today = new Date().toISOString().split('T')[0];

  modal.innerHTML = `
    <div class="modal-content modal-medium" style="max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3>New Project</h3>
        <button class="btn-icon" onclick="document.getElementById('modal-create-project').classList.add('hidden')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <form onsubmit="event.preventDefault();window.ProjectsModule.submitCreateProject()">
        <div class="form-group">
          <label for="new-project-name">Project Name *</label>
          <input type="text" id="new-project-name" required placeholder="e.g. Chevron AST Site — Mat Rental">
        </div>

        <div class="form-group">
          <label for="new-project-customer">Customer</label>
          <select id="new-project-customer">
            <option value="">Select customer...</option>
            ${_projectCustomerOptionsHtml(ProjectState.customers || [], null)}
          </select>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="new-project-start">Start Date</label>
            <input type="date" id="new-project-start" value="${today}">
          </div>
          <div class="form-group">
            <label for="new-project-end">End Date</label>
            <input type="date" id="new-project-end">
          </div>
        </div>

        <div class="form-group">
          <label for="new-project-po">Standing PO Number</label>
          <input type="text" id="new-project-po" placeholder="e.g. ND26UA2-U0">
          <small style="display:block;margin-top:4px;color:var(--text-secondary);font-size:0.75rem;">
            Applied to every job added to this project that doesn't already have its own PO. Existing job POs are never overwritten.
          </small>
        </div>

        ${renderProjectJobDefaultsSection('new-project', null)}

        <div class="form-group">
          <label for="new-project-location">Location</label>
          <input type="text" id="new-project-location" placeholder="Site address or well name">
        </div>

        <div class="form-group">
          <label for="new-project-description">Description</label>
          <textarea id="new-project-description" rows="2" placeholder="Project scope, notes..."></textarea>
        </div>

        <div class="form-group">
          <label for="new-project-tags">Tags (comma-separated)</label>
          <input type="text" id="new-project-tags" placeholder="mat-rental, oilfield, priority">
        </div>

        <div class="form-group">
          <label for="new-project-revenue">Estimated Revenue ($)</label>
          <input type="number" id="new-project-revenue" placeholder="0.00" step="0.01" min="0">
        </div>

        ${renderNewJobDefaultsSection('new-project', null)}

        ${templates.length > 0 ? `
          <div class="form-group">
            <label for="new-project-template">Create from Template</label>
            <select id="new-project-template">
              <option value="">No template — blank project</option>
              ${templates.map(t => `<option value="${t.id}">${escHtml(t.name)} (${t.defaultJobs?.length || 0} jobs)</option>`).join('')}
            </select>
          </div>
        ` : ''}

        <div class="form-actions" style="margin-top:16px;">
          <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-create-project').classList.add('hidden')">Cancel</button>
          <button type="submit" class="btn btn-primary" id="btn-create-project">
            <span class="material-icons" style="font-size:18px;">add</span> Create Project
          </button>
        </div>
      </form>
    </div>
  `;

  modal.classList.remove('hidden');
  hydrateNewJobDefaultsTemplatePicker('new-project'); // TASK-9391
}

async function submitCreateProject() {
  const name = document.getElementById('new-project-name').value.trim();
  if (!name) { window.showToast?.('Project name is required', 'error'); return; }

  const customerSelect = document.getElementById('new-project-customer');
  const customerId = customerSelect.value;
  const customerName = customerSelect.selectedOptions[0]?.dataset?.name || '';

  // TASK-9420 — same name as an existing project? Ask (soft; never a block).
  // The button is held while we look, so a second click can't stack dialogs.
  if (ProjectState._createNameChecking) return;
  ProjectState._createNameChecking = true;
  const _checkBtn = document.getElementById('btn-create-project');
  if (_checkBtn) _checkBtn.disabled = true;
  // Going on, the button STAYS disabled into the create below (no await in
  // between); it is re-enabled only when the save stops here.
  let _goOn = false;
  try {
    _goOn = await confirmProjectNameNotDuplicate(name, { customerId, closeModalId: 'modal-create-project' });
  } finally {
    ProjectState._createNameChecking = false;
    if (_checkBtn && !_goOn) _checkBtn.disabled = false;
  }
  if (!_goOn) return;

  const startVal = document.getElementById('new-project-start').value;
  const endVal = document.getElementById('new-project-end').value;
  const location = document.getElementById('new-project-location').value.trim();
  const poNumber = poNormalizeForSave(document.getElementById('new-project-po')?.value);
  const description = document.getElementById('new-project-description').value.trim();
  const tagsStr = document.getElementById('new-project-tags').value.trim();
  const tags = tagsStr ? tagsStr.split(',').map(t => t.trim()).filter(Boolean) : [];
  const revenue = parseFloat(document.getElementById('new-project-revenue').value) || null;
  const templateId = document.getElementById('new-project-template')?.value || '';

  const data = {
    name,
    customerIds: customerId ? [customerId] : [],
    primaryCustomerId: customerId || null,
    primaryCustomerName: customerName,
    startDate: startVal ? Timestamp.fromDate(new Date(startVal + 'T12:00:00')) : Timestamp.now(),
    endDate: endVal ? Timestamp.fromDate(new Date(endVal + 'T12:00:00')) : null,
    location,
    poNumber,
    description,
    tags,
    estimatedRevenue: revenue,
    onSiteContacts: [],
    newJobDefaults: readNewJobDefaultsFromForm('new-project'), // TASK-9391
    jobDefaults: readProjectJobDefaultsFromForm('new-project') // TASK-4696
  };

  const btn = document.getElementById('btn-create-project');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Creating...'; }

  let projectId;
  if (templateId) {
    projectId = await createProjectFromTemplate(templateId, data);
  } else {
    projectId = await createProject(data);
  }

  document.getElementById('modal-create-project').classList.add('hidden');

  if (projectId) {
    await openProjectDetail(projectId);
    // TASK-9887 — a potential project from Locates that looks like this one is
    // offered first; declining it falls through to the waiting-tickets offer.
    if (!(await p811OfferPotential(projectId, data))) p811OfferWaiting(projectId, data); // 811 waiting tickets
  } else {
    renderProjectsView();
  }
}

// ── TASK-9549 Project lead ─────────────────────────────
// One lead per project (leadUid + display leadName), set to the creator at
// birth. Changing it = the tenant OWNER (ownerId) or a 'dispatch'-module member
// (the rules arm that may write any project key). Modules, never roles.
function projectLeadCanChange() {
  return fieldTotalsCanEdit();
}
function projectLeadLabel(project) {
  if (project?.leadUid) {
    const name = project.leadName || ProjectState._leadNames?.get(project.leadUid) || 'Teammate';
    return escHtml(name);
  }
  const creator = project?.createdByName;
  return creator
    ? `${escHtml(creator)} <span style="color:var(--text-secondary);font-size:0.8rem;">(created it — no lead set)</span>`
    : '<span style="color:var(--text-secondary);">No lead set</span>';
}
const LEAD_INACTIVE = ['suspended', 'removed', 'revoked', 'inactive', 'pending'];
// Teammates who can lead: active staff logins holding 'projects' or 'dispatch'
// (or a legacy full-access member with no modules list), plus the owner. Link
// members (driver / customer links) never. Loaded once per tenant.
async function loadLeadCandidates() {
  const tid = ProjectState.tenantId;
  if (!tid || ProjectState._leadCandidatesFor === tid) return;
  ProjectState._leadCandidatesFor = tid;
  const out = [];
  const names = new Map();
  const ownerId = window.AppState?.tenantData?.ownerId || null;
  try {
    const snap = await getDocs(collection(db, 'tenants', tid, 'members'));
    snap.docs.forEach((d) => {
      const m = d.data() || {};
      const name = m.displayName || m.name || m.email || 'Teammate';
      names.set(d.id, name);
      if (m.accessToken || m.linkTypes || m.readOnly === true || LEAD_INACTIVE.includes(m.status)) return;
      const mods = m.modules;
      const holds = d.id === ownerId || !Array.isArray(mods) || mods.includes('projects') || mods.includes('dispatch');
      if (holds) out.push({ uid: d.id, name });
    });
  } catch (e) {
    console.warn('[Projects] lead list load failed:', e?.code || e?.message || e);
    ProjectState._leadCandidatesFor = null;
  }
  if (ownerId && !out.some(p => p.uid === ownerId)) {
    out.push({ uid: ownerId, name: names.get(ownerId) || window.AppState?.tenantData?.ownerName || 'Owner' });
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  ProjectState._leadCandidates = out;
  ProjectState._leadNames = names;
}
function projectLeadOptionsHtml(project) {
  const list = (ProjectState._leadCandidates || []).slice();
  const cur = project?.leadUid || '';
  if (cur && !list.some(p => p.uid === cur)) list.unshift({ uid: cur, name: project.leadName || 'Current lead' });
  const head = cur ? '' : '<option value="">No lead set</option>';
  return head + list.map(p => `<option value="${escAttr(p.uid)}" data-name="${escAttr(p.name)}" ${p.uid === cur ? 'selected' : ''}>${escHtml(p.name)}</option>`).join('');
}
async function refreshLeadSelect(projectId) {
  await loadLeadCandidates();
  const sel = document.getElementById('edit-project-lead');
  const project = ProjectState.projects.find(p => p.id === projectId);
  // Only the modal still open on THIS project (COD r2: A's late refresh must not repaint B).
  if (sel && project && sel.dataset.projectId === projectId && document.getElementById('modal-edit-project')?.classList.contains('hidden') === false) {
    // Keep a pick the user made before the list loaded (COD r1 Edge Case).
    const picked = sel.value;
    sel.innerHTML = projectLeadOptionsHtml(project);
    if (picked && [...sel.options].some(o => o.value === picked)) sel.value = picked;
  }
}

// TASK-10120 (bundle headroom) — the deferred leg of every lazy-Projects shim (the
// _loadPjXxx blocks below). A failed chunk load has already told the user (the
// loader's toast) and is retried on the next tap, so it is marked handled here:
// onclick callers ignore the returned promise and must not leave an unhandled
// rejection. Any other error a moved function throws on that first call is logged.
// Awaiting callers still see the promise reject exactly as before. Same pattern as
// maintenance.js's _maintLazyCall (TASK-10065).
function _projLazyCall(load, name, args) {
  const p = load().then((m) => m[name](...args));
  p.catch((e) => { if (!(e && e._projLazyLoadFailed)) console.error('[Projects] ' + name + ' failed:', e); });
  return p;
}

// =============================================================================
// TASK-10120 (bundle headroom) — the Edit Project form (open, customer portal + milestone tracker steps, CRM step import, save body)
// live in the LAZY views/projEditProject.js (perf budget appColdLoadGzip). Bodies moved
// verbatim; each shim keeps its name (window.ProjectsModule and projects.js
// callers are unchanged), loads the module on first use and, once loaded, calls
// straight through synchronously. Deps are handed over, never imported (pitfall #1).
// =============================================================================
let _pjEditMod = null;
let _pjEditLoad = null;
function _pjEditDeps() {
  return {
    _customerDocsNobodyWarning, _projectCustomerOptionsHtml, collection,
    confirmProjectNameNotDuplicate, customerDocsRecipientsExist, db, doc, escAttr, escHtml,
    finishProjectGeocodeLate, getDoc, getDocs, getPeople,
    hydrateNewJobDefaultsTemplatePicker, normalizeProjectJobDefaults, normalizeProjectName,
    p811OfferWaiting, poNormalizeForSave, projectAddressUnmapped, projectJobDefaultsEqual,
    projectLeadCanChange, projectLeadOptionsHtml, ProjectState, projectUnmappedMessage,
    readNewJobDefaultsFromForm, readProjectJobDefaultsFromForm, refreshLeadSelect,
    renderNewJobDefaultsSection, renderProjectJobDefaultsSection, renderProjectsView,
    resolveProjectCoords, setDoc, Timestamp, updateProject
  };
}
function _loadPjEdit() {
  if (_pjEditMod) return Promise.resolve(_pjEditMod);
  if (!_pjEditLoad) {
    _pjEditLoad = __ddChunkWrap(import('./projEditProject.js?v=1012295')).then((m) => { m._bind(_pjEditDeps()); _pjEditMod = m; return m; }).catch((e) => {
      _pjEditLoad = null; // the next click tries again
      console.error('[Projects] projEditProject load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _projLazyLoadFailed: true });
    });
  }
  return _pjEditLoad;
}
function showEditProject(...a) { return _pjEditMod ? _pjEditMod.showEditProject(...a) : _projLazyCall(_loadPjEdit, 'showEditProject', a); }
function renderPortalStepsList(...a) { return _pjEditMod ? _pjEditMod.renderPortalStepsList(...a) : _projLazyCall(_loadPjEdit, 'renderPortalStepsList', a); }
function addPortalStep(...a) { return _pjEditMod ? _pjEditMod.addPortalStep(...a) : _projLazyCall(_loadPjEdit, 'addPortalStep', a); }
function removePortalStep(...a) { return _pjEditMod ? _pjEditMod.removePortalStep(...a) : _projLazyCall(_loadPjEdit, 'removePortalStep', a); }
function updatePortalStepName(...a) { return _pjEditMod ? _pjEditMod.updatePortalStepName(...a) : _projLazyCall(_loadPjEdit, 'updatePortalStepName', a); }
function togglePortalStep(...a) { return _pjEditMod ? _pjEditMod.togglePortalStep(...a) : _projLazyCall(_loadPjEdit, 'togglePortalStep', a); }
function mergeStoredStepCompletion(...a) { return _pjEditMod ? _pjEditMod.mergeStoredStepCompletion(...a) : _projLazyCall(_loadPjEdit, 'mergeStoredStepCompletion', a); }
function importOppSteps(...a) { return _pjEditMod ? _pjEditMod.importOppSteps(...a) : _projLazyCall(_loadPjEdit, 'importOppSteps', a); }
function submitEditProjectBody(...a) { return _pjEditMod ? _pjEditMod.submitEditProjectBody(...a) : _projLazyCall(_loadPjEdit, 'submitEditProjectBody', a); }

// TASK-4842 (COD Code + Independent) — the save now awaits a geocode (up to
// PROJECT_GEOCODE_TIMEOUT_MS) before it writes, which widened the old
// double-click window: two overlapping saves could each mint a portal token and
// leave a live orphan accessTokens doc. One save at a time, button held for the
// WHOLE save (geocode, write, token mint), released in `finally`.
async function submitEditProject(projectId) {
  if (ProjectState._editSaving) return;
  ProjectState._editSaving = true;
  const saveBtn = document.querySelector('#modal-edit-project button[type="submit"]');
  if (saveBtn) saveBtn.disabled = true;
  try {
    await submitEditProjectBody(projectId);
  } finally {
    ProjectState._editSaving = false;
    if (saveBtn) saveBtn.disabled = false;
  }
}

// ── Link Existing Job ──────────────────────────────────
// TASK-9366 — the picker is DATE-driven, not status-driven. 952 of 972 TITS
// jobs are status:'active' with an endDate in the past (every tenant is the
// same shape), so "active" listed years of dead work. Mirrors the TASK-1082
// relevance rule in clock.js getRelevantJobs (inline — never import clock.js,
// Pitfall #1): recent + current + upcoming, closed statuses excluded.
const LINK_JOB_LOOKBACK_DAYS = 30;
const LINK_JOB_CLOSED_STATUSES = ['finished', 'cancelled', 'archived'];

function linkJobCutoff(now = new Date()) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - LINK_JOB_LOOKBACK_DAYS);
  return d;
}

// Pure: the client-side half of the picker. Drops closed statuses, already-
// linked jobs and anything whose endDate is unparseable or before the cutoff
// (a string endDate sorts ABOVE every Timestamp in a Firestore range query, so
// the query alone cannot enforce the cutoff for mixed data). Sorted endDate
// desc so upcoming work is first.
function pickLinkableJobs(jobs, cutoff) {
  const toEnd = (j) => {
    const v = j && j.endDate;
    const d = v && typeof v.toDate === 'function' ? v.toDate() : (v ? new Date(v) : null);
    return d && !isNaN(d) ? d : null;
  };
  return (jobs || [])
    .filter(j => j && !j.projectId && !LINK_JOB_CLOSED_STATUSES.includes(j.status))
    .map(j => ({ j, end: toEnd(j) }))
    .filter(x => x.end && x.end >= cutoff)
    .sort((a, b) => b.end - a.end)
    .map(x => x.j);
}

async function showLinkExistingJob(projectId) {
  // TASK-9365: the original status-equality + startDate orderBy needed a
  // composite index that was never declared, and the rejected promise was
  // never caught — the button silently did nothing. The query below is
  // single-field on endDate (range + orderBy on the SAME field), so no
  // composite index is needed; the try/catch stays so nothing is ever silent.
  const cutoff = linkJobCutoff();
  let snap;
  try {
    snap = await getDocs(
      query(
        collection(db, 'tenants', ProjectState.tenantId, 'jobs'),
        where('endDate', '>=', Timestamp.fromDate(cutoff)),
        orderBy('endDate', 'desc'),
        limit(200)
      )
    );
  } catch (err) {
    console.error('[Projects] Link Existing job load error:', err);
    window.showToast?.('Could not load jobs: ' + (err.message || 'Unknown error'), 'error');
    return;
  }
  const unlinkedJobs = pickLinkableJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })), cutoff);

  let modal = document.getElementById('modal-link-job');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-link-job';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }

  modal.innerHTML = `
    <div class="modal-content modal-medium" style="max-height:80vh;max-height:80dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3>Link Existing Job</h3>
        <button class="btn-icon" onclick="document.getElementById('modal-link-job').classList.add('hidden')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <input type="text" id="link-job-search" placeholder="Search jobs..." oninput="window.ProjectsModule.filterLinkJobs(this.value)"
             style="width:100%;padding:10px;border:1px solid var(--border-light);border-radius:8px;margin-bottom:12px;">
      <div id="link-job-list" style="max-height:400px;overflow-y:auto;">
        ${unlinkedJobs.length === 0 ? `<p style="color:var(--text-secondary);text-align:center;padding:20px;">No unlinked jobs ending in the last ${LINK_JOB_LOOKBACK_DAYS} days or later</p>` : ''}
        ${unlinkedJobs.map(j => `
          <div class="project-job-row" onclick="window.ProjectsModule.doLinkJob('${j.id}','${projectId}')" style="cursor:pointer;" data-title="${escAttr(j.title?.toLowerCase() || '')}">
            <div style="flex:1;">
              <div style="font-weight:600;font-size:0.9rem;">${escHtml(j.title)}</div>
              <div style="font-size:0.75rem;color:var(--text-secondary);">${formatDateShort(j.startDate)} — ${formatDateShort(j.endDate)} · ${escHtml(j.customer || '')}</div>
            </div>
            <span class="material-icons" style="color:var(--brand-blue);font-size:18px;">add_link</span>
          </div>
        `).join('')}
      </div>
    </div>
  `;

  modal.classList.remove('hidden');
}

function filterLinkJobs(query) {
  const q = query.trim().toLowerCase();
  document.querySelectorAll('#link-job-list .project-job-row').forEach(row => {
    row.style.display = row.dataset.title?.includes(q) ? '' : 'none';
  });
}

async function doLinkJob(jobId, projectId) {
  // TASK-9289 r3 — the picker only lists UNLINKED jobs (pickLinkableJobs), so
  // the job is expected to have no project; one linked since is refused.
  // TASK-9599 — 'ask': a project with no customer yet offers to adopt the job's.
  await linkJobToProject(jobId, projectId, { expectedPreviousProjectId: null, source: 'project-link-existing', adoptCustomer: 'ask' });
  document.getElementById('modal-link-job')?.classList.add('hidden');
  // Refresh detail view
  if (ProjectState.viewMode === 'detail' && ProjectState.currentProject?.id === projectId) {
    renderProjectsView();
  }
}

async function unlinkJob(jobId, projectId) {
  if (!confirm('Unlink this job from the project?')) return;
  // TASK-9289 r3 — expect the project this row was displayed under.
  await unlinkJobFromProject(jobId, { expectedPreviousProjectId: projectId || null });
  if (ProjectState.viewMode === 'detail' && ProjectState.currentProject?.id === projectId) {
    renderProjectsView();
  }
}

// ── Schedule New Job from Project ──────────────────────
// TASK-9391 Slice 2 — resolve everything New Job needs from the project: its
// customer / PO / location, its New job defaults (flags), and its latest job
// (the source the flags read from). Dispatch applies it (dispatch.js
// _buildProjectNewJobCarry / _applyProjectNewJobCarry). Also the seam TASK-9392
// uses for customer-request drafts: call this, add the request's facts, then
// window.DispatchModule.openNewJobWithProjectPrefill(link).
//
// Reads only the project's most recent jobs' days (by job end/start date,
// newest first, capped) rather than every day of a long project — the latest
// day belongs to one of the latest jobs.
const PROJECT_SOURCE_JOB_SCAN = 30;
async function resolveProjectNewJobPrefill(projectId) {
  const project = ProjectState.projects.find(p => p.id === projectId);
  if (!project) return null;
  const link = {
    projectId,
    projectName: project.name,
    customerId: project.primaryCustomerId,
    customerName: project.primaryCustomerName,
    location: project.location,
    poNumber: project.poNumber || '',
    jobDefaults: normalizeProjectJobDefaults(project.jobDefaults), // TASK-4696
    defaults: normalizeNewJobDefaults(project.newJobDefaults),
    source: null
  };
  try {
    const jobs = (await loadProjectJobs(projectId)).filter(j => j.status !== 'cancelled');
    if (jobs.length === 0) return link; // empty folder → today's prefill
    const jobMs = (j) => {
      const v = j.endDate || j.startDate || j.createdAt;
      const d = v?.toDate ? v.toDate() : (v ? new Date(v) : null);
      const t = d ? d.getTime() : NaN;
      return Number.isFinite(t) ? t : 0;
    };
    const recent = jobs.slice().sort((a, b) => jobMs(b) - jobMs(a)).slice(0, PROJECT_SOURCE_JOB_SCAN);
    const days = [];
    for (let i = 0; i < recent.length; i += 10) {
      const ids = recent.slice(i, i + 10).map(j => j.id);
      const snap = await getDocs(query(
        collection(db, 'tenants', ProjectState.tenantId, 'jobDays'),
        where('jobId', 'in', ids)
      ));
      snap.docs.forEach(d => days.push({ id: d.id, ...d.data() }));
    }
    link.source = pickProjectSourceJob(recent, days);
  } catch (err) {
    // A failed read must not block New Job — it opens with today's prefill and
    // says why nothing else carried.
    console.warn('[Projects] New Job source lookup failed:', err);
    window.logError?.(err, { module: 'projects', operation: 'NEW_JOB_SOURCE_LOOKUP', projectId });
    window.showToast?.('Could not read this project’s latest job — starting from the project details only.', 'warning', null, 6000);
    link.source = null;
  }
  return link;
}

let _scheduleJobFromProjectBusy = false;
async function scheduleJobFromProject(projectId) {
  if (_scheduleJobFromProjectBusy) return; // double-tap while the lookup runs
  _scheduleJobFromProjectBusy = true;
  try {
    const link = await resolveProjectNewJobPrefill(projectId);
    if (!link) return;
    if (typeof window.DispatchModule?.openNewJobWithProjectPrefill === 'function') {
      window.DispatchModule.openNewJobWithProjectPrefill(link);
    } else if (typeof window.DispatchModule?.showAddJob === 'function') {
      window.__pendingProjectLink = link;
      window.DispatchModule.showAddJob();
    } else {
      window.showToast?.('Navigate to dispatch and add a job manually', 'info');
    }
  } finally {
    _scheduleJobFromProjectBusy = false;
  }
}

// =============================================================================
// TASK-10120 (bundle headroom) — the Add Change Order modal (+ its customer-visibility preview and submit) and the Project Templates manager (list + create)
// live in the LAZY views/projChangeOrderTemplates.js (perf budget appColdLoadGzip). Bodies moved
// verbatim; each shim keeps its name (window.ProjectsModule and projects.js
// callers are unchanged), loads the module on first use and, once loaded, calls
// straight through synchronously. Deps are handed over, never imported (pitfall #1).
// =============================================================================
let _pjTplMod = null;
let _pjTplLoad = null;
function _pjTplDeps() {
  return {
    addChangeOrder, createTemplate, escHtml, ProjectState, renderProjectsView
  };
}
function _loadPjTpl() {
  if (_pjTplMod) return Promise.resolve(_pjTplMod);
  if (!_pjTplLoad) {
    _pjTplLoad = __ddChunkWrap(import('./projChangeOrderTemplates.js?v=1012002')).then((m) => { m._bind(_pjTplDeps()); _pjTplMod = m; return m; }).catch((e) => {
      _pjTplLoad = null; // the next click tries again
      console.error('[Projects] projChangeOrderTemplates load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _projLazyLoadFailed: true });
    });
  }
  return _pjTplLoad;
}
function showAddChangeOrder(...a) { return _pjTplMod ? _pjTplMod.showAddChangeOrder(...a) : _projLazyCall(_loadPjTpl, 'showAddChangeOrder', a); }
function updateChangeOrderPreview(...a) { return _pjTplMod ? _pjTplMod.updateChangeOrderPreview(...a) : _projLazyCall(_loadPjTpl, 'updateChangeOrderPreview', a); }
function submitChangeOrder(...a) { return _pjTplMod ? _pjTplMod.submitChangeOrder(...a) : _projLazyCall(_loadPjTpl, 'submitChangeOrder', a); }
function showManageTemplates(...a) { return _pjTplMod ? _pjTplMod.showManageTemplates(...a) : _projLazyCall(_loadPjTpl, 'showManageTemplates', a); }
function submitCreateTemplate(...a) { return _pjTplMod ? _pjTplMod.submitCreateTemplate(...a) : _projLazyCall(_loadPjTpl, 'submitCreateTemplate', a); }

let _templateJobCount = 0;

function addTemplateJobRow() {
  const container = document.getElementById('template-job-rows');
  if (!container) return;
  const idx = _templateJobCount++;
  const row = document.createElement('div');
  row.className = 'template-job-row';
  row.style.cssText = 'display:flex;gap:8px;align-items:center;margin-bottom:6px;';
  row.innerHTML = `
    <input type="text" class="tmpl-job-title" placeholder="Job title" style="flex:2;padding:6px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:0.85rem;">
    <input type="number" class="tmpl-job-offset" placeholder="Start +days" min="0" style="flex:1;padding:6px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:0.85rem;">
    <input type="number" class="tmpl-job-duration" placeholder="Duration (days)" min="1" style="flex:1;padding:6px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:0.85rem;">
    <button type="button" onclick="this.parentElement.remove()" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);">
      <span class="material-icons" style="font-size:16px;">close</span>
    </button>
  `;
  container.appendChild(row);
}

function showTemplateMenu() {
  const templates = ProjectState.templates || [];
  if (templates.length === 0) {
    window.showToast?.('No templates — create one first', 'info');
    showManageTemplates();
    return;
  }
  // For simplicity, show create project modal with template dropdown
  showCreateProject();
  // Pre-focus template dropdown after modal opens
  setTimeout(() => {
    const tmplSelect = document.getElementById('new-project-template');
    if (tmplSelect) tmplSelect.focus();
  }, 300);
}

// ── Project Menu (more actions) ────────────────────────
function showProjectMenu(projectId) {
  const project = ProjectState.projects.find(p => p.id === projectId);
  if (!project) return;

  const actions = [];
  if (project.status === 'active') {
    actions.push({ label: 'Mark Completed', icon: 'check_circle', action: `window.ProjectsModule.setProjectStatus('${projectId}','completed')` });
  }
  if (project.status === 'completed') {
    actions.push({ label: 'Reopen', icon: 'replay', action: `window.ProjectsModule.setProjectStatus('${projectId}','active')` });
    actions.push({ label: 'Archive', icon: 'archive', action: `window.ProjectsModule.setProjectStatus('${projectId}','archived')` });
  }
  if (project.status === 'archived') {
    actions.push({ label: 'Unarchive', icon: 'unarchive', action: `window.ProjectsModule.setProjectStatus('${projectId}','active')` });
  }
  actions.push({ label: 'Duplicate', icon: 'content_copy', action: `window.ProjectsModule.duplicateProject('${projectId}')` });
  actions.push({ label: 'Delete', icon: 'delete', action: `window.ProjectsModule.deleteProject('${projectId}')`, danger: true });

  // Simple dropdown
  let menu = document.getElementById('project-context-menu');
  if (!menu) {
    menu = document.createElement('div');
    menu.id = 'project-context-menu';
    document.body.appendChild(menu);
  }

  menu.innerHTML = `
    <div style="position:fixed;top:0;left:0;right:0;bottom:0;z-index:999;" onclick="document.getElementById('project-context-menu').innerHTML=''"></div>
    <div style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.15);z-index:1000;min-width:200px;overflow:hidden;">
      ${actions.map(a => `
        <button onclick="${a.action};document.getElementById('project-context-menu').innerHTML=''"
                style="display:flex;align-items:center;gap:10px;width:100%;padding:12px 16px;border:none;background:none;cursor:pointer;font-size:0.9rem;${a.danger ? 'color:#EF4444;' : ''}text-align:left;">
          <span class="material-icons" style="font-size:18px;">${a.icon}</span> ${a.label}
        </button>
      `).join('')}
    </div>
  `;
}

async function setProjectStatus(projectId, status) {
  const ok = await updateProject(projectId, { status });
  if (ok) {
    window.showToast?.(`Project ${status}`, 'success');
    if (ProjectState.currentProject?.id === projectId) {
      ProjectState.currentProject.status = status;
    }
    renderProjectsView();
  }
}

// TASK-9605 — one-click close from a "Wrapped" card. Same writer as the detail
// menu (updateProject); a 5s Undo toast instead of a modal. Never automatic.
// The schedule is RE-READ first (cache bypassed): the card may be minutes old and
// dispatch may have added a day since, so "Wrapped" is re-proved at click time.
const _completingProjects = new Set();
async function markWrappedComplete(projectId) {
  const tenantId = ProjectState.tenantId;
  const project = ProjectState.projects.find(p => p.id === projectId);
  if (!project || project.status !== 'active' || _completingProjects.has(projectId)) return;
  _completingProjects.add(projectId);
  try {
    const linked = _projectLinkedJobs.get(projectId) || [];
    for (const j of linked) _jobLastDayCache.delete(`${tenantId}/${j.id}`);
    let byJob = null;
    try { byJob = await loadJobLastDays(tenantId, linked, _projectJobCountsGen); } catch (err) { byJob = null; }
    if (ProjectState.tenantId !== tenantId) return;
    if (!byJob) {
      window.showToast?.('Could not re-check this project\u2019s schedule \u2014 nothing was changed.', 'warning', null, 6000);
      return;
    }
    const ms = tallyProjectLastDays([projectId], linked, byJob).get(projectId);
    project._lastDayMs = ms;
    if (_projectLastDaysTenant === tenantId) _projectLastDays.set(projectId, ms);
    if (projectRunState(project, new Date(), tenantTimeZone()).state !== 'wrapped') {
      window.showToast?.('Work is scheduled after the end date \u2014 project left open.', 'warning', null, 6000);
      renderProjectsView();
      return;
    }
    const ok = await updateProject(projectId, { status: 'completed' });
    if (!ok) return;
    window.showToast?.(`${project.name || 'Project'} marked complete`, 'success', {
      label: 'Undo',
      callback: async () => {
        if (ProjectState.tenantId !== tenantId) return; // tenant switched since — updateProject would hit the wrong tenant
        if (await updateProject(projectId, { status: 'active' })) renderProjectsView();
      }
    }, 5000);
    renderProjectsView();
  } finally {
    _completingProjects.delete(projectId);
  }
}

// ── Note submission ────────────────────────────────────
async function submitNote(projectId) {
  const input = document.getElementById('project-note-input');
  const text = input?.value?.trim();
  if (!text) { window.showToast?.('Note cannot be empty', 'error'); return; }

  const mentions = parseMentions(text);
  // TASK-9549 — default Internal; only an explicit pick shares it.
  const visEl = document.querySelector('input[name="project-note-visibility"]:checked');
  const visibility = visEl?.value === 'customer' ? 'customer' : 'internal';
  await addNote(projectId, text, mentions, visibility);
  if (input) input.value = '';

  // Refresh notes tab
  if (ProjectState.detailTab === 'notes') renderProjectsView();
}

async function deleteNoteHandler(projectId, noteId) {
  if (!confirm('Delete this note?')) return;
  await deleteNote(projectId, noteId);
  if (ProjectState.detailTab === 'notes') renderProjectsView();
}

// ── Tab / View switching ───────────────────────────────
function setDetailTab(tab) {
  ProjectState.detailTab = tab;
  renderProjectsView();
}

function backToList() {
  ProjectState.viewMode = 'list';
  ProjectState.currentProject = null;
  ProjectState.detailTab = 'overview';
  renderProjectsView();
  // TASK-9558 — a job linked/unlinked in the detail view doesn't touch the
  // project doc, so no snapshot refreshes the count; re-count on the way back.
  loadProjectJobCounts();
}

function onSearch(val) {
  ProjectState.searchQuery = val;
  renderProjectsView();
}

function clearSearch() {
  ProjectState.searchQuery = '';
  renderProjectsView();
}

function clearAllFilters() {
  ProjectState.searchQuery = '';
  ProjectState.statusFilter = window.ProjectStatusFilter.DEFAULT;
  ProjectState.customerFilter = '';
  ProjectState.tagFilter = '';
  renderProjectsView();
}

function setStatusFilter(status) {
  ProjectState.statusFilter = window.ProjectStatusFilter.resolve(status);
  renderProjectsView();
}

function setCustomerFilter(customerId) {
  ProjectState.customerFilter = customerId;
  renderProjectsView();
}

function setTagFilter(tag) {
  ProjectState.tagFilter = tag;
  renderProjectsView();
}

function promptSaveFilter() {
  const name = prompt('Name for this filter set:');
  if (name) saveFilter(name);
}

function approveChangeOrder(projectId, coId) {
  updateChangeOrderStatus(projectId, coId, 'approved').then(() => renderProjectsView());
}

function rejectChangeOrder(projectId, coId) {
  updateChangeOrderStatus(projectId, coId, 'rejected').then(() => renderProjectsView());
}

// fix/2096-preship — take a shared office change order back off the customer
// portal. One-way: rules allow customerVisible true -> false, never back on.
async function unshareChangeOrder(projectId, coId) {
  if (!ProjectState.tenantId || !projectId || !coId) return;
  const okGo = await window.showConfirm?.('Hide from the customer?',
    'The customer will stop seeing this change order on their portal. They may already have seen it. You cannot share it again afterwards.',
    { confirmText: 'Hide it' });
  if (okGo !== true) return;
  try {
    await updateDoc(doc(db, 'tenants', ProjectState.tenantId, 'projects', projectId, 'changeOrders', coId),
      { customerVisible: false, updatedAt: Timestamp.now() });
    window.showToast?.('Hidden from the customer portal', 'success');
    renderProjectsView();
  } catch (err) {
    console.error('[Projects] Error hiding change order:', err);
    window.showToast?.('Couldn’t hide it: ' + (err?.message || 'unknown error'), 'error');
  }
}

// ============================================
// AUTO-ARCHIVE: Run on load
// ============================================
async function autoArchiveExpired() {
  const now = new Date();
  const expired = ProjectState.projects.filter(p => {
    if (p.status !== 'completed') return false;
    const endDate = p.endDate?.toDate ? p.endDate.toDate() : null;
    if (!endDate) return false;
    // Archive completed projects 30 days after end date
    const daysSinceEnd = (now.getTime() - endDate.getTime()) / 86400000;
    return daysSinceEnd > 30;
  });

  for (const p of expired) {
    try {
      await updateDoc(doc(db, 'tenants', ProjectState.tenantId, 'projects', p.id), {
        status: 'archived',
        updatedAt: Timestamp.now()
      });
      console.log(`[Projects] Auto-archived project: ${p.name}`);
    } catch (err) {
      console.warn('[Projects] Auto-archive failed:', err);
    }
  }
}

// ============================================
// GET ACTIVE PROJECTS FOR A CUSTOMER (used by dispatch job modal)
// ============================================
function getProjectsForCustomer(customerId) {
  return ProjectState.projects.filter(p =>
    p.status === 'active' &&
    (p.primaryCustomerId === customerId || (p.customerIds || []).includes(customerId))
  );
}

function getAllActiveProjects() {
  return ProjectState.projects.filter(p => p.status === 'active');
}

// ── TASK-9420 — duplicate project name: a soft warning, never a block ──────
// One pure matcher for every path that names a project (New Project modal,
// Duplicate, Rename, and dispatch.js saveJob's "create a new project" via
// window.ProjectsModule — never a cross-import, Pitfall #1). Tenant-wide, ALL
// statuses (completed and archived too), same-customer matches first, then
// newest. No network call; names are never logged.
// 🔴 Never called from createProject: it is the shared sink for template,
// duplicate, New Job and edit-job, and the edit-job rollback
// (discardUnusedProject) needs it to return a genuinely NEW id. Check at the
// call sites, before any write.
function normalizeProjectName(name) {
  return String(name == null ? '' : name).replace(/\s+/g, ' ').trim().toLowerCase();
}

function findProjectsByName(name, opts = {}, projects = ProjectState.projects) {
  const key = normalizeProjectName(name);
  if (!key) return [];
  const excludeId = (opts && opts.excludeId) || null;
  const cust = (opts && opts.customerId) || '';
  const sameCust = p => !!cust && (p.primaryCustomerId === cust
    || (Array.isArray(p.customerIds) && p.customerIds.includes(cust)));
  const ms = v => (v && typeof v.toMillis === 'function') ? v.toMillis()
    : (v && typeof v.seconds === 'number') ? v.seconds * 1000
    : (v ? (new Date(v).getTime() || 0) : 0);
  return (Array.isArray(projects) ? projects : [])
    .filter(p => p && p.id && p.id !== excludeId && normalizeProjectName(p.name) === key)
    .map(p => ({ p, same: sameCust(p) ? 1 : 0, at: ms(p.createdAt) }))
    .sort((a, b) => (b.same - a.same) || (b.at - a.at))
    .map(x => x.p);
}

// The list may not be here yet (first snapshot in flight): wait for it up to
// ~2s, then answer from whatever is loaded — an empty list = no warning. Never
// throws, never blocks a save on the wait.
async function checkDuplicateProjectName(name, opts = {}) {
  try {
    if (ProjectState.loading && _projectsFirstSnapshot) {
      let t = null;
      await Promise.race([
        _projectsFirstSnapshot,
        new Promise(r => { t = setTimeout(r, 2000); })
      ]);
      clearTimeout(t);
    }
    return findProjectsByName(name, opts);
  } catch (_) {
    return [];
  }
}

function _dupProjectStatusLabel(status) {
  return status === 'completed' ? 'Completed' : status === 'archived' ? 'Archived' : 'Active';
}

// Three choices, so window.showConfirm (two, true/false) can't carry it.
// Resolves 'existing' | 'create' | 'cancel'. Every name goes in via
// textContent. Buttons stack with the primary on top. Escape and a backdrop
// click = cancel (the safe choice: nothing is written).
// A second dialog (e.g. Save reached by keyboard behind the first) settles the
// first as 'cancel' and removes its listener, so no caller is left waiting.
let _dupNameDialogFinish = null;
function showDuplicateProjectNameDialog(opts = {}) {
  return new Promise(resolve => {
    if (_dupNameDialogFinish) _dupNameDialogFinish('cancel');
    document.getElementById('modal-dup-project-name')?.remove();
    const matches = Array.isArray(opts.matches) ? opts.matches : [];
    const offerExisting = opts.offerExisting !== false && matches.length > 0;
    const el = (tag, style, text) => {
      const n = document.createElement(tag);
      if (style) n.style.cssText = style;
      if (text != null) n.textContent = text;
      return n;
    };

    // .modal = the fixed, centred backdrop; z-index above the open job form.
    const overlay = el('div', 'z-index:10000;');
    overlay.id = 'modal-dup-project-name';
    overlay.className = 'modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'dup-project-name-title');

    const box = el('div', 'display:block;overflow-y:auto;max-height:90dvh;padding:24px;');
    box.className = 'modal-content';

    const head = el('div', 'display:flex;align-items:center;gap:12px;margin-bottom:8px;');
    const icon = el('span', 'color:#F59E0B;font-size:28px;', 'warning');
    icon.className = 'material-icons';
    icon.setAttribute('aria-hidden', 'true');
    const title = el('h3', 'margin:0;font-size:18px;color:var(--text-primary);', 'This project name is already used');
    title.id = 'dup-project-name-title';
    head.append(icon, title);

    const lead = el('p', 'margin:8px 0 12px;color:var(--text-primary);line-height:1.5;');
    lead.append(document.createTextNode('“'), el('strong', '', String(opts.name || '').trim()),
      document.createTextNode(matches.length === 1 ? '” matches:' : `” matches ${matches.length} projects:`));

    const list = el('ul', 'margin:0 0 16px;padding:0;list-style:none;display:flex;flex-direction:column;gap:6px;');
    matches.slice(0, 6).forEach(p => {
      const li = el('li', 'padding:8px 10px;border:1px solid var(--border-light);border-radius:8px;');
      const cust = p.primaryCustomerName || (p.primaryCustomerId ? getCustomerName(p.primaryCustomerId) : 'No customer');
      li.append(el('span', '', p.name || 'Untitled Project'), document.createTextNode(' — '),
        el('strong', '', cust), document.createTextNode(' · '),
        el('span', 'color:var(--text-secondary);', _dupProjectStatusLabel(p.status)));
      list.appendChild(li);
    });

    const btns = el('div', 'display:flex;flex-direction:column;gap:8px;');
    const mk = (id, cls, label) => {
      const b = el('button', 'width:100%;min-height:44px;justify-content:center;', label);
      b.type = 'button';
      b.id = id;
      b.className = cls;
      return b;
    };
    const bExisting = offerExisting ? mk('dup-project-name-existing', 'btn btn-primary', opts.existingLabel || 'Open existing') : null;
    const bCreate = mk('dup-project-name-create', offerExisting ? 'btn btn-secondary' : 'btn btn-primary', opts.createLabel || 'Create anyway');
    const bCancel = mk('dup-project-name-cancel', 'btn btn-text', opts.cancelLabel || 'Cancel');
    bCancel.style.color = 'var(--text-secondary)';
    if (bExisting) btns.appendChild(bExisting);
    btns.append(bCreate, bCancel);

    box.append(head, lead, list);
    box.appendChild(btns);
    overlay.appendChild(box);

    let done = false;
    const onKey = (e) => {
      // Tab stays on the dialog's buttons, so Save behind it can't be reached.
      if (e.key === 'Tab') {
        const b = [bExisting, bCreate, bCancel].filter(Boolean);
        const i = b.indexOf(document.activeElement);
        e.preventDefault();
        b[(i + (e.shiftKey ? b.length - 1 : 1)) % b.length].focus();
        return;
      }
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation(); // the job form underneath must not close on this Escape
      finish('cancel');
    };
    const finish = (choice) => {
      if (done) return;
      done = true;
      if (_dupNameDialogFinish === finish) _dupNameDialogFinish = null;
      document.removeEventListener('keydown', onKey, true);
      overlay.remove();
      resolve(choice);
    };
    if (bExisting) bExisting.onclick = () => finish('existing');
    bCreate.onclick = () => finish('create');
    bCancel.onclick = () => finish('cancel');
    overlay.addEventListener('click', (e) => { if (e.target === overlay) finish('cancel'); });
    _dupNameDialogFinish = finish;
    document.addEventListener('keydown', onKey, true);
    document.body.appendChild(overlay);
    (bExisting || bCreate).focus?.();
  });
}

// The Projects-side paths (New Project, Duplicate, Rename). True = go on and
// save; false = stop (cancelled, or the existing project was opened instead).
// "Open existing" only navigates — it never moves jobs, tickets or clock entries.
async function confirmProjectNameNotDuplicate(name, { excludeId = null, customerId = '', closeModalId = '' } = {}) {
  const matches = await checkDuplicateProjectName(name, { excludeId, customerId });
  if (!matches.length) return true;
  const choice = await showDuplicateProjectNameDialog({
    name, matches, offerExisting: true,
    existingLabel: 'Open existing', createLabel: 'Create anyway', cancelLabel: 'Cancel'
  });
  if (choice === 'create') return true;
  if (choice === 'existing') {
    if (closeModalId) document.getElementById(closeModalId)?.classList.add('hidden');
    await openProjectDetail(matches[0].id);
  }
  return false;
}

// ============================================
// INIT
// ============================================
export async function initProjects(tenantId) {
  const mountGen = ++_projectsMountGen; // TASK-10003 — this mount is now the newest
  ProjectState.tenantId = tenantId;
  loadGroupPrefs();

  // Expose on window BEFORE async work so dispatch.js can render immediately
  window.ProjectsModule = {
    renderProjectsView,
    openProjectDoc, // TASK-9647
    setProjectDocsFilter, // TASK-9649 — Docs tab chips / job / search
    // TASK-9650 — project library (Docs tab) + the job form's "Use on jobs" picker
    showProjectLibDocModal, closeProjectLibModal, projectLibFilePicked, submitProjectLibDoc,
    deleteProjectLibDoc, setProjectLibUseOnJobs, setProjectLibFilter, openProjectLibDoc,
    openProjectDocRef, getUseOnJobsDocs,
    // TASK-9652 — customer uploads waiting for review (Docs tab)
    viewCustomerUpload, reviewCustomerUpload, showRejectCustomerUpload, submitRejectCustomerUpload,
    // TASK-9652 S2 — canned reject reasons + the nav badge's pending-upload count
    applyRejectReason, getPendingCustomerUploadCount, customerDocsRecipientsExist, openRosterForCustomerDocs,
    // TASK-9767 — 811 tickets on a project (Docs tab) + the hooks dispatch.js
    // reads (job readers, deletePermit). Reached via window, never imported.
    linkProject811,
    uploadProject811,
    unlinkProject811,
    getProjectPermitIds,
    p811OfferWaiting,   // 811 waiting tickets
    p811AttachWaiting,  // 811 waiting tickets
    projectsHoldingPermit,
    unlinkPermitFromProjects,
    // 811 project dropdown — the 811 window's per-ticket Project pick
    setPermitProject,
    getPermitProjectIds,
    project811CanEdit,
    openProjectDetail,
    showCreateProject,
    submitCreateProject,
    showEditProject,
    submitEditProject,
    addProjectPerson,    // TASK-4705
    removeProjectPerson, // TASK-4705
    toggleProjectPersonSteps, // TASK-4706
    deleteProject,
    duplicateProject,
    scheduleJobFromProject,
    // TASK-9391 — New job defaults + the resolver TASK-9392 builds on
    resolveProjectNewJobPrefill,
    // TASK-9392 — dispatch.js's customer-request listener calls this per snapshot
    onCustomerRequestsChanged,
    normalizeNewJobDefaults,
    normalizeProjectJobDefaults, // TASK-4696 — dispatch.js applyProjectJobDefaults reads through this
    showLinkExistingJob,
    doLinkJob,
    unlinkJob,
    openProjectJob, // job click → DispatchModule.openJobDetailsById
    filterLinkJobs,
    setDetailTab,
    backToList,
    onSearch,
    clearSearch,
    clearAllFilters,
    setStatusFilter,
    setCustomerFilter,
    setTagFilter,
    promptSaveFilter,
    applySavedFilter,
    deleteSavedFilter,
    submitNote,
    setNoteVisibility, // TASK-9549
    deleteNote: deleteNoteHandler,
    showAddChangeOrder,
    submitChangeOrder,
    approveChangeOrder,
    rejectChangeOrder,
    unshareChangeOrder, // fix/2096-preship
    updateChangeOrderPreview, // fix/2096-preship
    showManageTemplates,
    showTemplateMenu,
    addTemplateJobRow,
    submitCreateTemplate,
    deleteTemplate,
    showProjectMenu,
    setProjectStatus,
    markWrappedComplete, // TASK-9605
    // TASK-9674 — Field totals card
    showFieldTotalsPicker,
    findFieldTotalsItems,
    addFieldTotalsOther,
    saveFieldTotalsPicker,
    setFieldTotalsRange,
    refreshFieldTotals,
    // For dispatch integration
    getProjectsForCustomer,
    getAllActiveProjects,
    getActiveProjects: getAllActiveProjects,
    createProject,
    // TASK-9420 — duplicate-name warning; dispatch.js saveJob reads these via window
    normalizeProjectName,
    findProjectsByName,
    checkDuplicateProjectName,
    showDuplicateProjectNameDialog,
    linkJobToProject,
    unlinkJobFromProject,
    // TASK-9289 COD r1 — the one gated path for jobs.projectId
    changeJobProject,
    // TASK-9289 r5 — saveJob's create-new-project edit path: ask first, roll back an unused project
    confirmBilledJobMove,
    discardUnusedProject,
    loadProjects,
    setListMode,
    setGroupByCustomer,
    projectCalendarNav,
    projectCalendarToday,
    setCalendarMode,
    addPortalStep,
    removePortalStep,
    updatePortalStepName,
    togglePortalStep,
    importOppSteps,
    getState: () => ProjectState,
  };

  // Load data async — module functions are already available above
  await Promise.all([loadProjects(), loadTemplates(), loadCustomers()]);
  autoArchiveExpired();
  ProjectState.savedFilters = getSavedFilters();

  // TASK-10003 — tears down only if no newer mount has started (see _projectsMountGen).
  return () => { _teardownProjectsMount(mountGen); };
}

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
